/**
 * EmployeeWorkspace — the "what is this employee doing right now" surface on the Agent page
 * (DIGITAL-EMPLOYEE-WORKSPACE-001). Everything here is read from GET /api/agents/:id/workspace,
 * which itself is a projection of the existing AgentRun / Ticket / Conversation / Tool Registry
 * data — nothing on this screen is a mock and nothing on it grants anything.
 *
 * Layout: status + actions row → pilot acceptance panel (DIGEMP-PILOT-ACCEPTANCE-OPERATIONS-
 * FRONTEND-031, evidence-derived end-to-end readiness) → conversation section with the employee
 * (identity, live state, bound ticket, transcript and composer —
 * DIGEMP-EMPLOYEE-CONVERSATION-WORKSPACE-FRONTEND-020, the single chat surface for this employee)
 * → three cards (current work / assigned tickets /
 * recent conversations) → explicit browser work request section
 * (DIGEMP-BROWSER-WORK-REQUEST-FRONTEND-022, agents_run-gated, records a bound request and
 * renders the sanitized receipt — grants nothing) → capabilities chips with a collapsed
 * "פרטים טכניים" drawer → model & provider assignment
 * (DIGEMP-PROVIDER-MODEL-ASSIGNMENT-FRONTEND-042, read-only) → systems readiness
 * (DIGEMP-EMPLOYEE-CONNECTION-READINESS-FRONTEND-021, stored-evidence only) → specialist
 * readiness matrix (DIGEMP-SPECIALIST-READINESS-FRONTEND-027) → specialist pack capability
 * cards (DIGEMP-SPECIALIST-PACKS-FRONTEND-028, additive contract) → remote operations readiness
 * (DIGEMP-REMOTE-READINESS-FRONTEND-029) → ESET specialist readiness card
 * (DIGEMP-ESET-SPECIALIST-FRONTEND-035, evidence-only) → M365/Entra specialist readiness card
 * (DIGEMP-M365-ENTRA-SPECIALIST-FRONTEND-037, evidence-only) → Acronis specialist readiness
 * card (DIGEMP-ACRONIS-SPECIALIST-FRONTEND-036, evidence-only) → expert pack catalog
 * (DIGEMP-EXPERT-PACK-CATALOG-FRONTEND-032, additive contract) → skill assignment state
 * (DIGEMP-SKILL-ASSIGNMENT-STATE-FRONTEND-034, the read-only lifecycle/version/availability view
 * over the assigned Skills, from the additive skill_assignment_state projection — a Skill is not a
 * permission and this section adds no control) → experience timeline
 * (DIGEMP-EXPERIENCE-OUTCOMES-FRONTEND-024, derived from the audited run records the page
 * page already fetched) → approved skills & versions (DIGEMP-SKILL-VERSION-HISTORY-FRONTEND-025,
 * from the additive skill_history projection when the server supplies it) → learning & proficiency
 * history (DIGEMP-PROFICIENCY-LEARNING-FRONTEND-026, from the additive learning_history projection
 * when the server supplies it) → learning candidates vs approved knowledge
 * (DIGEMP-KNOWLEDGE-CANDIDATES-FRONTEND-033) → raw activity journal.
 *
 * The conversation section reuses ConversationPanel unchanged: it owns the transcript, the
 * composer, the approval/reject/retry controls and their permission gates. This component only
 * decides WHICH conversation is shown (a new one with this employee, or one picked from the
 * recent list) and renders the human-facing context around it.
 *
 * For the Platform Guardian employee the workspace additionally renders GuardianWorkspace
 * (DIGEMP-GUARDIAN-READONLY-FRONTEND-038): the read-only summary of the platform/security/backup
 * evidence Guardian has produced, read from the existing admin-gated guardian endpoints. It exposes
 * no run trigger, no status change, no ticket creation and no backup remediation — only evidence,
 * freshness and provenance; every unavailable or refused source degrades to an explicit unknown.
 */
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import api, { agentConnectionsApi, agentsApi, connectionsApi, platformGuardianApi, backupGuardianApi, scheduledChecksApi } from "../services/api";
import { detailText } from "../utils/apiErrors";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import EmployeeAvatar, { STATUS_META } from "./EmployeeAvatar";
import EmployeeIdentityCard from "./EmployeeIdentityCard";
import BrowserWorkSessionPanel from "./BrowserWorkSessionPanel";
import ConversationPanel from "./ConversationPanel";
import BrowserWorkRequestCard from "../features/browser-work/BrowserWorkRequestCard";
import { EMPLOYEE_WORKSPACE_SECTIONS, WorkspaceNav } from "./EmployeeWorkspaceNav";
import EmployeeConnectionHub from "./EmployeeConnectionHub";
import { CHIP_CLS, LIVE_STATUS_CLS } from "./statusVocabulary";
import { degradedAnalysisLabel } from "./degradedAnalysisVocabulary";
import { entitlementBoundaryNote } from "./entitlementEnforcementVocabulary";
import { approvalBindingNote } from "./approvalBindingVocabulary";
import { browserCapabilityBlockedLabel } from "./browserCapabilityVocabulary";
import { browserExecutionReasonLabel } from "./browserExecutionReasonVocabulary";
import {
  BROWSER_EXECUTION_TERMINAL_STATES,
  browserExecutionStateLabel,
} from "./browserExecutionStateVocabulary";
import { runStatusLabel } from "./runStatusVocabulary";
import { SYSTEM_TYPE_HE, systemTypeLabel } from "./systemTypeVocabulary";
import { conversationStatusLabel } from "./conversationStatusVocabulary";

// The jump navigation of DIGEMP-EMPLOYEE-WORKSPACE-V2-FRONTEND-075 over the composed workspace:
// ids/labels come from the single section registry, while the order below is this page's own
// reading order (the later composition tasks own how the sections stack — the registry only owns
// the anchor vocabulary, so navigation and anchors cannot drift). Every id listed here has exactly
// one anchor below; content that shares a group (attention/queue/browser work, approved skills,
// the raw journal) follows its group's anchor rather than opening a second one.
const WORKSPACE_NAV_ORDER = [
  "employee-growth",
  "employee-work",
  "employee-conversations",
  "employee-capabilities",
  "employee-systems",
  "employee-skills",
  "employee-evidence",
  "employee-learning",
];
const WORKSPACE_NAV_SECTIONS = WORKSPACE_NAV_ORDER
  .map((id) => EMPLOYEE_WORKSPACE_SECTIONS.find((section) => section.id === id))
  .filter(Boolean);

// The reserved backend name of the Guardian agent (app/services/platform_guardian.py GUARDIAN_NAME,
// pinned by backend/tests/test_agent_reserved_names.py). Same exact-match identity the backend uses.
const GUARDIAN_AGENT_NAME = "Platform Guardian";

export const STATUS_BADGE = {
  available: "bg-green-100 text-green-700",
  busy: "bg-amber-100 text-amber-700",
  away: "bg-gray-200 text-gray-600",
  offline: "bg-gray-200 text-gray-500",
};

// Each conversation state gets its own glyph and palette so an operator can tell
// "waiting on me" from "agent will continue" at a glance. Colors follow the
// ConversationsPage badge vocabulary, except awaiting_human and handed_off: those
// share a hue with another state there (amber with awaiting_approval, gray with
// closed), so they get their own here. The wording is NOT kept here — a state's label
// is the shared conversation-status vocabulary's word, so this list owns the glyph and
// the hue and nothing about how a state is spelled.
const CONVERSATION_STATE_STYLE = {
  open: {
    icon: "●", title: "שיחה פתוחה",
    badge: "bg-blue-100 text-blue-700 border-blue-200",
  },
  awaiting_agent: {
    icon: "▶", title: "ממתינה לעובד הדיגיטלי — יפעל בהמשך",
    badge: "bg-violet-100 text-violet-700 border-violet-200",
  },
  awaiting_human: {
    icon: "✋", title: "ממתינה לטכנאי — נדרשת פעולה אנושית",
    badge: "bg-orange-100 text-orange-700 border-orange-200",
  },
  awaiting_approval: {
    icon: "⏳", title: "ממתינה לאישור — חסומה עד החלטת אישור",
    badge: "bg-amber-100 text-amber-700 border-amber-200",
  },
  handed_off: {
    icon: "↪", title: "הועברה — האחריות עברה לגורם אחר",
    badge: "bg-teal-100 text-teal-700 border-teal-200",
  },
  resolved: {
    icon: "✓", title: "נפתרה",
    badge: "bg-green-100 text-green-700 border-green-200",
  },
  closed: {
    icon: "⊘", title: "סגורה",
    badge: "bg-gray-200 text-gray-500 border-gray-300",
  },
};

const CONVERSATION_STATE_FALLBACK = { icon: "•", badge: "bg-gray-100 text-gray-600 border-gray-200", title: "" };

function conversationState(status) {
  return {
    ...(CONVERSATION_STATE_STYLE[status] || CONVERSATION_STATE_FALLBACK),
    label: conversationStatusLabel(status),
  };
}

// Human-facing conversation state for the conversation section's header chip — phrased from the
// employee's side, distinct from CONVERSATION_STATE_STYLE above which describes the conversation
// record itself for the "שיחות אחרונות" card's badges. `awaiting_agent` names whose turn it is,
// not activity: a direct reply creates no AgentRun, so this chip must never say the employee is
// handling the conversation. Only `workingOnThis` (a real run bound to this conversation) prints
// "כרגע".
const CONVERSATION_SECTION_STATE = {
  open: { label: "פתוחה", tone: "gray" },
  awaiting_agent: { label: "ממתינה לטיפול העובד", tone: "violet" },
  awaiting_approval: { label: "ממתין לאישור", tone: "amber", hint: "אפשר לאשר בתמלול למטה" },
  awaiting_human: { label: "ממתין לך", tone: "orange", hint: "אפשר להשיב כאן" },
  handed_off: { label: "הועבר להמשך טיפול אנושי", tone: "blue" },
  resolved: { label: "הסתיים", tone: "green" },
  closed: { label: "הסתיים", tone: "green" },
};

const SECTION_STATE_TONE = {
  gray: "bg-gray-100 text-gray-600 border-gray-200",
  violet: "bg-violet-50 text-violet-700 border-violet-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  orange: "bg-orange-50 text-orange-700 border-orange-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  green: "bg-green-50 text-green-700 border-green-200",
};

// The remote-transport ids (`REMOTE_TRANSPORT_TYPES`, below) are a scale of their own and keep
// their own labels here. They are not `system_type` tokens: the connection-readiness and
// capabilities projections emit `AgentSystemType`, whose arms are the three in
// systemTypeVocabulary. A transport-named connection row is what `_transport_entries()` used to
// look like before it started carrying registry display names, and nothing on the workspace
// payload is known to mint one — see the residual recorded for `observedTransports`.
const REMOTE_TRANSPORT_HE = { teamviewer: "TeamViewer", openclaw: "OpenClaw" };

// DIGEMP-EMPLOYEE-CONNECTION-READINESS-FRONTEND-021: the systems/readiness surface. Every state
// comes from GET /api/agents/:id/connection-readiness (stored connection-test evidence + connector
// registry), and is never derived here — this screen must not claim a health check that was not
// actually run. A product of the stored evidence only: an unknown state is never dressed up as healthy.
const READINESS_STATE_META = {
  healthy: {
    label: "מחובר",
    cls: "bg-green-100 text-green-700 border-green-200",
    hint: "הבדיקה האחרונה שנשמרה הצליחה",
  },
  unavailable: {
    label: "לא זמין",
    cls: "bg-red-100 text-red-700 border-red-200",
    hint: "הבדיקה האחרונה שנשמרה נכשלה",
  },
  needs_attention: {
    label: "דורש טיפול",
    cls: "bg-amber-100 text-amber-700 border-amber-200",
    hint: "טרם נבדק, וקיימת בעיית תצורה ידועה",
  },
  unknown: {
    label: "לא ידוע",
    cls: "bg-gray-100 text-gray-600 border-gray-200",
    hint: "טרם נבדק — אין מידע על מצב החיבור",
  },
};
const READINESS_STATE_ORDER = ["healthy", "unavailable", "needs_attention", "unknown"];

const READINESS_ACCESS_META = {
  read: {
    label: "קריאה בלבד",
    cls: "bg-blue-50 text-blue-700 border-blue-200",
    hint: "קריאת נתונים בלבד — ללא כתיבה",
  },
  approved_write: {
    label: "כתיבה באישור",
    cls: "bg-amber-50 text-amber-700 border-amber-200",
    hint: "פעולות כתיבה מול מערכת זו מחייבות אישור מפעיל",
  },
};

export function connectionReadinessQueryKey(agentId) {
  return ["agent-connection-readiness", agentId];
}

const KIND_ICON = {
  run_started: "▶", action: "⚙", conversation_reply: "💬", handoff: "↪",
  run_completed: "✓", run_resolved: "✓", run_failed: "✕", run_rejected: "✕",
};

const CONNECTION_STATUS = {
  ok: { label: "תקין", title: "החיבור אומת בבדיקה האחרונה", chip: "bg-teal-50 text-teal-700 border-teal-200" },
  error: { label: "דורש בדיקה", title: "הבדיקה האחרונה של החיבור נכשלה", chip: "bg-amber-50 text-amber-800 border-amber-200" },
  untested: { label: "לא נבדק", title: "החיבור טרם נבדק", chip: "bg-gray-50 text-gray-600 border-gray-200" },
};

function connectionStatus(c) {
  if (c.last_test_status === "ok" || c.last_test_status === "success") return CONNECTION_STATUS.ok;
  if (c.last_test_status) return CONNECTION_STATUS.error;
  return CONNECTION_STATUS.untested;
}

export const TOOL_SENSITIVITY = [
  {
    key: "strong", label: "אישור מוגבר", title: "כלים שדורשים אישור מוגבר לפני כל הרצה",
    chip: "bg-purple-50 text-purple-700 border-purple-200",
    match: (t) => !!t.strong_approval_required,
  },
  {
    key: "approval", label: "דורש אישור", title: "כלים שדורשים אישור לפני הרצה",
    chip: "bg-orange-50 text-orange-700 border-orange-200",
    match: (t) => !t.strong_approval_required && !!t.approval_required,
  },
  {
    key: "read_only", label: "קריאה בלבד", title: "כלים שאינם משנים דבר ואינם דורשים אישור",
    chip: "bg-blue-50 text-blue-700 border-blue-200",
    match: (t) => !t.strong_approval_required && !t.approval_required,
  },
];

function toolSensitivityCounts(tools) {
  return TOOL_SENSITIVITY.map((s) => ({ ...s, count: tools.filter(s.match).length })).filter((s) => s.count > 0);
}

// Operator summary — one glanceable state (free / handling / waiting on technician, approval or
// customer) derived ONLY from the workspace payload this screen already fetched. No extra fetch,
// no extra authority; the links reuse the same run/ticket/conversation routes as the cards.
const SUMMARY_RUN_STATES = {
  awaiting_technician_review: "waiting_technician",
  paused: "waiting_technician",
  awaiting_approval: "waiting_approval",
  waiting_for_customer_connection: "waiting_customer",
  waiting_for_customer_answer: "waiting_customer",
};

const SUMMARY_LABEL = {
  waiting_technician: "ממתין לטכנאי",
  waiting_approval: "ממתין לאישור",
  waiting_customer: "ממתין ללקוח",
  free: "פנוי",
  away: "לא כאן",
  offline: "מנותק",
  disabled: "מושבת",
};

const SUMMARY_STYLE = {
  free: { chip: "bg-green-50 border-green-200 text-green-700", dot: "bg-green-500" },
  handling: { chip: "bg-blue-50 border-blue-200 text-blue-700", dot: "bg-blue-500" },
  waiting_technician: { chip: "bg-amber-50 border-amber-200 text-amber-800", dot: "bg-amber-500" },
  waiting_approval: { chip: "bg-orange-50 border-orange-200 text-orange-800", dot: "bg-orange-500" },
  waiting_customer: { chip: "bg-sky-50 border-sky-200 text-sky-700", dot: "bg-sky-500" },
  away: { chip: "bg-gray-100 border-gray-200 text-gray-600", dot: "bg-gray-400" },
  offline: { chip: "bg-gray-100 border-gray-200 text-gray-500", dot: "bg-gray-300" },
  disabled: { chip: "bg-gray-100 border-gray-200 text-gray-500", dot: "bg-gray-300" },
};

// The two human-decision queues the workspace projection deliberately keeps out of current_work: a
// run parked on review/approval is a human-gate state, not "busy", so the routing desk ranks such an
// employee as available and the panel behind the same row must not contradict it. It is still the
// wait this operator summary exists to show, so when there is no work in progress it is read from
// the engagement set (assigned_tickets) the attention items already read. Reviews outrank
// approvals, the order the attention items use.
const HUMAN_GATE_RUN_STATES = ["awaiting_technician_review", "awaiting_approval"];

function firstHumanGateRun(assignedTickets) {
  for (const status of HUMAN_GATE_RUN_STATES) {
    const run = assignedTickets.find((ref) => ref && ref.run_status === status);
    if (run) return run;
  }
  return null;
}

export function deriveOperatorSummary(workspace) {
  const {
    agent, current_work: currentWork, assigned_tickets: assignedTickets = [],
    recent_conversations: conversations = [],
  } = workspace;
  if (!agent.is_enabled) {
    return { state: "disabled", label: SUMMARY_LABEL.disabled, detail: "לא זמין לעבודה", links: [] };
  }
  const run = currentWork || firstHumanGateRun(assignedTickets);
  if (run) {
    const state = SUMMARY_RUN_STATES[run.run_status] || "handling";
    const ref = run.ticket_number
      ? `קריאה #${run.ticket_number}${run.ticket_title ? ` · ${run.ticket_title}` : ""}`
      : "משימה מתוך שיחה";
    const links = [{ to: `/agent-runs/${run.run_id}`, label: "לריצה" }];
    if (run.ticket_id) links.push({ to: `/tickets/${run.ticket_id}?tab=ai-diagnosis`, label: "לכרטיס" });
    if (run.conversation_id) links.push({ to: `/conversations/${run.conversation_id}`, label: "לשיחה" });
    const label = state === "handling"
      ? (run.ticket_number ? `מטפל בקריאה #${run.ticket_number}` : "מטפל במשימה")
      : SUMMARY_LABEL[state];
    const detail = run.run_status_label ? `${ref} — ${run.run_status_label}` : ref;
    return { state, label, detail, links };
  }
  const waitingConversation = conversations.find((c) => c.status === "awaiting_human" || c.status === "awaiting_approval");
  if (waitingConversation) {
    const state = waitingConversation.status === "awaiting_approval" ? "waiting_approval" : "waiting_technician";
    const links = [{ to: `/conversations/${waitingConversation.id}`, label: "לשיחה" }];
    if (waitingConversation.ticket_id) links.push({ to: `/tickets/${waitingConversation.ticket_id}?tab=ai-diagnosis`, label: "לכרטיס" });
    const statusText = conversationStatusLabel(waitingConversation.status);
    const detail = waitingConversation.last_message_preview ? `${statusText} — ${waitingConversation.last_message_preview}` : statusText;
    return { state, label: SUMMARY_LABEL[state], detail, links };
  }
  if (agent.effective_status === "away") return { state: "away", label: SUMMARY_LABEL.away, detail: null, links: [] };
  if (agent.effective_status === "offline") return { state: "offline", label: SUMMARY_LABEL.offline, detail: null, links: [] };
  return { state: "free", label: SUMMARY_LABEL.free, detail: "אין משימה פעילה", links: [] };
}

// Attention derivation — read-only projection of the same workspace payload the rest of this
// screen renders; no extra fetch, no extra authority.
const STALL_AFTER_MS = 30 * 60 * 1000;
const STALL_RUN_STATUSES = new Set(["pending", "analyzing", "diagnosing", "executing"]);
const FAILURE_KIND_HE = { run_failed: "ריצה נכשלה", run_rejected: "ריצה נדחתה" };
// Which conversation states put a row in the operator's attention list. Wording is not kept here:
// the row's label is "שיחה " + the shared conversation-status label, so this names only the two
// states that need a person and never restates how any state is spelled.
const CONVERSATION_WAITING_STATUSES = new Set(["awaiting_human", "awaiting_approval"]);
const ATTENTION_CAP = 3;

const SEVERITY_STYLE = {
  review: "bg-amber-50 border-amber-200 text-amber-900",
  approval: "bg-orange-50 border-orange-200 text-orange-900",
  stalled: "bg-yellow-50 border-yellow-200 text-yellow-900",
  failure: "bg-red-50 border-red-200 text-red-800",
  conversation: "bg-blue-50 border-blue-200 text-blue-800",
};
const SEVERITY_DOT = {
  review: "bg-amber-500", approval: "bg-orange-500", stalled: "bg-yellow-500",
  failure: "bg-red-500", conversation: "bg-blue-500",
};

function refDetail(ref) {
  if (!ref) return "";
  if (ref.ticket_number) return `קריאה #${ref.ticket_number}${ref.ticket_title ? ` · ${ref.ticket_title}` : ""}`;
  return ref.run_status_label || "";
}

function refLinks(ref, { conversation = false } = {}) {
  const links = [];
  if (ref.run_id) links.push({ to: `/agent-runs/${ref.run_id}`, label: "לריצה" });
  if (ref.ticket_id) links.push({ to: `/tickets/${ref.ticket_id}?tab=ai-diagnosis`, label: "לכרטיס" });
  if (conversation && ref.conversation_id) links.push({ to: `/conversations/${ref.conversation_id}`, label: "לשיחה" });
  return links;
}

export function deriveAttentionItems(workspace, now = Date.now()) {
  const currentWork = workspace.current_work;
  const assignedTickets = workspace.assigned_tickets || [];
  const conversations = workspace.recent_conversations || [];
  const activity = workspace.activity || [];
  const items = [];

  const seenRuns = new Set();
  const reviews = [];
  const approvals = [];
  for (const ref of [currentWork, ...assignedTickets]) {
    if (!ref || !ref.run_id || seenRuns.has(ref.run_id)) continue;
    seenRuns.add(ref.run_id);
    if (ref.run_status === "awaiting_technician_review") reviews.push(ref);
    else if (ref.run_status === "awaiting_approval") approvals.push(ref);
  }
  reviews.slice(0, ATTENTION_CAP).forEach((ref) => items.push({
    key: `review-${ref.run_id}`, severity: "review", label: "נדרשת סקירת טכנאי",
    detail: refDetail(ref), at: ref.updated_at || ref.started_at,
    links: refLinks(ref, { conversation: true }),
  }));
  approvals.slice(0, ATTENTION_CAP).forEach((ref) => items.push({
    key: `approval-${ref.run_id}`, severity: "approval", label: "נדרש אישור להמשך",
    detail: refDetail(ref), at: ref.updated_at || ref.started_at,
    links: refLinks(ref, { conversation: true }),
  }));

  if (currentWork && STALL_RUN_STATUSES.has(currentWork.run_status) && currentWork.updated_at) {
    const updated = Date.parse(currentWork.updated_at);
    if (Number.isFinite(updated) && now - updated > STALL_AFTER_MS) {
      items.push({
        key: `stalled-${currentWork.run_id}`, severity: "stalled", label: "נראה תקוע — אין התקדמות",
        detail: `${refDetail(currentWork)} · עודכן לאחרונה ${fmt(currentWork.updated_at)}`,
        at: currentWork.updated_at, links: refLinks(currentWork, { conversation: true }),
      });
    }
  }

  const seenFailureRuns = new Set();
  let failures = 0;
  for (const entry of activity) {
    const label = FAILURE_KIND_HE[entry.kind];
    if (!label || failures >= ATTENTION_CAP) continue;
    const runId = entry.ref && entry.ref.run_id;
    if (runId && seenFailureRuns.has(runId)) continue;
    if (runId) seenFailureRuns.add(runId);
    failures += 1;
    items.push({
      key: `failure-${runId || entry.at}-${entry.kind}`, severity: "failure", label,
      detail: refDetail(entry.ref), at: entry.at, links: refLinks(entry.ref || {}, { conversation: true }),
    });
  }

  conversations
    .filter((conv) => CONVERSATION_WAITING_STATUSES.has(conv.status))
    .slice(0, ATTENTION_CAP)
    .forEach((conv) => {
      items.push({
        key: `conversation-${conv.id}`, severity: "conversation",
        label: `שיחה ${conversationStatusLabel(conv.status)}`,
        detail: conv.last_message_preview || "", at: conv.updated_at,
        links: [{ to: `/conversations/${conv.id}`, label: "לשיחה" }],
      });
    });

  return items;
}

function OperatorAttention({ workspace }) {
  const items = deriveAttentionItems(workspace);
  return (
    <section aria-label="תשומת לב תפעולית" className="bg-white rounded-xl border shadow-sm p-4 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700">תשומת לב תפעולית</h3>
        {items.length > 0 && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">{items.length} פריטים לטיפול</span>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-sm text-gray-400 italic">אין פריטים הדורשים תשומת לב כרגע</p>
      ) : (
        <ul className="space-y-1.5">
          {items.map((item) => (
            <li
              key={item.key}
              className={`flex flex-wrap items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm ${SEVERITY_STYLE[item.severity]}`}
            >
              <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${SEVERITY_DOT[item.severity]}`} />
              <span className="font-medium whitespace-nowrap">{item.label}</span>
              {item.detail && <span className="text-xs opacity-80 truncate max-w-full">{item.detail}</span>}
              <span className="text-[11px] opacity-60 whitespace-nowrap">{fmt(item.at)}</span>
              <span className="flex gap-2 text-xs mr-auto whitespace-nowrap">
                {item.links.map((l) => (
                  <Link key={l.to} to={l.to} className="underline hover:opacity-80">{l.label}</Link>
                ))}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// Operator-facing split of the active-run statuses: who is the run waiting on. Mirrors the
// run-status→bucket vocabulary used by the runs dashboard (ACTIVE_STATUSES / REVIEW_STATUSES)
// and the backend's _RUN_STATUS_HE labels.
const QUEUE_BUCKETS = [
  { key: "active", label: "בעבודה", statuses: ["pending", "analyzing", "diagnosing", "executing", "connection_active"], chip: "bg-violet-50 border-violet-200 text-violet-700" },
  { key: "technician", label: "ממתין לסקירת טכנאי", statuses: ["awaiting_technician_review"], chip: "bg-amber-50 border-amber-200 text-amber-700" },
  { key: "approval", label: "ממתין לאישור", statuses: ["awaiting_approval"], chip: "bg-orange-50 border-orange-200 text-orange-700" },
  { key: "customer", label: "ממתין ללקוח", statuses: ["waiting_for_customer_connection", "waiting_for_customer_answer"], chip: "bg-blue-50 border-blue-200 text-blue-700" },
];

const PAUSED_CHIP = { key: "paused", label: "מושהה", chip: "bg-gray-100 border-gray-300 text-gray-600" };

function queueBuckets({ current_work, assigned_tickets }) {
  const buckets = { active: [], technician: [], approval: [], customer: [], paused: [] };
  const seen = new Set();
  for (const run of [current_work, ...assigned_tickets]) {
    if (!run || seen.has(run.run_id)) continue;
    seen.add(run.run_id);
    const bucket = QUEUE_BUCKETS.find((b) => b.statuses.includes(run.run_status));
    (bucket ? buckets[bucket.key] : buckets.paused).push(run);
  }
  return buckets;
}

function queueTitle(runs) {
  if (runs.length === 0) return undefined;
  return runs.slice(0, 5).map((r) => (r.ticket_number ? `#${r.ticket_number}` : r.ticket_title || "משימה")).join(", ");
}

function QueueStrip({ workspace }) {
  const buckets = queueBuckets(workspace);
  const chip = (bucket) => {
    const runs = buckets[bucket.key];
    return (
      <span
        key={bucket.key}
        aria-label={`${bucket.label}: ${runs.length}`}
        title={queueTitle(runs)}
        className={`text-[11px] px-2 py-0.5 rounded-full border ${runs.length > 0 ? bucket.chip : "bg-gray-50 border-gray-200 text-gray-400"}`}
      >
        {bucket.label} <span className="font-semibold">{runs.length}</span>
      </span>
    );
  };
  return (
    <div className="bg-white rounded-xl border shadow-sm px-3 py-2 flex flex-wrap items-center gap-1.5">
      <span className="text-xs font-semibold text-gray-500 ml-2">תור העבודה</span>
      {QUEUE_BUCKETS.map(chip)}
      {buckets.paused.length > 0 && chip(PAUSED_CHIP)}
    </div>
  );
}

function fmt(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("he-IL"); } catch { return ""; }
}

function Card({ title, children, count }) {
  return (
    <div className="bg-white rounded-xl border shadow-sm p-4 space-y-2 min-h-[8rem]">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        {count != null && <span className="text-xs text-gray-400">{count}</span>}
      </div>
      {children}
    </div>
  );
}

function CapabilityRow({ label, children }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
      <dt className="w-28 shrink-0 text-xs font-medium text-gray-500">{label}</dt>
      <dd className="flex flex-wrap items-center gap-1.5 text-[11px]">{children}</dd>
    </div>
  );
}

// The tool list on this screen is `capabilities.tools` — what the Tool Registry entitles this
// employee to. Whether that entitlement is enforced at execution time is a deployment setting the
// screen cannot read, so the server states it on the same payload; wherever the list is shown as
// this employee's authority, this states what it currently amounts to. One wording, one owner
// (entitlementEnforcementVocabulary) — never restated per surface.
// testId is per surface on purpose: the four surfaces that show the tool list can be on screen at
// once, and a test that pins one of them must not be satisfiable by another.
function EntitlementBoundaryNote({ capabilities, className, testId }) {
  const note = entitlementBoundaryNote(capabilities);
  if (!note) return null;
  return (
    <p data-testid={testId} className={className}>
      {note}
    </p>
  );
}

function TechnicalDrawer({ workspace }) {
  const [open, setOpen] = useState(false);
  const { agent, capabilities } = workspace;
  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-gray-500 hover:text-gray-700 underline decoration-dotted"
      >
        {open ? "הסתר פרטים טכניים" : "פרטים טכניים"}
      </button>
      {open && (
        <div data-testid="technical-drawer" className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-gray-700">
          <div className="space-y-1">
            <div><span className="font-medium">רמת הרשאה:</span> {agent.permission_tier}</div>
            <div><span className="font-medium">מודל:</span> <span className="font-mono" dir="ltr">{agent.model}</span></div>
            <div className="font-medium mt-2">חיבורים</div>
            {capabilities.connections.length === 0 && <div className="text-gray-400">אין חיבורים</div>}
            {capabilities.connections.map((c) => (
              <div key={c.id} className="flex items-center gap-2">
                <span>{systemTypeLabel(c.system_type)}</span>
                <span className="text-gray-400">· {c.display_name}</span>
                {c.last_test_status && (
                  <span className={`px-1.5 rounded ${c.last_test_status === "ok" || c.last_test_status === "success" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}`}>
                    {c.last_test_status}
                  </span>
                )}
              </div>
            ))}
          </div>
          <div>
            <div className="font-medium mb-1">כלים מורשים ({capabilities.tools.length})</div>
            <div className="flex flex-wrap gap-1">
              {capabilities.tools.map((t) => (
                <span
                  key={t.name}
                  title={t.strong_approval_required ? "דורש אישור מוגבר" : t.approval_required ? "דורש אישור" : "קריאה בלבד"}
                  className={`px-1.5 py-0.5 rounded border ${
                    t.strong_approval_required ? "bg-purple-50 border-purple-200 text-purple-700"
                    : t.approval_required ? "bg-orange-50 border-orange-200 text-orange-700"
                    : "bg-blue-50 border-blue-200 text-blue-700"
                  }`}
                >
                  {t.name}
                </span>
              ))}
            </div>
            <EntitlementBoundaryNote
              capabilities={capabilities}
              testId="entitlement-note-technical-drawer"
              className="mt-1.5 text-[11px] text-amber-700"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Systems readiness (DIGEMP-EMPLOYEE-CONNECTION-READINESS-FRONTEND-021) ─────

function ReadinessRow({ item }) {
  // An unrecognized state value is shown as "unknown" — the one direction that never overclaims.
  const meta = READINESS_STATE_META[item.state] || READINESS_STATE_META.unknown;
  const access = READINESS_ACCESS_META[item.access_mode];
  // The map settles a known arm regardless of what the projection put in `display_name`; the
  // identity fallback is only for a token this app has no label for, and echoing it is honest.
  const primary = SYSTEM_TYPE_HE[item.system_type] || item.connector_display_name || item.system_type;
  const unimplemented = item.state === "needs_attention" && item.implemented === false;
  const missingFields = item.state === "needs_attention" && Array.isArray(item.missing_required_fields)
    ? item.missing_required_fields
    : [];
  const stateHint = item.state === "unavailable" && item.last_test_status
    ? `${meta.hint} (${item.last_test_status})`
    : meta.hint;

  return (
    <li className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-800">{primary}</span>
        {item.display_name && item.display_name !== primary && (
          <span className="text-xs text-gray-500">· {item.display_name}</span>
        )}
        <span className={`text-[11px] px-2 py-0.5 rounded-full border ${meta.cls}`} title={stateHint}>
          {meta.label}
        </span>
        {access && (
          <span className={`text-[11px] px-2 py-0.5 rounded-full border ${access.cls}`} title={access.hint}>
            {access.label}
          </span>
        )}
        <span className="text-[11px] text-gray-500 mr-auto">
          {item.last_verified_at ? `נבדק לאחרונה: ${fmt(item.last_verified_at)}` : "טרם נבדק"}
        </span>
      </div>
      {(unimplemented || missingFields.length > 0) && (
        <div className="text-[11px] text-amber-700 mt-1 space-y-0.5">
          {unimplemented && <div>המחבר טרם מומש</div>}
          {missingFields.length > 0 && (
            <div>
              חסרים שדות הגדרה: <span className="font-mono" dir="ltr">{missingFields.join(", ")}</span>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

function ConnectionReadinessSection({ agentId }) {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: connectionReadinessQueryKey(agentId),
    queryFn: () => api.get(`/api/agents/${agentId}/connection-readiness`).then((r) => r.data),
    enabled: !!agentId,
  });

  return (
    <div className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-700">
          מוכנות מערכות{data ? ` (${data.summary.total})` : ""}
        </h3>
        <span className="text-[11px] text-gray-400">מבוסס על תוצאת בדיקת החיבור האחרונה שנשמרה — ללא בדיקה חיה</span>
      </div>

      {isLoading && <p className="text-sm text-gray-400">טוען מצב מערכות...</p>}

      {isError && (
        <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          לא ניתן לטעון את מצב המערכות
          <button type="button" onClick={() => refetch()} className="mr-2 underline">נסה שוב</button>
        </p>
      )}

      {data && data.summary.total === 0 && (
        <p className="text-sm text-gray-400 italic">לא הוגדרו מערכות לעובד זה</p>
      )}

      {data && data.summary.total > 0 && (
        <>
          <div className="flex flex-wrap items-center gap-1.5">
            {READINESS_STATE_ORDER.map((state) => (
              <span key={state} className={`text-[11px] px-2 py-0.5 rounded-full border ${READINESS_STATE_META[state].cls}`}>
                {READINESS_STATE_META[state].label}: {data.summary[state]}
              </span>
            ))}
          </div>
          <ul className="space-y-2">
            {data.connections.map((item) => <ReadinessRow key={item.connection_id} item={item} />)}
          </ul>
          <p className="text-[11px] text-gray-500">
            קריאה בלבד — העובד קורא נתונים בלבד · כתיבה באישור — העובד יכול להציע פעולה, אך הביצוע מחייב אישור
          </p>
        </>
      )}
    </div>
  );
}

// ─── Experience timeline (DIGEMP-EXPERIENCE-OUTCOMES-FRONTEND-024) ─────────────
//
// The operator-readable "what has this employee actually done" view. Every entry is derived from
// an audited AgentRun record (status + approver context + bounded result summary); nothing here is
// a model self-claim, and failed/rejected/unverified work is shown but never presented as a
// positive outcome. Only whitelisted scalar fields are rendered — never run.context, prompts or
// payloads — so raw connection ids/credentials stored in a run can not leak onto this screen.

export const OUTCOME_META = {
  verified:   { icon: "✓", label: "מאומת",    cls: "bg-green-100 text-green-700",                              fallback: "עבודה הושלמה ואומתה על ידי טכנאי" },
  unverified: { icon: "◷", label: "טרם אומת", cls: "bg-amber-50 text-amber-700 border border-amber-200",        fallback: "עבודה הושלמה ללא אימות טכנאי" },
  failed:     { icon: "✕", label: "נכשל",     cls: "bg-red-100 text-red-700",                                  fallback: "הריצה נכשלה" },
  cancelled:  { icon: "⊘", label: "נדחה",     cls: "bg-slate-200 text-slate-700",                              fallback: "הריצה נדחתה על ידי טכנאי" },
};

function boundedText(value, max) {
  return typeof value === "string" && value.trim() ? value.trim().slice(0, max) : null;
}

function boundedNumber(value) {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function technicianOf(run) {
  return boundedText(run?.context?.technician, 80);
}

function outcomeOf(run) {
  const status = run?.status;
  if (status === "failed") return "failed";
  if (status === "rejected") return "cancelled";
  if (status !== "resolved" && status !== "completed") return null;
  return technicianOf(run) ? "verified" : "unverified";
}

function runTimestamp(run) {
  return run?.finished_at || run?.updated_at || run?.created_at || null;
}

// The raw-field drill-down behind `אבחון טכני`. Its `ביטחון` field is a run's own
// `analysis.confidence` and is subject to the product's standing rule: a degraded analysis'
// figure is the fallback's own arithmetic (`MockAnalysisAdapter` caps it at 70, so it reads as a
// real "medium" rather than as "nothing ran"), which is why the producers
// (`action_executor._build_add_comment_body`, `manager_agent._analysis_complete_description`,
// `conversation_service.stage_human_text`) drop it and name the arm instead, and why every other
// surface that draws it draws its provenance first (FULL-0051, FULL-0057). This pane was filed as
// a judgement call on the reading that naming the `מודל` field is already the disclosure. That
// reading does not hold on the build the rule is about: with no `ANALYSIS_PROVIDER` the adapter is
// the deterministic mock, whose `get_last_usage()` returns None, so `_apply_analysis_telemetry`
// returns before writing anything and `run.model` is None — the pane then prints a bare
// `ביטחון: N%` and names nothing at all. The disclosure is therefore drawn here, and it is drawn
// unconditionally rather than beside the model line, because the arm where a real model IS named
// (`parse_failed` on a configured provider) is the one that would otherwise attribute the
// fallback's figure to that model. The figure itself is kept: this pane's question is which fields
// the record carries, so censoring it would make the pane lie about the record.
function EntryDiagnostics({ run, confidence, cost }) {
  const [open, setOpen] = useState(false);
  const degradedLabel = degradedAnalysisLabel(run?.analysis?.degraded);
  return (
    <div className="text-[11px] text-gray-500">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="underline decoration-dotted hover:text-gray-700"
      >
        {open ? "הסתר אבחון טכני" : "אבחון טכני"}
      </button>
      {open && (
        <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
          {degradedLabel && (
            <span
              data-testid={typeof run?.id === "string" ? `entry-diagnostics-degraded-${run.id}` : undefined}
              className="inline-block font-medium leading-none bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5"
            >
              {degradedLabel}
            </span>
          )}
          {boundedText(run.model, 80) && (
            <span>מודל: <span className="font-mono" dir="ltr">{boundedText(run.model, 80)}</span></span>
          )}
          {Number.isInteger(confidence) && confidence >= 0 && confidence <= 100 && <span>ביטחון: {confidence}%</span>}
          {cost != null && <span>עלות: ${cost.toFixed(4)}</span>}
          {typeof run.id === "string" && <span>מזהה ריצה: <span className="font-mono" dir="ltr">{run.id.slice(0, 8)}…</span></span>}
        </div>
      )}
    </div>
  );
}

// The rule that decides what may be shown as an employee's learning candidates is the backend's,
// not this component's: `knowledge_gap_service.extract_gaps_from_run` refuses to record a degraded
// analysis's `missing_information`, because a fallback's standing "missing information" is boilerplate
// rather than a gap, and a recorded gap is GLOBAL — it is injected into other customers' prompts. The
// employee-level view of the same field is the `learning_goals` projection, which is built from those
// recorded gaps and therefore already excludes degraded runs. This box reads the run field directly,
// so it has to apply the same rule instead of promoting what the product has already filtered out into
// an agenda the employee is said to stand behind. `withheldLabel` is non-null only when there was
// something to withhold, so a degraded run with no candidates renders exactly as it did before.
export function learningCandidates(run) {
  const items = Array.isArray(run?.analysis?.missing_information)
    ? run.analysis.missing_information
        .map((item) => boundedText(item, 120))
        .filter(Boolean)
        .slice(0, 3)
    : [];
  const armLabel = degradedAnalysisLabel(run?.analysis?.degraded);
  if (armLabel) return { candidates: [], withheldLabel: items.length > 0 ? armLabel : null };
  return { candidates: items, withheldLabel: null };
}

export function ExperienceTimeline({ runs = [], isLoading = false, isError = false, showAgent = false }) {
  const runList = Array.isArray(runs) ? runs : [];
  const entries = runList
    .map((run) => ({ run, outcome: outcomeOf(run) }))
    .filter((entry) => entry.outcome)
    .sort((a, b) => String(runTimestamp(b.run) || "").localeCompare(String(runTimestamp(a.run) || "")));

  return (
    <div className="bg-white rounded-xl border shadow-sm p-4 space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">ניסיון ותוצאות</h3>
        {entries.length > 0 && (
          <span className="text-xs text-gray-400">{entries.length} עבודות משמעותיות מתוך {runList.length} ריצות</span>
        )}
      </div>
      <p className="text-[11px] text-gray-400">
        מבוסס על רשומות הריצות המבוקרות של העובד — מאומת: אושר על ידי טכנאי · טרם אומת: הסתיים בלי אימות טכנאי · נדחה: בוטל על ידי טכנאי
      </p>

      {isLoading && entries.length === 0 && <p className="text-sm text-gray-400 italic">טוען תוצאות עבודה...</p>}
      {!isLoading && isError && entries.length === 0 && (
        <p role="alert" className="text-sm text-red-600">לא ניתן לטעון את תוצאות העבודה</p>
      )}
      {!isLoading && !isError && entries.length === 0 && (
        <p className="text-sm text-gray-400 italic">אין עדיין עבודות שהושלמו לתיעוד</p>
      )}

      <ol className="space-y-2">
        {entries.map(({ run, outcome }) => {
          const meta = OUTCOME_META[outcome];
          const technician = technicianOf(run);
          const summary = boundedText(run.result_summary, 200)
            || boundedText(run.ticket_title, 200)
            || meta.fallback;
          const confidence = boundedNumber(run?.analysis?.confidence);
          const cost = boundedNumber(run.estimated_cost);
          const feedbackCount = boundedNumber(run.feedback_count);
          const { candidates: lessons, withheldLabel } = learningCandidates(run);
          return (
            <li key={run.id} className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1.5">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap ${meta.cls}`}>
                  {meta.icon} {meta.label}
                </span>
                {showAgent && boundedText(run.agent_name, 80) && (
                  <span className="text-[11px] text-gray-600">{boundedText(run.agent_name, 80)}</span>
                )}
                {outcome === "verified" && technician && (
                  <span className="text-[11px] text-teal-700">אושר על ידי <span dir="ltr">{technician}</span></span>
                )}
                {outcome === "cancelled" && technician && (
                  <span className="text-[11px] text-slate-600">בוטל על ידי <span dir="ltr">{technician}</span></span>
                )}
                {feedbackCount > 0 && (
                  <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-700 border border-violet-200">
                    תוקן על ידי טכנאי ({feedbackCount})
                  </span>
                )}
                <span className="text-[11px] text-gray-400 whitespace-nowrap mr-auto">{fmt(runTimestamp(run))}</span>
              </div>

              <p className="text-sm text-gray-800">{summary}</p>

              <div className="flex flex-wrap items-center gap-3 text-xs">
                {run.ticket_id && (
                  <Link
                    to={`/tickets/${run.ticket_id}`}
                    className="text-blue-600 hover:underline"
                    aria-label={`פתח את הקריאה${run.ticket_number ? ` #${run.ticket_number}` : ""} שממנה הגיעה העבודה`}
                  >
                    {run.ticket_number ? `קריאה #${run.ticket_number}` : "קריאה מקושרת"}
                  </Link>
                )}
                <Link
                  to={`/agent-runs/${run.id}`}
                  className="text-blue-600 hover:underline"
                  aria-label={`פתח את הריצה${run.ticket_number ? ` של קריאה #${run.ticket_number}` : ""}`}
                >
                  צפה בריצה
                </Link>
              </div>

              {lessons.length > 0 && (
                <div className="text-xs text-gray-600 bg-amber-50/70 border border-amber-100 rounded px-2 py-1">
                  <span className="font-medium text-amber-800">מועמדים ללמידה:</span>
                  <ul className="list-disc pr-4 mt-0.5 space-y-0.5">
                    {lessons.map((lesson, i) => <li key={i}>{lesson}</li>)}
                  </ul>
                </div>
              )}

              {withheldLabel && (
                <div className="text-xs text-amber-800 bg-amber-50/70 border border-amber-100 rounded px-2 py-1">
                  <span className="font-medium">מועמדים ללמידה:</span>{" "}
                  <span>לא הוצגו — {withheldLabel}</span>
                </div>
              )}

              <EntryDiagnostics run={run} confidence={confidence} cost={cost} />
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ─── ESET specialist readiness (DIGEMP-ESET-SPECIALIST-FRONTEND-035) ─────────
//
// The operator view of what this employee can actually do against customer endpoints in ESET:
// which reads it is cleared for, what it may recommend on top of them (read-only), where its
// known limits are, and when the work must escalate to a technician for approval. Evidence
// comes from two sources: the observed ESET connection the workspace already projects (last
// verification status and when it happened) and the additive per-domain readiness contract
// (`eset_readiness` — accepted on the workspace payload, its agent projection or the page's
// agent detail, and as the ESET pack inside the specialist_packs descriptor list). Missing,
// stale, undated or unverified evidence is labelled as such; the section never invents
// capabilities, limits, timestamps or sources, and it adds no write, scan or remediation
// control — every state-changing protection action keeps going through the existing proposal →
// approval → execution flow. Only whitelisted scalar fields are rendered (bounded lengths,
// capped lists), so credential material stored next to the connection or the contract can not
// leak onto this screen.

const ESET_SYSTEM_TYPES = new Set(["eset", "eset_protect"]);
// Verification evidence ages out on the same 25h day-cycle convention the workspace uses for
// connection checks (backup_guardian default max age): an older verification is stale, and says so.
const ESET_STALE_HOURS = 25;
const ESET_FUTURE_SKEW_HOURS = 5 / 60;

const ESET_STATE_META = {
  ready: { label: "מוכן", cls: "bg-green-100 text-green-700" },
  partial: { label: "מוכן חלקית", cls: "bg-amber-100 text-amber-800 border border-amber-200" },
  not_ready: { label: "לא מוכן", cls: "bg-red-100 text-red-700" },
  unknown: { label: "לא ידוע", cls: "bg-gray-100 text-gray-600" },
};

const ESET_TEST_STATUS_META = {
  ok: { label: "אימות תקין", cls: "bg-green-100 text-green-700" },
  success: { label: "אימות תקין", cls: "bg-green-100 text-green-700" },
  config_error: { label: "שגיאת תצורה", cls: "bg-red-100 text-red-700" },
  auth_error: { label: "שגיאת אימות", cls: "bg-red-100 text-red-700" },
  permission_error: { label: "אין הרשאה", cls: "bg-red-100 text-red-700" },
  network_error: { label: "שגיאת רשת", cls: "bg-red-100 text-red-700" },
  rate_limited: { label: "הוגבל קצב בקשות", cls: "bg-amber-100 text-amber-800" },
  timeout: { label: "פסק זמן", cls: "bg-amber-100 text-amber-800" },
  not_implemented: { label: "לא נתמך", cls: "bg-amber-100 text-amber-800" },
  unsupported: { label: "לא נתמך", cls: "bg-amber-100 text-amber-800" },
  cross_customer: { label: "חשד לחציית לקוחות", cls: "bg-red-100 text-red-700" },
  error: { label: "שגיאה", cls: "bg-red-100 text-red-700" },
};

const ESET_STATE_KEYS = ["state", "readiness", "status"];
const ESET_READ_KEYS = ["supported_reads", "read", "reads", "read_actions", "read_only_actions", "supported_read_capabilities", "read_capabilities"];
const ESET_LIMITS_KEYS = ["limits", "known_limits", "constraints", "known_constraints"];
const ESET_ESCALATION_KEYS = ["escalation", "escalation_conditions", "escalate_when", "escalation_triggers", "approval_boundary", "approval_required_for", "handoff_conditions"];
const ESET_EVIDENCE_AT_KEYS = ["last_verified_at", "last_tested_at", "verified_at", "checked_at", "evidence_at", "updated_at", "as_of"];
const ESET_GENERATED_KEYS = ["generated_at", "as_of", "updated_at"];

function esetField(raw, keys) {
  if (!raw || typeof raw !== "object") return undefined;
  for (const key of keys) {
    if (raw[key] !== undefined && raw[key] !== null) return raw[key];
  }
  return undefined;
}

function esetToken(value) {
  return typeof value === "string" ? value.trim().toLowerCase().replace(/[\s-]+/g, "_") : "";
}

function esetText(value, max) {
  return typeof value === "string" && value.trim() ? value.trim().slice(0, max) : null;
}

function esetEntries(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) return [value];
  if (value && typeof value === "object") return [value];
  return [];
}

function esetNotes(value, keys, max, limit) {
  return esetEntries(value)
    .map((entry) => (typeof entry === "string" ? esetText(entry, max) : esetText(esetField(entry, keys), max)))
    .filter(Boolean)
    .slice(0, limit);
}

function esetReads(value, limit = 8) {
  return esetEntries(value)
    .map((entry) => {
      if (typeof entry === "string") {
        const label = esetText(entry, 60);
        return label ? { label, detail: null } : null;
      }
      const label = esetText(esetField(entry, ["name", "operation", "action", "tool", "label", "title", "id"]), 60);
      if (!label) return null;
      return { label, detail: esetText(esetField(entry, ["description", "detail", "note"]), 140) };
    })
    .filter(Boolean)
    .slice(0, limit);
}

function esetDate(value) {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function esetAgeLabel(hours) {
  if (hours < 1) return "לפני פחות משעה";
  const whole = Math.floor(hours);
  if (whole < 2) return "לפני שעה";
  if (hours < 48) return `לפני ${whole} שעות`;
  const days = Math.floor(hours / 24);
  return days < 2 ? "לפני יום" : `לפני ${days} ימים`;
}

function esetAgeLine(at, nowMs, { freshVerb, freshCls, undatedText }) {
  const date = esetDate(at);
  if (!date) return { text: undatedText, cls: "text-amber-700" };
  const age = (nowMs - date.getTime()) / 3600000;
  if (age < -ESET_FUTURE_SKEW_HOURS) {
    return { text: "חתימת הזמן של הראיה עתידית — הראיה אינה אמינה עד לאימות מחדש.", cls: "text-amber-700" };
  }
  const base = `${freshVerb}: ${fmt(at)} (${esetAgeLabel(age)})`;
  if (age > ESET_STALE_HOURS) {
    return { text: `ראיה ישנה — יש לאמת מחדש · ${base}.`, cls: "text-amber-700" };
  }
  return { text: `${base}.`, cls: freshCls };
}

function esetConnectionOf(connections) {
  return (Array.isArray(connections) ? connections : []).find(
    (connection) => connection && ESET_SYSTEM_TYPES.has(esetToken(connection.system_type))
  ) || null;
}

function esetContractOf(workspace, agentDetail) {
  const pick = (source) =>
    source?.eset_readiness ?? source?.eset_specialist_readiness ?? source?.eset_specialist
    ?? source?.eset_protect_readiness ?? source?.eset ?? null;
  const direct = pick(workspace) ?? pick(workspace?.agent) ?? pick(agentDetail) ?? null;
  if (direct && typeof direct === "object" && !Array.isArray(direct)) return { block: direct, pack: null };
  const packsRoot = workspace?.specialist_packs ?? workspace?.specialist_pack_descriptors
    ?? workspace?.agent?.specialist_packs ?? workspace?.agent?.specialist_pack_descriptors
    ?? agentDetail?.specialist_packs ?? agentDetail?.specialist_pack_descriptors ?? null;
  const list = Array.isArray(packsRoot)
    ? packsRoot
    : packsRoot && typeof packsRoot === "object"
      ? esetField(packsRoot, ["packs", "descriptors", "pack_descriptors", "items"])
      : undefined;
  const pack = (Array.isArray(list) ? list : []).find((entry) => {
    if (!entry || typeof entry !== "object") return false;
    const token = esetToken(esetField(entry, ["domain", "domain_name", "domain_label", "name", "pack", "title", "specialty", "id"]));
    return token === "eset" || token === "eset_protect";
  }) || null;
  return { block: null, pack };
}

function EsetBlock({ title, testId, children }) {
  return (
    <div data-testid={testId} className="rounded-lg border border-gray-200 bg-gray-50/60 p-2.5 space-y-1.5">
      <div className="text-xs font-medium text-gray-700">{title}</div>
      {children}
    </div>
  );
}

export function EsetSpecialistSection({ workspace, agentDetail }) {
  const nowMs = Date.now();
  const connection = esetConnectionOf(workspace?.capabilities?.connections);
  const { block, pack } = esetContractOf(workspace, agentDetail);
  const contractReported = Boolean(block || pack);

  const field = (keys) => {
    const fromBlock = block ? esetField(block, keys) : undefined;
    if (fromBlock !== undefined) return fromBlock;
    return pack ? esetField(pack, keys) : undefined;
  };

  const stateRaw = contractReported ? field(ESET_STATE_KEYS) : undefined;
  const stateToken = ESET_STATE_META[esetToken(stateRaw)] ? esetToken(stateRaw) : "unknown";
  const stateMeta = ESET_STATE_META[stateToken];
  const stateReported = esetText(stateRaw, 40);

  const readsRaw = field(ESET_READ_KEYS);
  const limitsRaw = field(ESET_LIMITS_KEYS);
  const escalationRaw = field(ESET_ESCALATION_KEYS);
  const reads = esetReads(readsRaw);
  const limits = esetNotes(limitsRaw, ["limit", "text", "title", "description"], 140, 6);
  const escalation = esetNotes(escalationRaw, ["condition", "text", "title", "trigger", "description"], 160, 6);

  const generatedAt = field(ESET_GENERATED_KEYS);

  const statusRaw = esetToken(connection?.last_test_status);
  const statusMeta = ESET_TEST_STATUS_META[statusRaw] || null;
  const statusLabel = statusMeta
    ? statusMeta.label
    : statusRaw
      ? esetText(connection.last_test_status, 40)
      : "טרם אומת";
  const statusCls = statusMeta
    ? statusMeta.cls
    : statusRaw ? "bg-gray-100 text-gray-600" : "bg-amber-100 text-amber-800";

  const verifiedOk = statusRaw === "ok" || statusRaw === "success";

  const connectionEvidence = connection
    ? esetAgeLine(connection.last_tested_at, nowMs, {
        freshVerb: verifiedOk ? "אומת לאחרונה" : "נבדק לאחרונה",
        freshCls: verifiedOk ? "text-green-700" : "text-gray-500",
        undatedText: verifiedOk
          ? "האימות דווח תקין אך מועדו טרם תועד — אין ראיה מתוארכת."
          : "מועד האימות האחרון טרם תועד — אין ראיה מתוארכת לאימות.",
      })
    : null;
  const contractEvidence = !connection && field(ESET_EVIDENCE_AT_KEYS) !== undefined
    ? esetAgeLine(field(ESET_EVIDENCE_AT_KEYS), nowMs, {
        freshVerb: "עודכן לאחרונה",
        freshCls: "text-gray-600",
        undatedText: "מועד עדכון המוכנות טרם תועד — אין ראיה מתוארכת.",
      })
    : null;

  return (
    <section aria-label="מומחה ESET" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700">מומחה ESET</h3>
        <span
          className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${stateMeta.cls}`}
          title={stateToken === "unknown" && stateReported ? `דווח על-ידי השרת: ${stateReported}` : undefined}
        >
          {stateMeta.label}
        </span>
        {generatedAt != null && <span className="text-[11px] text-gray-400 mr-auto">עודכן: {fmt(generatedAt)}</span>}
      </div>
      <p className="text-[11px] text-gray-400">
        ראיות קריאה בלבד לעבודת הגנת נקודות קצה: מה העובד יכול לקרוא ולהמליץ, מגבלות ידועות, ומתי נדרשת הסלמה או אישור טכנאי.
      </p>
      {!contractReported && (
        <p className="text-xs text-gray-500">
          מוכנות מומחה ESET טרם נמסרה על-ידי השרת — לא מוצגות כאן יכולות קריאה, מגבלות או תנאי הסלמה ללא נתונים מגבים.
        </p>
      )}

      <EsetBlock title="מוכנות החיבור" testId="eset-evidence">
        {connection ? (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-700">
              <span>חיבור {SYSTEM_TYPE_HE[connection.system_type] || "ESET"}</span>
              <span className={`px-1.5 py-0.5 rounded border whitespace-nowrap ${statusCls}`}>{statusLabel}</span>
            </div>
            <p className={`text-xs ${connectionEvidence.cls}`}>{connectionEvidence.text}</p>
          </>
        ) : contractEvidence ? (
          <>
            <p className="text-xs text-gray-500">לא נמצא חיבור ESET מוגדר לעובד זה.</p>
            <p className={`text-xs ${contractEvidence.cls}`}>ראיית המוכנות: {contractEvidence.text}</p>
          </>
        ) : (
          <p className="text-xs text-gray-500">לא נמצא חיבור ESET מוגדר לעובד זה — אין ראיות הגנה זמינות להצגה.</p>
        )}
      </EsetBlock>

      <EsetBlock title="קריאות נתמכות" testId="eset-reads">
        {reads.length > 0 ? (
          <ul className="space-y-1">
            {reads.map((read, i) => (
              <li key={`${read.label}-${i}`} className="text-xs text-gray-700 space-y-0.5">
                <span className="font-mono" dir="ltr">{read.label}</span>
                {read.detail && <div className="text-gray-500">{read.detail}</div>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-gray-500">
            {readsRaw !== undefined
              ? "השרת דיווח שלא הוגדרו יכולות קריאה לעובד זה."
              : "טרם דווחו יכולות קריאה על-ידי השרת."}
          </p>
        )}
      </EsetBlock>

      <EsetBlock title="מגבלות המומחה" testId="eset-limits">
        {limits.length > 0 ? (
          <ul className="list-disc pr-4 space-y-1">
            {limits.map((limit, i) => <li key={i} className="text-xs text-gray-700">{limit}</li>)}
          </ul>
        ) : (
          <p className="text-xs text-gray-500">
            {limitsRaw !== undefined
              ? "השרת דיווח שלא תועדו מגבלות ידועות."
              : "טרם דווחו מגבלות ידועות על-ידי השרת."}
          </p>
        )}
      </EsetBlock>

      <EsetBlock title="הסלמה ואישור" testId="eset-escalation">
        {escalation.length > 0 ? (
          <ul className="list-disc pr-4 space-y-1">
            {escalation.map((condition, i) => <li key={i} className="text-xs text-gray-700">{condition}</li>)}
          </ul>
        ) : (
          <p className="text-xs text-gray-500">
            {escalationRaw !== undefined
              ? "השרת דיווח שלא הוגדרו תנאי הסלמה."
              : "טרם דווחו תנאי הסלמה על-ידי השרת."}
          </p>
        )}
      </EsetBlock>

      <p className="text-[11px] text-gray-400">
        תצוגת ראיות בלבד — הכרטיס אינו כולל פעולות כתיבה, סריקה או שינוי מדיניות הגנה; כל פעולה משנה-מצב ממשיכה לעבור במסלול ההצעה, האישור והביצוע הקיים.
      </p>
    </section>
  );
}

// ─── Pilot acceptance (DIGEMP-PILOT-ACCEPTANCE-OPERATIONS-FRONTEND-031) ───────
//
// Operator-facing end-to-end readiness for the Digital Employee pilot, in human language. Every
// state derives from evidence this page already loaded (the workspace projection, the audited run
// records the page fetched, and the agent detail payload) — the panel fetches nothing, shows no
// raw runtime data, and grants nothing. A domain is "proven" (green) only against positive
// evidence; a known blocking condition is "דורש תשומת לב"; anything today's contracts cannot show
// is explicitly "טרם ניתן לאמת" and never rendered as green.

const PILOT_STATES = {
  proven:    { label: "הוכח",           icon: "✓", cls: "bg-green-100 text-green-700" },
  attention: { label: "דורש תשומת לב",  icon: "!", cls: "bg-amber-100 text-amber-700" },
  unknown:   { label: "טרם ניתן לאמת", icon: "?", cls: "bg-gray-200 text-gray-600" },
};

const PILOT_CONNECTION_OK = new Set(["ok", "success"]);
const PILOT_REVIEW_STATUSES = new Set(["awaiting_approval", "awaiting_technician_review"]);
const PILOT_DECIDED_STATUSES = new Set(["approved", "rejected"]);

const oneMany = (n, one, many) => (n === 1 ? one : many);

function countPendingApprovals(runList, workspace, agentDetail) {
  // The server-side count on the agent detail is authoritative (uncapped); the workspace/run
  // projections are the fallback when only the workspace payload is present.
  if (typeof agentDetail?.pending_runs_count === "number") return agentDetail.pending_runs_count;
  const ids = new Set();
  const refs = [
    ...(Array.isArray(workspace?.assigned_tickets) ? workspace.assigned_tickets : []),
    workspace?.current_work,
  ];
  for (const ref of refs) {
    if (ref?.run_id && PILOT_REVIEW_STATUSES.has(ref.run_status)) ids.add(ref.run_id);
  }
  for (const run of runList) {
    if (run?.id && PILOT_REVIEW_STATUSES.has(run.status)) ids.add(run.id);
  }
  return ids.size;
}

export function derivePilotAcceptance({ workspace, runs = [], agentDetail = null, runsLoading = false, runsError = false }) {
  const runList = Array.isArray(runs) ? runs : [];
  const conversations = Array.isArray(workspace?.recent_conversations) ? workspace.recent_conversations : [];
  const connections = Array.isArray(workspace?.capabilities?.connections) ? workspace.capabilities.connections : [];
  const assigned = Array.isArray(workspace?.assigned_tickets) ? workspace.assigned_tickets : [];
  const currentWork = workspace?.current_work || null;
  const agentEnabled = workspace?.agent?.is_enabled !== false;

  // Run-dependent domains must not present "no evidence" while runs are still loading or failed to
  // load — those are different facts (the experience timeline follows the same rule).
  const runsNote = () => {
    if (runsError) return "לא ניתן לטעון את ראיות הריצות — לא ידוע";
    if (runsLoading && runList.length === 0) return "טוען ראיות ריצות…";
    return null;
  };

  const domains = [];
  const domain = (key, label, state, detail, links = []) => domains.push({ key, label, state, detail, links });

  // שיחה — conversations actually recorded with this employee.
  if (conversations.length > 0) {
    domain("conversation", "שיחה", "proven", "קיימות שיחות מוקלטות בין מפעילים לעובד", [
      { to: `/conversations/${conversations[0].id}`, label: "לשיחה האחרונה", aria: "פתח את השיחה האחרונה של העובד" },
      { to: "/conversations", label: "לכל השיחות" },
    ]);
  } else {
    domain("conversation", "שיחה", "unknown", "טרם התקיימה שיחה מוקלטת — אין ראיה", [
      { to: "/conversations", label: "למסך השיחות" },
    ]);
  }

  // קריאה — a run bound to a ticket, from the run records or the workspace projection.
  const ticketRef = runList.find((r) => r?.ticket_id)
    || (currentWork?.ticket_id ? currentWork : null)
    || assigned.find((t) => t?.ticket_id)
    || null;
  if (ticketRef) {
    domain("ticket", "קריאה", "proven", "קיימת ריצה המקושרת לקריאה", [
      { to: `/tickets/${ticketRef.ticket_id}`, label: ticketRef.ticket_number ? `קריאה #${ticketRef.ticket_number}` : "לקריאה", aria: "פתח את הקריאה המקושרת" },
    ]);
  } else {
    domain("ticket", "קריאה", "unknown", runsNote() || "לא נמצאה ריצה המקושרת לקריאה — אין ראיה", [
      { to: "/tickets", label: "למסך הקריאות" },
    ]);
  }

  // הקצאת עבודה — a disabled employee cannot be assigned anything; otherwise an active assignment
  // is the evidence, and its absence is an explicit unknown, not a pass.
  if (!agentEnabled) {
    domain("assignment", "הקצאת עבודה", "attention", "העובד מושבת — לא ניתן להקצות לו עבודה");
  } else if (currentWork || assigned.length > 0) {
    const ref = currentWork || assigned[0];
    const links = ref?.ticket_id
      ? [{ to: `/tickets/${ref.ticket_id}`, label: ref.ticket_number ? `קריאה #${ref.ticket_number}` : "לקריאה", aria: "פתח את הקריאה המשויכת לעובד" }]
      : [];
    domain("assignment", "הקצאת עבודה", "proven",
      ref?.ticket_id ? "קריאה פעילה משויכת לעובד" : "משימת שיחה פעילה משויכת לעובד", links);
  } else {
    domain("assignment", "הקצאת עבודה", "unknown", "לא הוקצתה לעובד קריאה — אין ראיה", [
      { to: "/tickets", label: "למסך הקריאות" },
    ]);
  }

  // ניתוב על-ידי מנהל — no routing evidence contract reaches this screen yet; say so explicitly
  // instead of implying the capability works.
  domain("manager_routing", "ניתוב על-ידי מנהל", "unknown", "נתיב ניתוב מנהל טרם הופעל לעובד — אין ראיה", [
    { to: "/conversations", label: "לשיחות" },
  ]);

  // אישור פעולות — a decided request proves the approval spine end-to-end; pending requests are
  // live and therefore attention, not green; nothing at all stays unknown.
  const decided = runList.filter((r) => r && PILOT_DECIDED_STATUSES.has(r.status)).length;
  const pending = countPendingApprovals(runList, workspace, agentDetail);
  if (decided > 0) {
    domain("approval", "אישור פעולות", "proven",
      `${oneMany(decided, "בקשת אישור אחת הוכרעה", `${decided} בקשות אישור הוכרעו`)} בריצות שנטענו`,
      [{ to: "/agent-runs", label: "למרכז הריצות" }]);
  } else if (pending > 0) {
    domain("approval", "אישור פעולות", "attention",
      `${oneMany(pending, "בקשה אחת ממתינה", `${pending} בקשות ממתינות`)} להחלטת מפעיל — המסלול פעיל, טרם הוכרע`,
      [{ to: "/agent-runs", label: "למרכז הריצות" }]);
  } else {
    domain("approval", "אישור פעולות", "unknown", runsNote() || "טרם הופעל מסלול אישור — אין ראיה",
      [{ to: "/agent-runs", label: "למרכז הריצות" }]);
  }

  // חיבורים — a connection confirmed by a test is the evidence; configured-but-unverified or
  // absent connections need operator attention and are never green.
  if (connections.length === 0) {
    domain("connections", "חיבורים למערכות", "attention", "לא מוגדרים חיבורים למערכות חיצוניות", [
      { to: "/connections", label: "למסך החיבורים" },
    ]);
  } else {
    const ok = connections.filter((c) => PILOT_CONNECTION_OK.has(c?.last_test_status)).length;
    if (ok > 0) {
      domain("connections", "חיבורים למערכות", "proven",
        connections.length === 1 ? "החיבור המוגדר נבדק בהצלחה" : `${ok} מתוך ${connections.length} חיבורים נבדקו בהצלחה`,
        [{ to: "/connections", label: "למסך החיבורים" }]);
    } else {
      domain("connections", "חיבורים למערכות", "attention",
        connections.length === 1 ? "החיבור המוגדר טרם נבדק בהצלחה" : `${connections.length} חיבורים מוגדרים — אף אחד לא נבדק בהצלחה`,
        [{ to: "/connections", label: "למסך החיבורים" }]);
    }
  }

  // עבודת דפדפן — same as manager routing: no evidence contract reaches this screen yet.
  domain("browser", "עבודת דפדפן", "unknown", "עבודת דפדפן טרם הופעלה לעובד — אין ראיה");

  // אימות תוצאות — technician confirmation of finished work, from the audited run records.
  const outcomes = runList.map((r) => outcomeOf(r)).filter(Boolean);
  const verified = outcomes.filter((o) => o === "verified").length;
  if (verified > 0) {
    const verifiedRef = runList.find((r) => outcomeOf(r) === "verified");
    domain("verification", "אימות תוצאות", "proven",
      `${oneMany(verified, "עבודה אחת אומתה", `${verified} עבודות אומתו`)} על-ידי טכנאי`, [
        { to: `/agent-runs/${verifiedRef.id}`, label: "לריצה המאומתת", aria: "פתח ריצה שאומתה על-ידי טכנאי" },
        { to: "/agent-runs", label: "למרכז הריצות" },
      ]);
  } else if (outcomes.length > 0) {
    domain("verification", "אימות תוצאות", "attention",
      `${oneMany(outcomes.length, "עבודה אחת הסתיימה", `${outcomes.length} עבודות הסתיימו`)} — אף אחת לא אומתה על-ידי טכנאי`,
      [{ to: "/agent-runs", label: "למרכז הריצות" }]);
  } else {
    domain("verification", "אימות תוצאות", "unknown", runsNote() || "טרם הסתיימו עבודות — אין ראיה",
      [{ to: "/agent-runs", label: "למרכז הריצות" }]);
  }

  return {
    summary: {
      total: domains.length,
      proven: domains.filter((d) => d.state === "proven").length,
      attention: domains.filter((d) => d.state === "attention").length,
      unknown: domains.filter((d) => d.state === "unknown").length,
    },
    domains,
  };
}

export function PilotAcceptancePanel({ workspace, runs = [], agentDetail = null, runsLoading = false, runsError = false }) {
  const { summary, domains } = derivePilotAcceptance({ workspace, runs, agentDetail, runsLoading, runsError });

  return (
    <section aria-label="קבילות לפיילוט" data-testid="pilot-acceptance" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700">קבילות לפיילוט — מוכנות מקצה-לקצה</h3>
        <span className="text-[11px] text-gray-400 mr-auto">קריאה בלבד · מבוסס ראיות קיימות · אינו מעניק הרשאות</span>
      </div>
      <div className="flex flex-wrap gap-2 text-[11px]" data-testid="pilot-summary">
        <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">הוכחו: {summary.proven} מתוך {summary.total}</span>
        <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">דורשים תשומת לב: {summary.attention}</span>
        <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">טרם ניתן לאמת: {summary.unknown}</span>
      </div>
      <p className="text-[11px] text-gray-400">
        מצב ירוק מוצג רק כשיש ראיה מפורשת; היעדר ראיה מוצג כ״טרם ניתן לאמת״ ואינו נחשב להצלחה.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {domains.map((d) => {
          const meta = PILOT_STATES[d.state];
          return (
            <div
              key={d.key}
              data-testid={`pilot-domain-${d.key}`}
              data-state={d.state}
              className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1.5"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium text-gray-700">{d.label}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${meta.cls}`}>{meta.icon} {meta.label}</span>
              </div>
              <p className="text-[11px] text-gray-600">{d.detail}</p>
              {d.links.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {d.links.map((l) => (
                    <Link key={`${l.to}-${l.label}`} to={l.to} aria-label={l.aria} className="text-[11px] text-blue-600 hover:underline">
                      {l.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// DIGEMP-PILOT-E2E-CLOSEOUT-095: explicit Browser Work request pilot path, wiring the isolated
// DIGEMP-BROWSER-WORK-FOUNDATION-ASAF-098 contract into the assigned-ticket list. This panel
// itself has no execution/approval control — it only submits the request and truthfully renders
// whatever the server returns (see BrowserWorkRequestCard), including a refusal.
function BrowserWorkRequestPanel({ agentId, ticketId }) {
  const [open, setOpen] = useState(false);
  const [targetSystem, setTargetSystem] = useState("");
  const [objective, setObjective] = useState("");

  const { data: connectorTypes } = useQuery({
    queryKey: ["connector-types"],
    queryFn: agentConnectionsApi.connectorTypes,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: () =>
      agentsApi.requestBrowserWork(agentId, {
        ticket_id: ticketId,
        target_system: targetSystem,
        objective,
      }),
  });

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="text-[11px] text-blue-600 hover:underline whitespace-nowrap"
      >
        🌐 בקש עבודת דפדפן
      </button>
    );
  }

  return (
    <div dir="rtl" className="mt-1.5 space-y-1.5 rounded border border-gray-200 bg-gray-50 p-2">
      {mutation.data ? (
        <>
          <BrowserWorkRequestCard request={mutation.data} />
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              mutation.reset();
            }}
            className="text-[11px] text-gray-500 hover:underline"
          >
            סגור
          </button>
        </>
      ) : (
        <>
          <label className="block text-[11px] text-gray-600">
            מערכת יעד
            <select
              value={targetSystem}
              onChange={(e) => setTargetSystem(e.target.value)}
              className="mt-0.5 block w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
            >
              <option value="">בחר מערכת…</option>
              {(connectorTypes || []).map((c) => (
                <option key={c.connector_id} value={c.system_type || c.connector_id}>
                  {c.display_name}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-[11px] text-gray-600">
            מטרת הבקשה
            <textarea
              value={objective}
              onChange={(e) => setObjective(e.target.value)}
              rows={2}
              className="mt-0.5 block w-full rounded border border-gray-300 px-1.5 py-1 text-xs"
            />
          </label>
          {mutation.isError && (
            <p className="text-[11px] text-red-600">
              {mutation.error?.response?.data?.detail || "שגיאה בשליחת הבקשה"}
            </p>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={!targetSystem || !objective.trim() || mutation.isPending}
              onClick={() => mutation.mutate()}
              className="text-[11px] px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {mutation.isPending ? "שולח…" : "שלח בקשה"}
            </button>
            <button type="button" onClick={() => setOpen(false)} className="text-[11px] text-gray-500 hover:underline">
              ביטול
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Approved Skills & Versions (DIGEMP-SKILL-VERSION-HISTORY-FRONTEND-025) ───────────────────
//
// Operator-readable Skill assignment/version history fed by the additive `skill_history`
// projection when the server supplies it. Four truthfulness rules are enforced by construction:
//  - "approved" is never inferred: a Skill renders as approved only when the server reports an
//    approved-family status (and then only with a source that is not a model self-claim); an
//    unrecognized status is shown as reported, a candidate/suspended/deprecated Skill as the
//    lifecycle state it is — nothing is promoted to "approved" here
//  - an exact version is rendered only when the server reports one; an absent history is an
//    explicit unknown, never an empty-looking "no history"
//  - declared specialty (free-text tags from the employee definition) is not repeated on this
//    surface: the professional profile card ("פרופיל מקצועי") is its single home, where it is
//    shown apart from measured proficiency and labeled declaration-only
//  - the surface is documentation only: it holds no assign/approve/remove controls, and it
//    states that a Skill grants no connection, write, execution or approval authority

const SKILL_STATUS_META = {
  approved:   { label: "מאושר", cls: "bg-green-100 text-green-700 border-green-200" },
  candidate:  { label: "מועמד — טרם אושר", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  suspended:  { label: "מושהה", cls: "bg-orange-100 text-orange-700 border-orange-200" },
  deprecated: { label: "הוצא משימוש", cls: "bg-slate-200 text-slate-700 border-slate-300" },
};

const SKILL_STATUS_SYNONYMS = {
  approved: ["approved", "active", "assigned", "granted", "current", "in_effect"],
  candidate: ["candidate", "pending", "proposed", "requested", "awaiting_approval", "in_review", "draft"],
  suspended: ["suspended", "suspension", "paused", "on_hold"],
  deprecated: ["deprecated", "retired", "revoked", "removed", "expired"],
};

// A source that is a model/self claim can never render as an approved Skill, whatever status
// the payload carries alongside it.
const SELF_CLAIM_SOURCES = ["model_claim", "self_claim", "self_reported", "self_report", "self_assessment", "agent_claim", "model", "llm", "agent"];

const SOURCE_OF_SKILL_HE = { training: "אימון", evaluation: "הערכה", manual_grant: "שיוך ידני" };

function pickSkillText(item, keys, max = 200) {
  if (!item || typeof item !== "object") return null;
  for (const key of keys) {
    const value = boundedText(item[key], max);
    if (value) return value;
  }
  return null;
}

function pickSkillInteger(item, keys) {
  if (!item || typeof item !== "object") return null;
  for (const key of keys) {
    if (Number.isInteger(item[key])) return item[key];
  }
  return null;
}

function pickSkillArray(raw, keys) {
  if (!raw || typeof raw !== "object") return null;
  for (const key of keys) {
    if (Array.isArray(raw[key])) return raw[key];
  }
  return null;
}

function normalizeSkillToken(value) {
  const text = boundedText(value, 80);
  return text ? text.toLowerCase().replace(/\s+/g, "_") : null;
}

function mapSkillToken(raw, synonyms) {
  const token = normalizeSkillToken(raw);
  if (!token) return null;
  for (const [canonical, list] of Object.entries(synonyms)) {
    if (list.includes(token)) return canonical;
  }
  return "unknown";
}

function normalizeVersionEntry(item) {
  if (typeof item === "string") {
    const text = boundedText(item, 200);
    return text ? { version: null, fromVersion: null, at: null, statusKey: null, statusRaw: null, reason: text, evidence: null, approver: null } : null;
  }
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const rawStatus = pickSkillText(item, ["status", "state"], 60);
  const entry = {
    version: pickSkillInteger(item, ["version", "to_version", "new_version"]),
    fromVersion: pickSkillInteger(item, ["from_version", "previous_version", "old_version"]),
    at: pickSkillText(item, ["at", "timestamp", "occurred_at", "approved_at", "assigned_at", "created_at", "date", "time"], 40),
    statusKey: rawStatus ? mapSkillToken(rawStatus, SKILL_STATUS_SYNONYMS) : null,
    statusRaw: rawStatus,
    reason: pickSkillText(item, ["reason", "summary", "detail", "note", "description", "change", "title"], 200),
    evidence: pickSkillText(item, ["evidence", "evidence_summary", "evidence_detail"], 200),
    approver: pickSkillText(item, ["approved_by", "approver", "human_approver", "by", "actor", "decided_by"], 80),
  };
  return entry.version != null || entry.fromVersion != null || entry.at || entry.reason || entry.evidence || entry.statusRaw ? entry : null;
}

function normalizeSkill(item) {
  if (typeof item === "string") {
    const name = boundedText(item, 120);
    return name ? { name, domain: null, subdomain: null, version: null, statusKey: null, statusRaw: null, level: null, evidenceCount: null, sourceRaw: null, selfClaim: false, assignedAt: null, approver: null, history: null } : null;
  }
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const name = pickSkillText(item, ["name", "skill", "title", "label"], 120);
  if (!name) return null;
  const rawStatus = pickSkillText(item, ["status", "state", "assignment_status"], 60);
  const rawSource = pickSkillText(item, ["source_of_skill", "source", "origin"], 80);
  const sourceToken = normalizeSkillToken(rawSource);
  const historyRaw = pickSkillArray(item, ["history", "version_history", "versions", "changes", "events"]);
  return {
    name,
    domain: pickSkillText(item, ["domain", "area", "category"], 120),
    subdomain: pickSkillText(item, ["subdomain", "sub_area", "topic"], 120),
    version: pickSkillInteger(item, ["version", "current_version", "skill_version"]),
    statusKey: rawStatus ? mapSkillToken(rawStatus, SKILL_STATUS_SYNONYMS) : null,
    statusRaw: rawStatus,
    level: pickSkillInteger(item, ["current_level", "level"]),
    evidenceCount: pickSkillInteger(item, ["evidence_count"]),
    sourceRaw: rawSource,
    selfClaim: sourceToken ? SELF_CLAIM_SOURCES.includes(sourceToken) : false,
    assignedAt: pickSkillText(item, ["assigned_at", "approved_at", "assignment_timestamp", "granted_at", "created_at"], 40),
    approver: pickSkillText(item, ["approved_by", "approver", "human_approver", "assigned_by", "decided_by"], 80),
    history: historyRaw === null ? null : historyRaw.map(normalizeVersionEntry).filter(Boolean),
  };
}

/**
 * Normalizes the additive `skill_history` projection into a display-ready structure. Returns null
 * when no payload is present at all; a per-field null then means "not supplied by the server"
 * (rendered as an explicit unknown), while an empty array means the server explicitly reported
 * "none". A bare skills array is accepted as a payload too. No value is ever computed here, and
 * an entry without a recognizable name is dropped rather than rendered nameless.
 */
export function normalizeSkillHistory(raw) {
  const payload = Array.isArray(raw) ? { skills: raw } : raw;
  if (!payload || typeof payload !== "object") return null;
  const skillsRaw = pickSkillArray(payload, ["skills", "approved_skills", "assignments", "skill_assignments", "items"]);
  return {
    skills: skillsRaw === null ? null : skillsRaw.map(normalizeSkill).filter(Boolean),
    asOf: pickSkillText(payload, ["generated_at", "as_of", "updated_at"], 40),
  };
}

// The additive contract may arrive under any of these keys on any of the carriers the workspace
// already reads (workspace top-level, the workspace agent projection, the page's agent detail).
const SKILL_HISTORY_PAYLOAD_KEYS = ["skill_history", "skills_history", "skill_assignments", "approved_skills"];

function pickSkillHistoryPayload(source) {
  if (!source || typeof source !== "object" || Array.isArray(source)) return null;
  for (const key of SKILL_HISTORY_PAYLOAD_KEYS) {
    const value = source[key];
    if (value !== undefined && value !== null) return value;
  }
  return null;
}

function SkillVersionRow({ entry }) {
  const statusMeta = entry.statusKey ? SKILL_STATUS_META[entry.statusKey] : null;
  const reportedUnmapped = entry.statusKey === "unknown";
  return (
    <li className="space-y-0.5">
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span className="px-1.5 py-0.5 rounded border border-gray-200 bg-white text-gray-700 whitespace-nowrap">
          {entry.version != null ? `גרסה ${entry.version}` : "גרסה לא דווחה"}
        </span>
        {entry.fromVersion != null && entry.version != null && (
          <span className="text-gray-500 whitespace-nowrap">{`מגרסה ${entry.fromVersion}`}</span>
        )}
        {reportedUnmapped ? (
          <span
            title="סטטוס כפי שדווח על-ידי השרת — אינו ממופה לסטטוס סקיל מוכר"
            className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-dashed border-gray-300 whitespace-nowrap"
          >
            {entry.statusRaw}
          </span>
        ) : statusMeta ? (
          <span className={`px-1.5 py-0.5 rounded-full border whitespace-nowrap ${statusMeta.cls}`}>{statusMeta.label}</span>
        ) : null}
        <span className="text-gray-400 whitespace-nowrap mr-auto">{entry.at ? fmt(entry.at) : "זמן לא דווח"}</span>
      </div>
      {entry.reason && <p className="text-xs text-gray-700">{entry.reason}</p>}
      {entry.evidence && <p className="text-xs text-gray-500">ראיה: {entry.evidence}</p>}
      {entry.approver && (
        <p className="text-[11px] text-gray-500">אושר על ידי <span dir="ltr">{entry.approver}</span></p>
      )}
    </li>
  );
}

function SkillCard({ skill }) {
  const selfClaim = skill.selfClaim;
  const approved = !selfClaim && skill.statusKey === "approved";
  const statusMeta = skill.statusKey && skill.statusKey !== "unknown" ? SKILL_STATUS_META[skill.statusKey] : null;
  const reportedUnmapped = !selfClaim && skill.statusKey === "unknown";
  const sourceLabel = skill.sourceRaw
    ? (SOURCE_OF_SKILL_HE[normalizeSkillToken(skill.sourceRaw)] || skill.sourceRaw)
    : null;
  return (
    <li className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-800">{skill.name}</span>
        {(skill.domain || skill.subdomain) && (
          <span className="text-[11px] text-gray-500">{[skill.domain, skill.subdomain].filter(Boolean).join(" · ")}</span>
        )}
        {selfClaim ? (
          <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 whitespace-nowrap">
            הצהרה עצמית — לא מאושר
          </span>
        ) : reportedUnmapped ? (
          <span
            title="סטטוס כפי שדווח על-ידי השרת — אינו ממופה לסטטוס סקיל מוכר"
            className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-dashed border-gray-300 whitespace-nowrap"
          >
            {skill.statusRaw}
          </span>
        ) : statusMeta ? (
          <span className={`text-[11px] px-1.5 py-0.5 rounded-full border whitespace-nowrap ${statusMeta.cls}`}>{statusMeta.label}</span>
        ) : (
          <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500 border border-dashed border-gray-300 whitespace-nowrap">
            סטטוס לא דווח
          </span>
        )}
        <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-white text-gray-700 border border-gray-200 whitespace-nowrap">
          {skill.version != null ? `גרסה ${skill.version}` : "גרסה לא דווחה"}
        </span>
        {skill.assignedAt && (
          <span className="text-[11px] text-gray-400 whitespace-nowrap mr-auto">שויך: {fmt(skill.assignedAt)}</span>
        )}
      </div>

      {(skill.approver || approved) && (
        <div className="text-[11px]">
          {skill.approver ? (
            <span className="text-teal-700">אושר על ידי <span dir="ltr">{skill.approver}</span></span>
          ) : (
            <span className="text-gray-400">גורם מאשר לא דווח</span>
          )}
        </div>
      )}

      {(skill.level != null || skill.evidenceCount != null || sourceLabel) && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-500">
          {skill.level != null && <span>רמה: {skill.level}</span>}
          {skill.evidenceCount != null && <span>{skill.evidenceCount} ראיות</span>}
          {sourceLabel && <span>מקור הסקיל: {sourceLabel}</span>}
        </div>
      )}

      {selfClaim && (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          הצהרת המודל אינה מוצגת כסקיל מאושר — נדרשים שיוך או הערכה מאושרים ומתועדים.
        </p>
      )}
      {approved && skill.level === 0 && (
        <p className="text-[11px] text-gray-500 bg-gray-100 border border-gray-200 rounded px-2 py-1">
          ברמה 0 — טרם נצברה ראיה מדידה; הסקיל מאושר אך אינו בשימוש מקצועי.
        </p>
      )}
      {!selfClaim && skill.statusKey === "candidate" && (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          מועמד — טרם אושר; אינו נחשב לסקיל מאושר ואינו בשימוש.
        </p>
      )}
      {!selfClaim && skill.statusKey === "suspended" && (
        <p className="text-[11px] text-orange-800 bg-orange-50 border border-orange-200 rounded px-2 py-1">
          מושהה — לא נעשה שימוש בסקיל עד להסרת ההשהיה; ההיסטוריה נשמרת במלואה.
        </p>
      )}
      {!selfClaim && skill.statusKey === "deprecated" && (
        <p className="text-[11px] text-slate-700 bg-slate-100 border border-slate-200 rounded px-2 py-1">
          הוצא משימוש — מצב סופי; הרשומה וההיסטוריה נשמרות.
        </p>
      )}

      {skill.history === null ? (
        <p className="text-[11px] text-gray-400 italic border-t border-gray-100 pt-1.5">
          היסטוריית גרסאות טרם דווחה על-ידי המערכת (לא ידוע)
        </p>
      ) : skill.history.length === 0 ? (
        <p className="text-[11px] text-gray-400 italic border-t border-gray-100 pt-1.5">לא דווחו גרסאות קודמות</p>
      ) : (
        <div className="border-t border-gray-100 pt-1.5 space-y-1">
          <p className="text-[11px] font-medium text-gray-600">היסטוריית גרסאות</p>
          <ol className="space-y-1.5">
            {skill.history.map((entry, i) => <SkillVersionRow key={`${entry.version ?? "v"}-${entry.at || "t"}-${i}`} entry={entry} />)}
          </ol>
        </div>
      )}
    </li>
  );
}

export function ApprovedSkillsSection({ skillHistory }) {
  const history = normalizeSkillHistory(skillHistory);
  return (
    <section aria-label="סקילים מאושרים וגרסאות" className="bg-white rounded-xl border shadow-sm p-4 space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-gray-700">סקילים מאושרים וגרסאות</h3>
        {history && history.asOf && <span className="text-[11px] text-gray-400">נכון ל־{fmt(history.asOf)}</span>}
      </div>
      <p className="text-[11px] text-gray-400">
        סקיל מאושר נרשם רק בשיוך או בהערכה מאושרים ומתועדים; תגית מוצהרת או הצהרת מודל אינן סקיל מאושר.
      </p>
      <p className="text-[11px] text-gray-400">
        תיעוד בלבד — נוכחות סקיל אינה מעניקה הרשאות חיבור, כתיבה, הרצה או אישור ואינה משנה את רמת ההרשאה של
        העובד; שיוך, אישור או הסרה של סקילים מתבצעים מחוץ למסך זה.
      </p>

      {!history ? (
        <p className="text-sm text-gray-400 italic">היסטוריית הסקילים טרם סופקה על-ידי המערכת (לא ידוע)</p>
      ) : (
        <div data-testid="approved-skills">
          {history.skills === null ? (
            <p className="text-sm text-gray-400 italic">רשימת הסקילים טרם סופקה על-ידי המערכת (לא ידוע)</p>
          ) : history.skills.length === 0 ? (
            <p className="text-sm text-gray-400 italic">לא דווחו סקילים מאושרים לעובד זה</p>
          ) : (
            <ul className="space-y-2">
              {history.skills.map((skill, i) => <SkillCard key={`${skill.name}-${i}`} skill={skill} />)}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
// ─── Learning & Proficiency history (DIGEMP-PROFICIENCY-LEARNING-FRONTEND-026) ─────────────────

// ─── Skill assignment state (DIGEMP-SKILL-ASSIGNMENT-STATE-FRONTEND-034) ──────
//
// Read-only lifecycle/availability view over the employee's assigned Skills: which Skill is
// assigned, at which exact version, in which lifecycle state, the evaluated proficiency (the
// record's level — per the Skill contract a level can only move through a human-reviewed
// evaluation, never through training or a model claim) and the last evaluation evidence.
//
// A Skill is not a permission: nothing on this surface grants a tool, a connection, execution
// or approval, and it is deliberately NOT folded into the capabilities cards — the standalone
// note below states the boundary. Lifecycle states render verbatim; only "active" reads as
// available, and suspended / deprecated / unrecognized / unreported states never do.

const SKILL_STATE_PAYLOAD_KEYS = [
  "skill_assignment_state", "skill_assignment_status", "assigned_skill_state", "assigned_skills",
  "skill_history", "skills_history", "skill_assignments", "approved_skills",
];

const SKILL_STATE_SYNONYMS = {
  active: ["active", "approved", "assigned", "granted", "current", "in_effect"],
  candidate: ["candidate", "pending", "proposed", "requested", "awaiting_approval", "in_review", "draft"],
  suspended: ["suspended", "suspension", "paused", "on_hold"],
  deprecated: ["deprecated", "retired", "revoked", "removed", "expired"],
};

const SKILL_STATE_META = {
  active: { label: "פעיל", cls: "bg-green-100 text-green-700 border-green-200" },
  candidate: { label: "מועמד", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  suspended: { label: "מושהה", cls: "bg-orange-100 text-orange-700 border-orange-200" },
  deprecated: { label: "הוצא משימוש", cls: "bg-slate-200 text-slate-600 border-slate-300" },
};

// Availability answers exactly one question — "does the lifecycle state allow work" — and only
// "active" is a yes. Unrecognized or unreported states are an explicit no, never a default yes.
const SKILL_AVAILABILITY = {
  active: { text: "זמין לעבודה", cls: "bg-green-50 text-green-700 border-green-200" },
  candidate: { text: "אינו זמין (מועמד)", cls: "bg-gray-100 text-gray-600 border-gray-200" },
  suspended: { text: "אינו זמין (מושהה)", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  deprecated: { text: "אינו זמין (הוצא משימוש)", cls: "bg-slate-100 text-slate-600 border-slate-300" },
  unknown: { text: "אינו זמין (מצב לא ידוע)", cls: "bg-gray-100 text-gray-500 border-dashed border-gray-300" },
};

const SKILL_SOURCE_HE = { training: "אימון", evaluation: "הערכה", manual_grant: "שיוך ידני" };

function pickStateText(item, keys, max) {
  if (!item || typeof item !== "object") return null;
  for (const key of keys) {
    const value = boundedText(item[key], max);
    if (value) return value;
  }
  return null;
}

function pickStateInteger(item, keys) {
  if (!item || typeof item !== "object") return null;
  for (const key of keys) {
    if (Number.isInteger(item[key])) return item[key];
  }
  return null;
}

function pickStateArray(raw, keys) {
  if (!raw || typeof raw !== "object") return null;
  for (const key of keys) {
    if (Array.isArray(raw[key])) return raw[key];
  }
  return null;
}

function mapSkillStateToken(value) {
  const token = typeof value === "string" ? value.trim().toLowerCase().replace(/\s+/g, "_") : null;
  if (!token) return null;
  for (const [canonical, list] of Object.entries(SKILL_STATE_SYNONYMS)) {
    if (list.includes(token)) return canonical;
  }
  return "unknown";
}

function normalizeSkillStateItem(item) {
  if (typeof item === "string") {
    const name = boundedText(item, 120);
    return name ? { key: null, name, domain: null, subdomain: null, version: null, statusKey: null, statusRaw: null, level: null, evidenceCount: null, lastEvaluatedAt: null, evalEvidence: null, evalApprover: null, deprecatedAt: null, source: null, assignedAt: null } : null;
  }
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const name = pickStateText(item, ["name", "skill", "title", "label"], 120);
  if (!name) return null;
  const rawStatus = pickStateText(item, ["status", "state", "assignment_status", "lifecycle_status"], 60);
  const levelRaw = pickStateInteger(item, ["current_level", "level", "proficiency", "evaluated_proficiency", "evaluated_level"]);
  const lastEvaluation = item.last_evaluation && typeof item.last_evaluation === "object" && !Array.isArray(item.last_evaluation)
    ? item.last_evaluation
    : null;
  return {
    key: pickStateText(item, ["skill_id", "id"], 80),
    name,
    domain: pickStateText(item, ["domain", "area", "category"], 120),
    subdomain: pickStateText(item, ["subdomain", "sub_area", "topic"], 120),
    version: pickStateInteger(item, ["version", "current_version", "skill_version"]),
    statusKey: rawStatus ? mapSkillStateToken(rawStatus) : null,
    statusRaw: rawStatus,
    // The registry's level is a 0..100 integer; any other shape is left unreported rather
    // than rescaled, so a non-contract number can never be shown as a measurement.
    level: levelRaw != null && levelRaw >= 0 && levelRaw <= 100 ? levelRaw : null,
    evidenceCount: pickStateInteger(item, ["evidence_count", "evidence_total"]),
    lastEvaluatedAt:
      pickStateText(item, ["last_evaluated_at", "last_evaluation_at", "evaluated_at"], 40)
      || (lastEvaluation ? pickStateText(lastEvaluation, ["at", "evaluated_at", "timestamp"], 40) : null),
    evalEvidence: lastEvaluation ? pickStateText(lastEvaluation, ["evidence", "evidence_summary", "summary"], 200) : null,
    evalApprover: lastEvaluation ? pickStateText(lastEvaluation, ["approved_by", "approver", "reviewer"], 80) : null,
    deprecatedAt: pickStateText(item, ["deprecated_at", "retired_at"], 40),
    source: pickStateText(item, ["source_of_skill", "source", "origin"], 80),
    assignedAt: pickStateText(item, ["assigned_at", "approved_at", "granted_at"], 40),
  };
}

/**
 * Normalizes the additive assignment-state projection. Returns null when no payload is present
 * at all; a per-field null then means "not supplied" (rendered as an explicit unknown), while an
 * empty array means the server explicitly reported "none". Only whitelisted bounded scalars are
 * read, so any extra material next to a record can not reach the screen. A bare skills array is
 * accepted as a payload too.
 */
export function normalizeSkillAssignmentState(raw) {
  const payload = Array.isArray(raw) ? { skills: raw } : raw;
  if (!payload || typeof payload !== "object") return null;
  const skillsRaw = pickStateArray(payload, ["skills", "assigned_skills", "approved_skills", "assignments", "items"]);
  return {
    skills: skillsRaw === null ? null : skillsRaw.map(normalizeSkillStateItem).filter(Boolean),
    asOf: pickStateText(payload, ["generated_at", "as_of", "updated_at"], 40),
  };
}

// The projection may arrive under the assignment-state key the backend projection will use, or
// under the existing Skill Registry read-model family — same carriers the workspace already
// reads (workspace payload, the workspace agent projection, the page's agent detail).
function pickSkillStatePayload(source) {
  if (!source || typeof source !== "object" || Array.isArray(source)) return null;
  for (const key of SKILL_STATE_PAYLOAD_KEYS) {
    const value = source[key];
    if (value !== undefined && value !== null) return value;
  }
  return null;
}

function SkillStateRow({ skill }) {
  const statusMeta = skill.statusKey && skill.statusKey !== "unknown" ? SKILL_STATE_META[skill.statusKey] : null;
  const availability = SKILL_AVAILABILITY[statusMeta ? skill.statusKey : "unknown"];
  const measured = skill.level != null && (skill.level > 0 || skill.lastEvaluatedAt);
  const sourceLabel = skill.source ? (SKILL_SOURCE_HE[skill.source.toLowerCase()] || skill.source) : null;
  return (
    <li className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-800">{skill.name}</span>
        {(skill.domain || skill.subdomain) && (
          <span className="text-[11px] text-gray-500">{[skill.domain, skill.subdomain].filter(Boolean).join(" · ")}</span>
        )}
        <span className="text-[11px] px-1.5 py-0.5 rounded border border-gray-200 bg-white text-gray-700 whitespace-nowrap">
          {skill.version != null ? `גרסה ${skill.version}` : "גרסה לא דווחה"}
        </span>
        <span
          className={`${CHIP_CLS} ${
            statusMeta ? statusMeta.cls : "bg-white text-gray-500 border-dashed border-gray-300"
          }`}
        >
          {statusMeta ? statusMeta.label : skill.statusRaw ? `לא מוכר: ${skill.statusRaw}` : "מצב לא דווח"}
        </span>
        <span className={`text-[11px] px-1.5 py-0.5 rounded-full border whitespace-nowrap ${availability.cls}`}>
          {availability.text}
        </span>
        {skill.assignedAt && (
          <span className="text-[11px] text-gray-400 whitespace-nowrap mr-auto">שויך: {fmt(skill.assignedAt)}</span>
        )}
      </div>

      <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-600">
        <span>
          {measured
            ? `מיומנות נמדדת: רמה ${skill.level}`
            : skill.level === 0
              ? "מיומנות נמדדת: טרם נמדדה"
              : "מיומנות נמדדת: לא דווחה"}
        </span>
        <span className="text-gray-500">{skill.lastEvaluatedAt ? `הוערך לאחרונה: ${fmt(skill.lastEvaluatedAt)}` : "הוערך לאחרונה: אין תיעוד הערכה"}</span>
        <span className="text-gray-500">{skill.evidenceCount != null ? `${skill.evidenceCount} ראיות מתועדות` : "ראיות הערכה טרם דווחו"}</span>
        {sourceLabel && <span className="text-gray-500">מקור: {sourceLabel}</span>}
      </div>

      {skill.evalEvidence && (
        <p className="text-[11px] text-gray-500">
          ראיית ההערכה האחרונה: {skill.evalEvidence}
          {skill.evalApprover ? ` · אושרה על ידי ` : ""}
          {skill.evalApprover && <span dir="ltr">{skill.evalApprover}</span>}
        </p>
      )}
      {skill.statusKey === "deprecated" && skill.deprecatedAt && (
        <p className="text-[11px] text-slate-500">הוצא משימוש ב־{fmt(skill.deprecatedAt)}</p>
      )}
    </li>
  );
}

export function SkillAssignmentStateSection({ skillState }) {
  const state = normalizeSkillAssignmentState(skillState);
  const skills = state && Array.isArray(state.skills) ? state.skills : [];
  const availableCount = skills.filter((s) => s.statusKey === "active").length;
  return (
    <section data-testid="skill-assignment-state" aria-label="מצב שיוך סקילים" className="bg-white rounded-xl border shadow-sm p-4 space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-gray-700">מצב שיוך סקילים</h3>
        {state && state.asOf && <span className="text-[11px] text-gray-400">נכון ל־{fmt(state.asOf)}</span>}
      </div>
      <p className="text-[11px] text-gray-400">
        המצב, הגרסה, הרמה הנמדדת והראיות מוצגים כפי שדווחו ברשומת הסקיל — דבר אינו מוסק או מומצא כאן.
      </p>
      <p className="text-[11px] text-gray-400">
        סקיל אינו הרשאה: השיוך אינו מעניק כלים, חיבורים, הרצת פעולות או אישור ואינו משנה את רמת ההרשאה של
        העובד; הרשאות נקבעות במסלולי ההרשאות הקיימים בלבד.
      </p>

      {!state ? (
        <p className="text-sm text-gray-400 italic">נתוני שיוך הסקילים טרם סופקו על-ידי המערכת (לא ידוע)</p>
      ) : state.skills === null ? (
        <p className="text-sm text-gray-400 italic">רשימת הסקילים המשויכים טרם סופקה על-ידי המערכת (לא ידוע)</p>
      ) : state.skills.length === 0 ? (
        <p className="text-sm text-gray-400 italic">לא דווחו סקילים משויכים לעובד זה</p>
      ) : (
        <>
          <p className="text-[11px] text-gray-400">{state.skills.length} סקילים משויכים · {availableCount} זמינים לעבודה</p>
          <ul className="space-y-2">
            {state.skills.map((skill, i) => <SkillStateRow key={skill.key || `${skill.name}-${i}`} skill={skill} />)}
          </ul>
        </>
      )}
    </section>
  );
}

//
// Operator-readable learning history fed by the additive `learning_history` projection when the
// server supplies it. Three rules are enforced by construction here:
//  - every proficiency state and change must name its evidence source and time; the UI itself
//    never infers, computes or interpolates a score, and an absent field stays an explicit unknown
//  - evidence whose source is a model/self claim is never presented as proficiency — such rows
//    render as an explicit "not verified" marker instead of a proficiency state
//  - learning goals are not repeated on this surface: the professional profile card ("פרופיל
//    מקצועי" · "יעדי למידה") is their single home
//  - the surface is documentation only: nothing here grants or expands execution, connection or
//    approval authority, and it holds no controls

const STATE_META = {
  proficient: { label: "מיומן", cls: "bg-green-100 text-green-700" },
  learning: { label: "בלמידה", cls: "bg-blue-100 text-blue-700" },
  regression: { label: "נסיגה", cls: "bg-red-100 text-red-700" },
  suspended: { label: "מושהה", cls: "bg-orange-100 text-orange-700" },
  not_started: { label: "טרם נלמד", cls: "bg-gray-100 text-gray-600" },
  unknown: { label: "מצב לא ידוע", cls: "bg-gray-100 text-gray-500" },
};

const STATE_SYNONYMS = {
  proficient: ["proficient", "evaluated", "verified", "qualified", "competent", "mastered", "מיומן"],
  learning: ["learning", "training", "in_training", "in_progress", "studying", "בלמידה"],
  regression: ["regression", "regressed", "declined", "downgraded", "נסיגה"],
  suspended: ["suspended", "suspension", "paused", "on_hold", "מושהה"],
  not_started: ["not_started", "no_evidence", "not_measured", "טרם_נלמד"],
};

// Audited sources are the only ones allowed to carry a proficiency meaning. Everything else is
// either an explicit unverified self-claim or an unknown source, both rendered as such.
const SOURCE_META = {
  verified_work: { label: "עבודה מאומתת", cls: "bg-green-50 text-green-700 border-green-200", trust: "verified" },
  evaluation: { label: "הערכה מדידה", cls: "bg-blue-50 text-blue-700 border-blue-200", trust: "verified" },
  approved_skill: { label: "סקיל מאושר", cls: "bg-violet-50 text-violet-700 border-violet-200", trust: "verified" },
  approved_knowledge: { label: "ידע מאושר", cls: "bg-teal-50 text-teal-700 border-teal-200", trust: "verified" },
  operator: { label: "החלטת טכנאי/מפעיל", cls: "bg-indigo-50 text-indigo-700 border-indigo-200", trust: "verified" },
  self_claim: { label: "הצהרה עצמית — לא מאומתת", cls: "bg-amber-50 text-amber-800 border-amber-300", trust: "self_claim" },
  unknown: { label: "מקור ראיה לא ידוע", cls: "bg-gray-100 text-gray-500 border-gray-200", trust: "unknown" },
};

const SOURCE_SYNONYMS = {
  verified_work: ["verified_work", "verified_outcome", "work_outcome", "technician_approved", "approved_work", "run_verified"],
  evaluation: ["evaluation", "eval", "assessment", "evaluation_report", "measured"],
  approved_skill: ["approved_skill", "skill_assignment", "skill_version", "skill"],
  approved_knowledge: ["approved_knowledge", "verified_knowledge", "knowledge", "kb_article"],
  operator: ["operator", "technician", "admin", "human", "manual", "operator_decision", "human_decision"],
  self_claim: ["model_claim", "self_claim", "self_reported", "self_report", "self_assessment", "agent_claim", "model", "llm", "agent"],
};

const CHANGE_KIND_META = {
  promotion: { label: "קידום מיומנות", cls: "bg-green-50 text-green-700 border-green-200" },
  regression: { label: "נסיגה", cls: "bg-red-50 text-red-700 border-red-200" },
  suspension: { label: "השהיה", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  reinstated: { label: "הסרת השהיה", cls: "bg-teal-50 text-teal-700 border-teal-200" },
  evaluation: { label: "הערכה", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  skill: { label: "סקיל מאושר", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  knowledge: { label: "ידע מאושר", cls: "bg-teal-50 text-teal-700 border-teal-200" },
  goal: { label: "עדכון יעד", cls: "bg-gray-50 text-gray-700 border-gray-200" },
  other: { label: "שינוי", cls: "bg-gray-50 text-gray-700 border-gray-200" },
};

const CHANGE_KIND_SYNONYMS = {
  promotion: ["promotion", "promoted", "upgrade", "upgraded", "improvement", "improved", "advanced"],
  regression: ["regression", "regressed", "downgrade", "downgraded", "decline", "declined"],
  suspension: ["suspension", "suspended", "pause", "paused", "hold", "on_hold"],
  reinstated: ["reinstated", "reinstatement", "lifted", "resumed", "unsuspended", "reactivated", "restored"],
  evaluation: ["evaluation", "eval", "assessment", "re_evaluation", "reevaluation"],
  skill: ["skill_assigned", "skill_approved", "approved_skill", "skill_change", "skill_version", "skill_granted"],
  knowledge: ["knowledge_verified", "knowledge_approved", "approved_knowledge", "knowledge_added"],
  goal: ["goal", "goal_set", "goal_updated", "learning_goal", "learning_goal_set", "goal_change"],
};

const normalizeToken = (value) => {
  const text = boundedText(value, 80);
  return text ? text.toLowerCase().replace(/\s+/g, "_") : null;
};

function mapToken(raw, synonyms) {
  const token = normalizeToken(raw);
  if (!token) return null;
  for (const [canonical, list] of Object.entries(synonyms)) {
    if (list.includes(token)) return canonical;
  }
  return "unknown";
}

function pickText(item, keys, max = 200) {
  if (!item || typeof item !== "object") return null;
  for (const key of keys) {
    const value = boundedText(item[key], max);
    if (value) return value;
  }
  return null;
}

function pickNumberFrom(item, keys) {
  if (!item || typeof item !== "object") return null;
  for (const key of keys) {
    const value = boundedNumber(item[key]);
    if (value != null) return value;
  }
  return null;
}

function pickArray(raw, keys) {
  if (!raw || typeof raw !== "object") return null;
  for (const key of keys) {
    if (Array.isArray(raw[key])) return raw[key];
  }
  return null;
}

function stateLabelOf(raw) {
  const key = mapToken(raw, STATE_SYNONYMS);
  if (!key) return null;
  if (key === "unknown") return normalizeToken(raw) === "unknown" ? STATE_META.unknown.label : boundedText(raw, 60);
  return STATE_META[key] ? STATE_META[key].label : null;
}

function normalizeChange(item) {
  if (typeof item === "string") {
    const text = boundedText(item, 200);
    return text
      ? { kindKey: null, rawKind: null, at: null, fromLabel: null, toLabel: null, reason: text, evidence: null, sourceKey: "unknown", actor: null, runId: null, ticketId: null, ticketNumber: null, trust: "unknown" }
      : null;
  }
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const rawKind = pickText(item, ["kind", "type", "change", "change_type", "event", "action"], 60);
  const rawSource = pickText(item, ["evidence_source", "source", "basis", "based_on", "evidence_type"], 80);
  const sourceKey = mapToken(rawSource, SOURCE_SYNONYMS) || "unknown";
  const change = {
    kindKey: mapToken(rawKind, CHANGE_KIND_SYNONYMS),
    rawKind,
    at: pickText(item, ["at", "timestamp", "created_at", "date", "time", "occurred_at", "evaluated_at"], 40),
    fromLabel: stateLabelOf(pickText(item, ["from_state", "previous_state", "old_state", "from"], 60)),
    toLabel: stateLabelOf(pickText(item, ["to_state", "new_state", "state", "to"], 60)),
    reason: pickText(item, ["reason", "summary", "detail", "description", "explanation"], 200),
    evidence: pickText(item, ["evidence", "evidence_summary", "evidence_detail"], 200),
    sourceKey,
    actor: pickText(item, ["actor", "decided_by", "operator", "by", "technician"], 80),
    runId: pickText(item, ["run_id"], 64),
    ticketId: pickText(item, ["ticket_id"], 64),
    ticketNumber: pickNumberFrom(item, ["ticket_number"]),
    trust: SOURCE_META[sourceKey].trust,
  };
  return change.kindKey || change.rawKind || change.reason || change.at || change.evidence || change.toLabel ? change : null;
}

function normalizeSubdomain(item, fallbackDomain) {
  if (typeof item === "string") {
    const name = boundedText(item, 120);
    return name
      ? { name, domain: fallbackDomain || null, stateKey: null, stateRaw: null, sourceKey: "unknown", evidenceCount: null, since: null, lastAt: null, changes: [], trust: "unknown" }
      : null;
  }
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const name = pickText(item, ["subdomain", "skill", "topic", "area", "name", "title"], 120);
  if (!name) return null;
  const rawSource = pickText(item, ["evidence_source", "source", "basis", "based_on", "evidence_type"], 80);
  const sourceKey = mapToken(rawSource, SOURCE_SYNONYMS) || "unknown";
  return {
    name,
    domain: fallbackDomain || pickText(item, ["domain"], 120),
    stateKey: mapToken(pickText(item, ["state", "status", "proficiency_state", "level_state"], 60), STATE_SYNONYMS),
    stateRaw: pickText(item, ["state", "status", "proficiency_state", "level_state"], 60),
    sourceKey,
    evidenceCount: pickNumberFrom(item, ["evidence_count", "verified_runs", "total", "count"]),
    since: pickText(item, ["since", "state_since", "changed_at"], 40),
    lastAt: pickText(item, ["last_evaluated_at", "evaluated_at", "updated_at"], 40),
    changes: (pickArray(item, ["changes", "history", "events", "timeline"]) || []).map(normalizeChange).filter(Boolean),
    trust: SOURCE_META[sourceKey].trust,
  };
}

/**
 * Normalizes the additive `learning_history` projection into a display-ready structure.
 * Returns null when no payload is present at all; per-field null then means "not supplied by the
 * server" (rendered as an explicit unknown), while an empty array means the server explicitly
 * reported "none". No value is ever computed or invented here.
 */
export function normalizeLearningHistory(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const domainsRaw = pickArray(raw, ["domains", "proficiency", "proficiency_by_domain"]);
  const grouped = [];
  for (const entry of domainsRaw || []) {
    if (typeof entry === "string") {
      const name = boundedText(entry, 120);
      if (name) grouped.push({ name, subdomains: [] });
      continue;
    }
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) continue;
    const domainName = pickText(entry, ["domain", "area", "name", "title", "category"], 120);
    const nested = pickArray(entry, ["subdomains", "skills", "areas", "topics", "sub_areas"]);
    if (nested) {
      grouped.push({
        name: domainName || "כללי",
        subdomains: nested.map((sub) => normalizeSubdomain(sub, domainName)).filter(Boolean),
      });
      continue;
    }
    const sub = normalizeSubdomain(entry, domainName);
    if (!sub) continue;
    const groupName = sub.domain || "כללי";
    const existing = grouped.find((group) => group.name === groupName);
    if (existing) existing.subdomains.push(sub);
    else grouped.push({ name: groupName, subdomains: [sub] });
  }

  const changesRaw = pickArray(raw, ["evaluated_changes", "changes", "events"]);
  return {
    domains: domainsRaw === null ? null : grouped,
    changes: changesRaw === null ? null : changesRaw.map(normalizeChange).filter(Boolean),
    asOf: pickText(raw, ["generated_at", "as_of", "evaluated_at", "updated_at"], 40),
  };
}

function ChangeRow({ change }) {
  const selfClaim = change.trust === "self_claim";
  const recognizedKind = CHANGE_KIND_META[change.kindKey];
  const kindLabel = selfClaim
    ? `${(recognizedKind || CHANGE_KIND_META.other).label} — הצהרה עצמית (לא נספרת)`
    : recognizedKind
      ? recognizedKind.label
      : change.rawKind
        ? `שינוי: ${change.rawKind}`
        : CHANGE_KIND_META.other.label;
  const kindMeta = recognizedKind || CHANGE_KIND_META.other;
  const sourceMeta = SOURCE_META[change.sourceKey] || SOURCE_META.unknown;
  return (
    <li className="space-y-0.5">
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span className={`px-1.5 py-0.5 rounded border whitespace-nowrap ${selfClaim ? "bg-amber-50 text-amber-800 border-amber-300" : kindMeta.cls}`}>
          {kindLabel}
        </span>
        {(change.fromLabel || change.toLabel) && (
          <span className="text-gray-600">{change.fromLabel || "?"} → {change.toLabel || "?"}</span>
        )}
        <span className={`px-1.5 py-0.5 rounded-full border whitespace-nowrap ${sourceMeta.cls}`}>{sourceMeta.label}</span>
        <span className="text-gray-400 whitespace-nowrap mr-auto">{change.at ? fmt(change.at) : "זמן לא דווח"}</span>
      </div>
      {change.reason && <p className="text-xs text-gray-700">{change.reason}</p>}
      {change.evidence && <p className="text-xs text-gray-500">ראיה: {change.evidence}</p>}
      {(change.actor || change.runId || change.ticketId) && (
        <div className="flex flex-wrap items-center gap-3 text-[11px]">
          {change.actor && <span className="text-gray-500">מקור ההחלטה: <span dir="ltr">{change.actor}</span></span>}
          {change.runId && (
            <Link
              to={`/agent-runs/${change.runId}`}
              className="text-blue-600 hover:underline"
              aria-label="פתח את הריצה שממנה נרשמה הראיה"
            >
              צפה בריצה
            </Link>
          )}
          {change.ticketId && (
            <Link
              to={`/tickets/${change.ticketId}`}
              className="text-blue-600 hover:underline"
              aria-label="פתח את הקריאה שממנה נרשמה הראיה"
            >
              {change.ticketNumber ? `קריאה #${change.ticketNumber}` : "קריאה מקושרת"}
            </Link>
          )}
        </div>
      )}
    </li>
  );
}

function SubdomainRow({ subdomain }) {
  const stateKey = subdomain.stateKey || "unknown";
  const selfClaim = subdomain.trust === "self_claim";
  const reportedUnmapped = !selfClaim && stateKey === "unknown" && subdomain.stateRaw && normalizeToken(subdomain.stateRaw) !== "unknown";
  const stateMeta = STATE_META[stateKey] || STATE_META.unknown;
  const sourceMeta = SOURCE_META[subdomain.sourceKey] || SOURCE_META.unknown;
  return (
    <li className="rounded border border-gray-200 bg-white p-2.5 space-y-1.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-800">{subdomain.name}</span>
        {selfClaim ? (
          <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300 whitespace-nowrap">
            הצהרה עצמית — לא מאומתת
          </span>
        ) : reportedUnmapped ? (
          <span
            className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-dashed border-gray-300 whitespace-nowrap"
            title="מצב כפי שדווח על-ידי השרת — אינו ממופה למצב מיומנות מוכר"
          >
            {boundedText(subdomain.stateRaw, 60)}
          </span>
        ) : (
          <span className={`text-[11px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${stateMeta.cls}`}>
            {stateMeta.label}
          </span>
        )}
        <span className={`text-[11px] px-1.5 py-0.5 rounded-full border whitespace-nowrap ${sourceMeta.cls}`}>
          מקור: {sourceMeta.label}
        </span>
        {subdomain.evidenceCount != null && (
          <span className="text-[11px] text-gray-500 whitespace-nowrap">{subdomain.evidenceCount} ראיות</span>
        )}
        {subdomain.lastAt && (
          <span className="text-[11px] text-gray-400 whitespace-nowrap mr-auto">עודכן: {fmt(subdomain.lastAt)}</span>
        )}
      </div>

      {selfClaim && (
        <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
          הצהרת המודל אינה מוצגת כמיומנות — נדרשת ראיה מאומתת: עבודה מאומתת, הערכה, סקיל או ידע מאושרים, או החלטת טכנאי.
        </p>
      )}
      {!selfClaim && stateKey === "regression" && (
        <p className="text-[11px] text-red-800 bg-red-50 border border-red-200 rounded px-2 py-1">
          נסיגה — המיומנות ירדה לעומת המצב הקודם{subdomain.since ? ` (מאז ${fmt(subdomain.since)})` : ""}. ההיסטוריה נשמרת במלואה.
        </p>
      )}
      {!selfClaim && stateKey === "suspended" && (
        <p className="text-[11px] text-orange-800 bg-orange-50 border border-orange-200 rounded px-2 py-1">
          מושהה — לא נעשה שימוש במיומנות זו עד להסרת ההשהיה{subdomain.since ? ` (מאז ${fmt(subdomain.since)})` : ""}. ההיסטוריה נשמרת במלואה.
        </p>
      )}

      {subdomain.changes.length > 0 && (
        <ul className="space-y-1.5 border-t border-gray-100 pt-1.5">
          {subdomain.changes.map((change, i) => <ChangeRow key={`${change.at || "no-time"}-${i}`} change={change} />)}
        </ul>
      )}
    </li>
  );
}

export function LearningProficiencyHistory({ learningHistory }) {
  const history = normalizeLearningHistory(learningHistory);
  return (
    <section aria-label="למידה ומיומנות" className="bg-white rounded-xl border shadow-sm p-4 space-y-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h3 className="text-sm font-semibold text-gray-700">למידה ומיומנות</h3>
        {history && history.asOf && <span className="text-[11px] text-gray-400">נכון ל־{fmt(history.asOf)}</span>}
      </div>
      <p className="text-[11px] text-gray-400">
        מבוסס ראיות מתועדות בלבד — עבודה מאומתת, הערכות מדידות, סקילים וידע מאושרים והחלטות טכנאי. הצהרות עצמיות אינן נחשבות
        למיומנות, וכל רשומה מוצגת עם מקור הראיה והזמן שלה.
      </p>
      <p className="text-[11px] text-gray-400">
        תיעוד בלבד — היסטוריית הלמידה אינה מרחיבה הרשאות הרצה, חיבורים או אישור ואינה משנה את רמת ההרשאה של העובד.
      </p>

      {!history && (
        <p className="text-sm text-gray-400 italic">היסטוריית למידה טרם סופקה על-ידי המערכת (לא ידוע)</p>
      )}

      {history && (
        <>
          {history.domains === null ? (
            <p className="text-sm text-gray-400 italic">פירוט מיומנות לפי דומיין ותת-תחום טרם סופק על-ידי המערכת (לא ידוע)</p>
          ) : history.domains.length === 0 ? (
            <p className="text-sm text-gray-400 italic">לא דווחו דומייני מיומנות</p>
          ) : (
            <div className="space-y-3">
              {history.domains.map((domain, i) => (
                <div key={`${domain.name}-${i}`} className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-2">
                  <h4 className="text-xs font-semibold text-gray-600">{domain.name}</h4>
                  {domain.subdomains.length === 0 ? (
                    <p className="text-xs text-gray-400 italic">לא דווחו תת-תחומים לדומיין זה</p>
                  ) : (
                    <ul className="space-y-2">
                      {domain.subdomains.map((sub, j) => <SubdomainRow key={`${sub.name}-${j}`} subdomain={sub} />)}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}

          {history.changes !== null && (
            <div>
              <h4 className="text-xs font-semibold text-gray-600 mb-1">שינויים שנמדדו</h4>
              {history.changes.length === 0 ? (
                <p className="text-xs text-gray-400 italic">לא דווחו שינויים</p>
              ) : (
                <ul className="space-y-1.5">{history.changes.map((change, i) => <ChangeRow key={i} change={change} />)}</ul>
              )}
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ─── Specialist readiness matrix (DIGEMP-SPECIALIST-READINESS-FRONTEND-027) ───
//
// Read-only readiness matrix for the four specialist domains — Helpdesk, ESET PROTECT, Acronis
// and Microsoft 365 / Entra. The additive `specialist_readiness` contract (accepted on the
// workspace payload, its agent projection, or the page's agent detail, like the other additive
// contracts) supplies each domain's state, the evidence behind it and the human-readable
// read / recommend / execute-with-approval / escalate boundaries. When the server has not
// supplied a state, a domain's state is derived only from what this payload already proves:
// no configured domain connection → not available; a configured connection whose last test
// failed → not ready; otherwise unknown. A positive "ready" verdict is never derived locally —
// only the server contract can state it — and every state renders next to its evidence, so no
// badge appears without provenance. Only whitelisted bounded scalar fields are rendered, so
// credential material stored next to an evidence entry can not leak. The section adds no
// controls: execution stays on the existing proposal → approval → execution path.

const SPECIALIST_DOMAIN_ORDER = ["helpdesk", "eset_protect", "acronis", "microsoft_365_entra"];

const SPECIALIST_DOMAIN_LABELS = {
  helpdesk: "Helpdesk",
  eset_protect: "ESET PROTECT",
  acronis: "Acronis",
  microsoft_365_entra: "Microsoft 365 / Entra",
};

// The M365/Entra vendor domain is named by a different token on each specialist contract —
// specialist_readiness emits `microsoft_365_entra`, specialist_packs `m365` (pack_id `m365_entra`),
// the /specialists shell key is `m365_entra`, the connector system type is `m365_graph`. Every
// alias table in this file derives from this one list so the spellings can not drift apart again
// (section 3.3 of docs/release/digital-employee-specialist-remote-productization.md).
const M365_DOMAIN_TOKENS = [
  "microsoft_365", "microsoft_365_entra", "microsoft365", "m365", "m365_entra",
  "m365_graph", "entra", "entra_id", "entraid", "azure_ad",
];

const SPECIALIST_DOMAIN_ALIASES = {
  helpdesk: "helpdesk", help_desk: "helpdesk", service_desk: "helpdesk",
  eset: "eset_protect", eset_protect: "eset_protect",
  acronis: "acronis",
  ...Object.fromEntries(M365_DOMAIN_TOKENS.map((token) => [token, "microsoft_365_entra"])),
};

// Which connection `system_type` tokens belong to each specialist domain. The input is strictly
// `capabilities.connections[].system_type` — always an `AgentSystemType` arm — so these lists hold
// the producer's own tokens and nothing else. The Acronis domain used to list `acronis`, a domain
// spelling the connection row can never carry: an existing Acronis connection was filtered out of
// this matrix, which then reported "no connection configured for this domain" while the Acronis
// specialist card on the same page showed it. `acronis` is the domain key (SPECIALIST_DOMAIN_ALIASES)
// and stays one; it is not a connection token.
const DOMAIN_CONNECTION_TYPES = {
  eset_protect: ["eset_protect"],
  acronis: ["acronis_backup"],
  microsoft_365_entra: M365_DOMAIN_TOKENS,
};

const DOMAIN_STATE_META = {
  ready: { label: "מוכן", cls: "bg-green-100 text-green-700" },
  needs_attention: { label: "דורש תשומת לב", cls: "bg-amber-100 text-amber-800 border border-amber-300" },
  not_ready: { label: "לא מוכן", cls: "bg-red-100 text-red-700 border border-red-200" },
  unavailable: { label: "לא זמין", cls: "bg-slate-100 text-slate-500 border border-dashed border-slate-300" },
  unknown: { label: "לא ידוע", cls: "bg-white text-gray-500 border border-dashed border-gray-400" },
};

const EVIDENCE_STATUS_META = {
  ok: { label: "תקין", cls: "bg-green-100 text-green-700" },
  error: { label: "שגיאה", cls: "bg-red-100 text-red-700" },
  warning: { label: "אזהרה", cls: "bg-amber-100 text-amber-800" },
};

const EVIDENCE_KIND_HE = {
  connection: "חיבור", tool: "כלי", permission: "הרשאה", test: "בדיקת חיבור",
  ticket: "קריאה", run: "ריצה", knowledge: "מקור ידע", check: "בדיקה",
};

const FAILING_CONNECTION_STATUSES = ["error", "failed", "failure", "failing", "invalid", "rejected"];
const OK_CONNECTION_STATUSES = ["ok", "success", "passed", "valid", "active", "connected"];

const BOUNDARY_ROWS = [
  {
    label: "קריאה",
    keys: ["read", "reading", "read_scope"],
    fallback: "קריאת נתונים בלבד — ללא שינוי מצב.",
  },
  {
    label: "המלצה",
    keys: ["recommend", "recommendation", "recommendations", "advise", "suggest"],
    fallback: "המלצות והצעות בלבד — אינן מבוצעות ללא אישור טכנאי.",
  },
  {
    label: "ביצוע באישור",
    keys: ["execute_with_approval", "execute", "execution", "write", "actions_with_approval"],
    chip: { label: "דורש אישור טכנאי", cls: "bg-orange-50 border border-orange-200 text-orange-700" },
    fallback: "ביצוע רק לאחר הצעה ואישור טכנאי, במסלול האישורים הקיים.",
  },
  {
    label: "הסלמה",
    keys: ["escalate", "escalation", "escalation_conditions", "human_escalation"],
    fallback: "מצבים שאינם בתחום המוגדר מועברים להמשך טיפול אנושי.",
  },
];

function pickField(raw, keys) {
  if (!raw || typeof raw !== "object") return undefined;
  for (const key of keys) {
    if (raw[key] !== undefined && raw[key] !== null) return raw[key];
  }
  return undefined;
}

function firstText(item, keys, max = 200) {
  if (typeof item === "string") return boundedText(item, max);
  if (!item || typeof item !== "object") return null;
  for (const key of keys) {
    const found = boundedText(item[key], max);
    if (found) return found;
  }
  return null;
}

function textList(value, max, maxItems = 5) {
  if (!Array.isArray(value)) return [];
  const out = [];
  for (const item of value) {
    const found = firstText(item, ["text", "reason", "label", "message", "description", "name", "detail"], max);
    if (found) out.push(found);
    if (out.length >= maxItems) break;
  }
  return out;
}

function normalizeDomainKey(value) {
  if (typeof value !== "string") return null;
  const v = value.trim().toLowerCase().replace(/[\s-]+/g, "_");
  return SPECIALIST_DOMAIN_ALIASES[v] || null;
}

function normalizeDomainState(value) {
  if (typeof value !== "string") return null;
  const v = value.trim().toLowerCase();
  if (["ready", "ok", "available", "healthy", "operational", "enabled", "active"].includes(v)) return "ready";
  if (["needs_attention", "degraded", "warning", "partial", "attention"].includes(v)) return "needs_attention";
  if (["not_ready", "blocked", "error", "failed", "failure", "disabled", "misconfigured"].includes(v)) return "not_ready";
  if (["unavailable", "not_configured", "unconfigured", "missing", "absent", "not_provisioned"].includes(v)) return "unavailable";
  return null;
}

function evidenceStatus(value) {
  const reported = boundedText(value, 24);
  if (!reported) return null;
  const v = reported.toLowerCase();
  if (OK_CONNECTION_STATUSES.includes(v) || ["approved"].includes(v)) return { ...EVIDENCE_STATUS_META.ok };
  if (FAILING_CONNECTION_STATUSES.includes(v)) return { ...EVIDENCE_STATUS_META.error };
  if (["warning", "degraded", "stale", "partial"].includes(v)) return { ...EVIDENCE_STATUS_META.warning };
  return { label: reported, cls: "bg-gray-100 text-gray-500" };
}

function normalizeEvidence(value, max = 6) {
  if (!Array.isArray(value)) return [];
  const out = [];
  for (const item of value) {
    if (out.length >= max) break;
    if (typeof item === "string") {
      const text = boundedText(item, 200);
      if (text) out.push({ kind: null, text, status: null, observedAt: null });
      continue;
    }
    if (!item || typeof item !== "object") continue;
    const text = firstText(item, ["label", "text", "summary", "description", "detail", "name", "note"], 200);
    if (!text) continue;
    const rawKind = boundedText(item.kind ?? item.type, 24);
    out.push({
      kind: rawKind ? EVIDENCE_KIND_HE[rawKind.toLowerCase()] || null : null,
      text,
      status: evidenceStatus(item.status ?? item.state ?? item.last_test_status ?? item.result),
      observedAt: boundedText(item.observed_at ?? item.checked_at ?? item.as_of ?? item.at ?? item.last_tested_at, 40),
    });
  }
  return out;
}

function normalizeBoundary(value, max = 140, maxItems = 3) {
  if (typeof value === "string") {
    const text = boundedText(value, max);
    return text ? [text] : [];
  }
  if (Array.isArray(value)) return textList(value, max, maxItems);
  if (value && typeof value === "object") {
    const text = firstText(value, ["text", "description", "note", "summary", "label", "name"], max);
    return text ? [text] : [];
  }
  return [];
}

function contractDomainState(entry) {
  const raw = pickField(entry, ["state", "status", "readiness"]);
  return {
    state: normalizeDomainState(raw) || "unknown",
    stateReported: boundedText(raw, 40),
  };
}

function contractBoundaries(entry) {
  const nested = pickField(entry, ["boundaries", "boundary"]);
  return BOUNDARY_ROWS.map((row) => ({
    row,
    texts: normalizeBoundary(pickField(nested, row.keys) ?? pickField(entry, row.keys)),
  }));
}

function defaultBoundaries() {
  return BOUNDARY_ROWS.map((row) => ({ row, texts: [] }));
}

function observedDomainEntry(domainKey, workspace) {
  const connections = Array.isArray(workspace?.capabilities?.connections) ? workspace.capabilities.connections : [];
  if (domainKey === "helpdesk") {
    const tools = Array.isArray(workspace?.capabilities?.tools) ? workspace.capabilities.tools : [];
    const approved = tools.filter((t) => t && (t.approval_required === true || t.strong_approval_required === true)).length;
    const tickets = Array.isArray(workspace?.assigned_tickets) ? workspace.assigned_tickets.length : 0;
    return {
      state: "unknown",
      stateReported: null,
      summary: null,
      note: "מוצגים נתוני העבודה הנוכחיים בלבד — מוכנות התחום טרם דווחה על-ידי השרת.",
      // Its own field, not appended to `note`: `note` is about the domain's readiness contract being
      // absent, this is about what the tool count above it currently amounts to. A reader must be
      // able to tell which of the two facts stopped them from trusting the card.
      entitlementNote: entitlementBoundaryNote(workspace?.capabilities),
      evidenceReported: false,
      evidence: [
        { kind: "כלי", text: `כלים מורשים לעובד: ${tools.length} (${approved} באישור טכנאי)`, status: null, observedAt: null },
        { kind: "קריאה", text: `קריאות בטיפול כרגע: ${tickets}`, status: null, observedAt: null },
      ],
      boundaries: defaultBoundaries(),
    };
  }
  const types = DOMAIN_CONNECTION_TYPES[domainKey] || [];
  const matching = connections.filter((c) => c && types.includes(String(c.system_type)));
  if (matching.length === 0) {
    return {
      state: "unavailable",
      stateReported: null,
      summary: null,
      note: null,
      evidenceReported: false,
      evidence: [{ kind: null, text: "לא מוגדר חיבור לתחום זה לעובד", status: null, observedAt: null }],
      boundaries: defaultBoundaries(),
    };
  }
  const statuses = matching.map((c) => String(c.last_test_status || "").trim().toLowerCase());
  const failing = statuses.some((s) => FAILING_CONNECTION_STATUSES.includes(s));
  const hasOkTest = statuses.some((s) => OK_CONNECTION_STATUSES.includes(s));
  return {
    state: failing ? "not_ready" : "unknown",
    stateReported: null,
    summary: null,
    note: failing
      ? null
      : hasOkTest
        ? "בדיקת החיבור האחרונה תקינה — מוכנות מלאה טרם דווחה על-ידי השרת."
        : "חיבור מוגדר לתחום זה — מוכנות מלאה טרם דווחה על-ידי השרת.",
    evidenceReported: false,
    evidence: matching.map((c) => ({
      kind: "חיבור",
      text: boundedText(c.display_name, 80) || systemTypeLabel(c.system_type),
      status: evidenceStatus(c.last_test_status),
      observedAt: boundedText(c.last_tested_at, 40),
    })),
    boundaries: defaultBoundaries(),
  };
}

function buildSpecialistMatrix(readiness, workspace) {
  const contract = readiness && typeof readiness === "object" && !Array.isArray(readiness) ? readiness : null;
  const listRaw = contract
    ? pickField(contract, ["domains", "matrix", "entries"])
    : Array.isArray(readiness) ? readiness : undefined;
  const contractPresent = contract !== null || Array.isArray(readiness);
  const entries = Array.isArray(listRaw)
    ? listRaw.filter((e) => e && typeof e === "object" && !Array.isArray(e))
    : [];

  const byKey = new Map();
  const extras = [];
  for (const entry of entries.slice(0, 12)) {
    const rawKey = firstText(entry, ["domain", "domain_key", "key", "id"], 60);
    const key = normalizeDomainKey(rawKey);
    if (key && !byKey.has(key)) byKey.set(key, entry);
    else if (!key && rawKey) extras.push({ label: rawKey, entry });
  }

  const fromContract = (entry) => {
    const { state, stateReported } = contractDomainState(entry);
    const rawEvidence = pickField(entry, ["evidence", "proof", "provenance", "signals"]);
    return {
      state,
      stateReported,
      summary: firstText(entry, ["summary", "description", "note"], 220),
      note: state === "unknown" ? "מצב המוכנות טרם דווח באופן מזוהה על-ידי השרת." : null,
      evidenceReported: Array.isArray(rawEvidence),
      evidence: normalizeEvidence(rawEvidence),
      boundaries: contractBoundaries(entry),
    };
  };

  const domains = SPECIALIST_DOMAIN_ORDER.map((key) => {
    if (byKey.has(key)) return { key, mode: "contract", ...fromContract(byKey.get(key)) };
    if (contractPresent) {
      return {
        key, mode: "unreported", state: "unknown", stateReported: null,
        summary: null, note: null, evidenceReported: false, evidence: [],
        boundaries: defaultBoundaries(),
      };
    }
    return { key, mode: "observed", ...observedDomainEntry(key, workspace) };
  });

  for (const extra of extras.slice(0, 4)) {
    domains.push({ key: null, label: extra.label, mode: "contract", ...fromContract(extra.entry) });
  }

  const generatedAt = contract ? pickField(contract, ["generated_at", "as_of", "updated_at"]) : undefined;
  return { contractPresent, generatedAt: boundedText(generatedAt, 40), domains };
}

function DomainCard({ domain }) {
  const meta = DOMAIN_STATE_META[domain.state] || DOMAIN_STATE_META.unknown;
  const label = domain.label || SPECIALIST_DOMAIN_LABELS[domain.key] || domain.key;
  return (
    <div data-testid={`specialist-domain-${domain.key || "extra"}`} className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-semibold text-gray-800">{label}</span>
        <span
          className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${meta.cls}`}
          title={domain.stateReported ? `דווח על-ידי השרת: ${domain.stateReported}` : undefined}
        >
          {meta.label}
        </span>
      </div>
      {domain.summary && <p className="text-xs text-gray-600">{domain.summary}</p>}
      {domain.note && <p className="text-[11px] text-gray-400">{domain.note}</p>}

      <div className="space-y-1">
        <div className="text-[11px] font-medium text-gray-500">
          {domain.mode === "observed" ? "נתונים נצפים" : "עדויות למוכנות"}
        </div>
        {domain.evidence.length > 0 ? (
          <ul className="space-y-0.5">
            {domain.evidence.map((e, i) => (
              <li key={i} className="flex flex-wrap items-center gap-1.5 text-[11px] text-gray-700">
                {e.kind && <span className="text-gray-400">{e.kind}:</span>}
                <span className="truncate" title={e.text}>{e.text}</span>
                {e.status && <span className={`px-1.5 py-0.5 rounded ${e.status.cls}`}>{e.status.label}</span>}
                {e.observedAt && fmt(e.observedAt) && <span className="text-gray-400">{fmt(e.observedAt)}</span>}
              </li>
            ))}
          </ul>
        ) : domain.mode === "unreported" ? (
          <p className="text-[11px] text-gray-400">השרת לא דיווח מצב לתחום זה.</p>
        ) : domain.evidenceReported ? (
          <p className="text-[11px] text-gray-400">השרת דיווח שאין עדויות תומכות לתחום זה.</p>
        ) : (
          <p className="text-[11px] text-gray-400">לא צורפו עדויות תומכות לתחום זה.</p>
        )}
        {domain.entitlementNote && (
          <p data-testid="entitlement-note-specialist-domain" className="text-[11px] text-amber-700">
            {domain.entitlementNote}
          </p>
        )}
      </div>

      <div className="space-y-0.5 border-t border-gray-100 pt-1.5">
        {domain.boundaries.map(({ row, texts }) => (
          <div key={row.label} className="flex flex-wrap items-baseline gap-1.5 text-[11px]">
            <span className="w-20 shrink-0 font-medium text-gray-600">{row.label}</span>
            {texts.length > 0 ? (
              <span className="flex-1 text-gray-700">{texts.join(" · ")}</span>
            ) : (
              <span className="flex-1 text-gray-400">כלל מערכת · {row.fallback}</span>
            )}
            {row.chip && <span className={`px-1.5 py-0.5 rounded border ${row.chip.cls}`}>{row.chip.label}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SpecialistReadinessMatrix({ workspace, agentDetail }) {
  const readiness = workspace?.specialist_readiness
    ?? workspace?.agent?.specialist_readiness
    ?? agentDetail?.specialist_readiness
    ?? null;
  const { contractPresent, generatedAt, domains } = buildSpecialistMatrix(readiness, workspace);

  return (
    <section aria-label="מוכנות מומחיות" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700">מוכנות מומחיות</h3>
        <span className="text-[11px] text-gray-400">Helpdesk · ESET · Acronis · Microsoft 365 / Entra</span>
        {generatedAt && <span className="text-[11px] text-gray-400 mr-auto">עודכן: {fmt(generatedAt)}</span>}
      </div>
      <p className="text-[11px] text-gray-400">
        מצב המוכנות של כל תחום, העדויות שמאחוריו וגבולות הפעולה: קריאה, המלצה, ביצוע באישור והסלמה.
        תצוגת מידע בלבד — אינה מפעילה, מאשרת או מקדמת פעולות; ביצוע נשאר במסלול הצעה → אישור טכנאי → ביצוע.
      </p>
      {!contractPresent && (
        <p className="text-[11px] text-gray-400">
          השרת טרם סיפק מצב מוכנות לתחומים אלה — המצב נגזר מהנתונים הנצפים בלבד:
          אין חיבור מוגדר → לא זמין · בדיקת חיבור אחרונה נכשלה → לא מוכן · אחרת → לא ידוע.
        </p>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {domains.map((domain, i) => (
          <DomainCard key={domain.key || `extra-${i}`} domain={domain} />
        ))}
      </div>
    </section>
  );
}

// ─── Specialist Packs (DIGEMP-SPECIALIST-PACKS-FRONTEND-028) ──────────────────
//
// Professional capability cards for the domains this employee can be equipped for (Helpdesk,
// ESET, Acronis, Microsoft 365/Entra). Everything comes from the additive `specialist_packs`
// descriptor contract — accepted on the workspace payload, its agent projection, or the page's
// agent detail, like the other additive contracts. Each card carries only declared boundaries:
// what the employee may read, what it may recommend, what runs only with technician approval
// (visually distinct from the other two), the knowledge provenance behind the pack, its known
// limits, and the conditions under which it must escalate to a human. When the server has not
// supplied a descriptor, the section says so explicitly — it never invents capabilities, action
// names, sources or readiness. Only whitelisted scalar fields are rendered (bounded lengths,
// capped lists), so credential material stored next to a descriptor can not leak. Informational
// only: no plugin management, no permission toggle, no execute/approve control — actions keep
// going through the existing proposal → approval → action flow.

const SPECIALIST_DOMAIN_HE = {
  helpdesk: "Helpdesk", help_desk: "Helpdesk", service_desk: "Helpdesk",
  eset: "ESET PROTECT", eset_protect: "ESET PROTECT",
  acronis: "Acronis", acronis_backup: "Acronis",
  ...Object.fromEntries(M365_DOMAIN_TOKENS.map((token) => [token, "Microsoft 365 / Entra"])),
};

const PACK_STATE_META = {
  ready: { label: "מוכן", cls: "bg-green-100 text-green-700" },
  partial: { label: "מוכן חלקית", cls: "bg-amber-100 text-amber-800 border border-amber-200" },
  not_ready: { label: "לא מוכן", cls: "bg-red-100 text-red-700" },
  unknown: { label: "לא ידוע", cls: "bg-gray-100 text-gray-600" },
};

const READ_BADGE = { label: "קריאה", cls: "bg-blue-50 border-blue-200 text-blue-700" };
const RECOMMEND_BADGE = { label: "המלצה", cls: "bg-indigo-50 border-indigo-200 text-indigo-700" };
const APPROVAL_BADGE = { label: "דורש אישור", cls: "bg-orange-50 border-orange-200 text-orange-700" };
const STRONG_APPROVAL_BADGE = { label: "דורש אישור מוגבר", cls: "bg-purple-50 border-purple-200 text-purple-700" };

const PACK_KNOWLEDGE_STATUS = {
  approved: { label: "מאושר", cls: "bg-green-100 border-green-200 text-green-700" },
  pending: { label: "טרם אושר", cls: "bg-amber-100 border-amber-200 text-amber-800" },
};

const PACK_READ_KEYS = ["read", "read_actions", "read_only", "read_only_actions"];
const PACK_RECOMMEND_KEYS = ["recommend", "recommend_actions", "recommendations"];
const PACK_EXECUTE_KEYS = ["execute_with_approval", "execute_actions", "approval_actions", "approval_bound_actions", "write_actions", "execute"];
const PACK_KNOWLEDGE_KEYS = ["knowledge", "knowledge_provenance", "provenance", "sources", "knowledge_sources"];
const PACK_LIMITS_KEYS = ["limits", "known_limits", "constraints", "boundaries"];
const PACK_ESCALATION_KEYS = ["escalation", "escalation_conditions", "escalate_when", "escalation_triggers", "handoff_conditions"];

const PACK_SUPPORTED_DOMAINS = "תחומים נתמכים בזמן מסירת החבילות: Helpdesk · ESET · Acronis · Microsoft 365 / Entra";

function packField(raw, keys) {
  if (!raw || typeof raw !== "object") return undefined;
  for (const key of keys) {
    if (raw[key] !== undefined && raw[key] !== null) return raw[key];
  }
  return undefined;
}

function packToken(value) {
  return typeof value === "string" ? value.trim().toLowerCase().replace(/[\s-]+/g, "_") : "";
}

function packText(item, keys, max = 160) {
  if (typeof item === "string") return boundedText(item, max);
  if (!item || typeof item !== "object") return null;
  for (const key of keys) {
    const found = boundedText(item[key], max);
    if (found) return found;
  }
  return null;
}

function packEntries(value) {
  if (Array.isArray(value)) return value;
  return typeof value === "string" ? [value] : [];
}

function packDate(value) {
  if (typeof value !== "string" || Number.isNaN(Date.parse(value))) return null;
  return fmt(value) || null;
}

function normalizePackState(value) {
  if (typeof value !== "string") return null;
  const v = packToken(value);
  if (["ready", "available", "ok", "approved", "enabled", "active", "configured"].includes(v)) return "ready";
  if (["partial", "limited", "degraded", "onboarding", "provisioning"].includes(v)) return "partial";
  if (["not_ready", "blocked", "unavailable", "unconfigured", "not_configured", "disabled", "error", "failed"].includes(v)) return "not_ready";
  return "unknown";
}

function normalizePackActions(value, cap = 8) {
  return packEntries(value)
    .map((entry) => {
      const label = packText(entry, ["name", "action", "tool", "label", "title"], 60);
      if (!label) return null;
      const detail = packText(entry, ["description", "detail", "note"], 120);
      return {
        label,
        detail: detail && detail !== label ? detail : null,
        strong: !!entry && typeof entry === "object" && entry.strong_approval_required === true,
        approval: !!entry && typeof entry === "object" && entry.approval_required === true,
      };
    })
    .filter(Boolean)
    .slice(0, cap);
}

function normalizePackNotes(value, keys, cap = 6) {
  return packEntries(value)
    .map((entry) => packText(entry, keys, 140))
    .filter(Boolean)
    .slice(0, cap);
}

function normalizePackKnowledge(value, cap = 6) {
  return packEntries(value)
    .map((entry) => {
      const source = packText(entry, ["source", "name", "title", "vendor", "knowledge_base", "base"], 120);
      if (!source) return null;
      const version = packText(entry, ["version", "revision", "version_label"], 40)
        || (entry && typeof entry === "object" ? packDate(packText(entry, ["updated_at", "last_updated"], 40)) : null);
      const statusRaw = entry && typeof entry === "object"
        ? packText(entry, ["status", "state", "verification", "approval", "review_status"], 40)
        : null;
      let status = { kind: "none", raw: null };
      if (statusRaw) {
        const token = packToken(statusRaw);
        if (["approved", "verified", "current", "active", "ok", "published"].includes(token)) status = { kind: "approved", raw: statusRaw };
        else if (["pending", "proposed", "draft", "unverified", "candidate", "under_review"].includes(token)) status = { kind: "pending", raw: statusRaw };
        else status = { kind: "unknown", raw: statusRaw };
      }
      return { source, version: version || null, status };
    })
    .filter(Boolean)
    .slice(0, cap);
}

function normalizeSpecialistPack(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const rawLabel = packText(raw, ["domain", "domain_name", "domain_label", "name", "pack", "title", "specialty", "id"], 80);
  if (!rawLabel) return null;
  const stateRaw = packField(raw, ["state", "status", "readiness"]);
  const readRaw = packField(raw, PACK_READ_KEYS);
  const recommendRaw = packField(raw, PACK_RECOMMEND_KEYS);
  const executeRaw = packField(raw, PACK_EXECUTE_KEYS);
  const knowledgeRaw = packField(raw, PACK_KNOWLEDGE_KEYS);
  const limitsRaw = packField(raw, PACK_LIMITS_KEYS);
  const escalationRaw = packField(raw, PACK_ESCALATION_KEYS);
  return {
    label: SPECIALIST_DOMAIN_HE[packToken(rawLabel)] || rawLabel,
    state: normalizePackState(stateRaw),
    stateRaw: typeof stateRaw === "string" ? boundedText(stateRaw, 40) : null,
    read: normalizePackActions(readRaw),
    readDefined: readRaw !== undefined,
    recommend: normalizePackActions(recommendRaw),
    recommendDefined: recommendRaw !== undefined,
    execute: normalizePackActions(executeRaw),
    executeDefined: executeRaw !== undefined,
    knowledge: normalizePackKnowledge(knowledgeRaw),
    knowledgeDefined: knowledgeRaw !== undefined,
    limits: normalizePackNotes(limitsRaw, ["limit", "text", "title", "description"], 6),
    limitsDefined: limitsRaw !== undefined,
    escalation: normalizePackNotes(escalationRaw, ["condition", "text", "title", "trigger", "description"], 6),
    escalationDefined: escalationRaw !== undefined,
  };
}

function PackBadge({ meta }) {
  return <span className={`px-1.5 py-0.5 rounded border whitespace-nowrap ${meta.cls}`}>{meta.label}</span>;
}

function PackActionList({ items, badgeOf, fallback }) {
  if (!items.length) return <p className="text-xs text-gray-500">{fallback}</p>;
  return (
    <ul className="space-y-1">
      {items.map((action, i) => (
        <li key={`${action.label}-${i}`} className="text-xs text-gray-700 space-y-0.5">
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <span className="font-mono" dir="ltr">{action.label}</span>
            <PackBadge meta={badgeOf(action)} />
          </span>
          {action.detail && <div className="text-gray-500">{action.detail}</div>}
        </li>
      ))}
    </ul>
  );
}

function PackBlock({ title, tone, testId, children }) {
  return (
    <div data-testid={testId} className={`rounded-lg border p-2.5 space-y-1.5 ${tone}`}>
      <div className="text-xs font-medium text-gray-700">{title}</div>
      {children}
    </div>
  );
}

function SpecialistPackCard({ pack, index }) {
  const stateMeta = pack.state ? PACK_STATE_META[pack.state] : null;
  return (
    <div data-testid={`specialist-pack-${index}`} className="rounded-lg border border-gray-200 bg-white p-3 space-y-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold text-gray-800">{pack.label}</h4>
        {stateMeta && (
          <span
            className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${stateMeta.cls}`}
            title={pack.state === "unknown" && pack.stateRaw ? `דווח על-ידי השרת: ${pack.stateRaw}` : undefined}
          >
            {stateMeta.label}
          </span>
        )}
      </div>

      <PackBlock testId="pack-read" tone="border-blue-100 bg-blue-50/40" title="פעולות קריאה">
        <PackActionList
          items={pack.read}
          badgeOf={() => READ_BADGE}
          fallback={pack.readDefined ? "לא הוגדרו פעולות קריאה." : "טרם דווחו פעולות קריאה מהשרת."}
        />
      </PackBlock>

      <PackBlock testId="pack-recommend" tone="border-indigo-100 bg-indigo-50/40" title="המלצות">
        <PackActionList
          items={pack.recommend}
          badgeOf={() => RECOMMEND_BADGE}
          fallback={pack.recommendDefined ? "לא הוגדרו המלצות." : "טרם דווחו המלצות מהשרת."}
        />
      </PackBlock>

      <PackBlock testId="pack-execute" tone="border-orange-200 bg-orange-50/40" title="ביצוע באישור">
        <PackActionList
          items={pack.execute}
          badgeOf={(action) => (action.strong ? STRONG_APPROVAL_BADGE : APPROVAL_BADGE)}
          fallback={pack.executeDefined ? "לא הוגדרו פעולות ביצוע באישור." : "טרם דווחו פעולות ביצוע באישור מהשרת."}
        />
      </PackBlock>

      <PackBlock testId="pack-knowledge" tone="border-gray-100 bg-gray-50/60" title="מקורות ידע">
        {pack.knowledge.length > 0 ? (
          <ul className="space-y-1">
            {pack.knowledge.map((entry, i) => (
              <li key={`${entry.source}-${i}`} className="text-xs text-gray-700 flex flex-wrap items-center gap-1.5">
                <span>{entry.source}</span>
                {entry.version && <span className="text-gray-400">· {entry.version}</span>}
                {entry.status.kind === "approved" && <PackBadge meta={PACK_KNOWLEDGE_STATUS.approved} />}
                {entry.status.kind === "pending" && <PackBadge meta={PACK_KNOWLEDGE_STATUS.pending} />}
                {entry.status.kind === "unknown" && entry.status.raw && (
                  <PackBadge meta={{ label: entry.status.raw, cls: "bg-gray-100 border-gray-200 text-gray-500" }} />
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-gray-500">
            {pack.knowledgeDefined ? "לא דווחו מקורות ידע." : "טרם דווחו מקורות ידע מהשרת."}
          </p>
        )}
      </PackBlock>

      <PackBlock testId="pack-limits" tone="border-gray-100 bg-gray-50/60" title="מגבלות החבילה">
        {pack.limits.length > 0 ? (
          <ul className="list-disc pr-4 text-xs text-gray-700 space-y-0.5">
            {pack.limits.map((limit, i) => <li key={i}>{limit}</li>)}
          </ul>
        ) : (
          <p className="text-xs text-gray-500">{pack.limitsDefined ? "לא דווחו מגבלות." : "טרם דווחו מגבלות מהשרת."}</p>
        )}
      </PackBlock>

      <PackBlock testId="pack-escalation" tone="border-amber-200 bg-amber-50/60" title="הסלמה לטכנאי">
        {pack.escalation.length > 0 ? (
          <>
            <p className="text-[11px] text-amber-800">מתי העבודה מועברת להמשך טיפול אנושי:</p>
            <ul className="list-disc pr-4 text-xs text-gray-700 space-y-0.5">
              {pack.escalation.map((condition, i) => <li key={i}>{condition}</li>)}
            </ul>
          </>
        ) : (
          <p className="text-xs text-gray-500">
            {pack.escalationDefined ? "לא דווחו תנאי הסלמה." : "טרם דווחו תנאי הסלמה מהשרת."}
          </p>
        )}
      </PackBlock>
    </div>
  );
}

export function SpecialistPacksSection({ workspace, agentDetail }) {
  const pickRoot = (source) => source?.specialist_packs ?? source?.specialist_pack_descriptors ?? null;
  const raw = pickRoot(workspace) ?? pickRoot(workspace?.agent) ?? pickRoot(agentDetail) ?? null;
  const root = raw && typeof raw === "object" ? raw : null;
  const packsRaw = Array.isArray(root)
    ? root
    : root
      ? packField(root, ["packs", "descriptors", "pack_descriptors", "items"])
      : undefined;
  const list = Array.isArray(packsRaw) ? packsRaw : null;
  const packs = (list || []).map(normalizeSpecialistPack).filter(Boolean).slice(0, 8);
  const updatedAt = root && !Array.isArray(root) ? packField(root, ["generated_at", "as_of", "updated_at"]) : undefined;
  const explicitlyEmpty = list !== null && list.length === 0;

  return (
    <section aria-label="חבילות מומחה" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700">חבילות מומחה</h3>
        {updatedAt && <span className="text-[11px] text-gray-400 mr-auto">עודכן: {fmt(updatedAt)}</span>}
      </div>
      <p className="text-[11px] text-gray-400">
        לכל חבילה: מה העובד יכול לקרוא, להמליץ, לבצע רק באישור טכנאי — ומתי עליו להסלים לטכנאי. הקטע אינו מעניק הרשאות.
      </p>

      {packs.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {packs.map((pack, i) => <SpecialistPackCard key={`${pack.label}-${i}`} pack={pack} index={i} />)}
        </div>
      )}

      {packs.length === 0 && explicitlyEmpty && (
        <p className="text-sm text-gray-500">השרת דיווח שאין חבילות מומחה מוגדרות לעובד זה.</p>
      )}

      {packs.length === 0 && !explicitlyEmpty && (
        <>
          <p className="text-sm text-gray-500">
            חבילות המומחה טרם נמסרו על-ידי השרת — לא מוצגות כאן יכולות, מקורות, מגבלות או תנאי הסלמה ללא נתונים מגבים.
          </p>
          <p className="text-[11px] text-gray-400">{PACK_SUPPORTED_DOMAINS}</p>
        </>
      )}

      <p className="text-[11px] text-gray-400">
        תצוגת מידע בלבד — הקטע אינו מעניק הרשאות, אינו יוצר חיבורים ואינו מאשר פעולות; כל ביצוע ממשיך לעבור במסלול ההצעה, האישור והביצוע הקיים.
      </p>
    </section>
  );
}

// ─── Remote Operations readiness (DIGEMP-REMOTE-READINESS-FRONTEND-029) ────────
//
// Read-only view of whether this employee can currently do remote work, and why. The verdict
// (state + reasons) and the operating specifics (approved transports, required target binding,
// approval tier, supported bounded named actions, postcondition/evidence requirements) come
// from the additive `remote_readiness` contract — accepted on the workspace payload, its agent
// projection, or the page's agent detail, like the other additive contracts. When the server
// has not supplied it, the section says so explicitly and shows the facts already in this
// payload (the configured TeamViewer/OpenClaw connection records) plus the standing system
// rules — never an invented verdict. Only whitelisted scalar fields are rendered (bounded
// lengths, capped lists), so credential material stored next to a transport/action can not
// leak. The section adds no controls: remote actions still go through the existing action
// proposal → approval → execution flow and nothing here starts, approves or accelerates one.

const REMOTE_TRANSPORT_TYPES = ["teamviewer", "openclaw"];

const READINESS_META = {
  ready: { label: "מוכן לפעולות מרחוק", cls: "bg-green-100 text-green-700" },
  not_ready: { label: "לא מוכן לפעולות מרחוק", cls: "bg-amber-100 text-amber-800 border border-amber-200" },
  unknown: { label: "לא ידוע", cls: "bg-gray-100 text-gray-600" },
};

const TRANSPORT_STATUS_META = {
  available: { label: "זמין", cls: "bg-green-100 text-green-700" },
  unavailable: { label: "לא זמין", cls: "bg-red-100 text-red-700" },
  unknown: { label: "לא ידוע", cls: "bg-gray-100 text-gray-600" },
};

const PERMISSION_TIER_HE = {
  tier_1: "רמה 1 — קריאה בלבד",
  tier_2: "רמה 2 — פעולות תפעוליות באישור טכנאי",
  tier_3: "רמה 3 — אישור טכנאי ונתיב אישור מוגבר",
};

function scalarText(value, max) {
  if (typeof value === "number" && Number.isFinite(value)) return String(value).slice(0, max);
  return boundedText(value, max);
}

function normalizeReadinessState(value) {
  if (typeof value !== "string") return null;
  const v = value.trim().toLowerCase();
  if (["ready", "available", "ok", "approved", "enabled", "active"].includes(v)) return "ready";
  if (["not_ready", "blocked", "unavailable", "unconfigured", "not_configured", "disabled", "error", "failed"].includes(v)) return "not_ready";
  return null;
}

function transportStatus(value) {
  if (typeof value !== "string") return "unknown";
  const v = value.trim().toLowerCase();
  if (["ok", "success", "available", "ready", "connected", "active"].includes(v)) return "available";
  if (["error", "failed", "failure", "unavailable", "not_configured", "unconfigured", "disconnected", "missing", "offline"].includes(v)) return "unavailable";
  return "unknown";
}

function normalizeContractTransports(value) {
  return value
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const rawName = firstText(entry, ["name", "display_name", "transport", "adapter", "system_type"], 60);
      if (!rawName) return null;
      return {
        name: SYSTEM_TYPE_HE[rawName] || REMOTE_TRANSPORT_HE[rawName] || rawName,
        status: transportStatus(entry.status ?? entry.availability ?? entry.state ?? entry.last_test_status),
        detail: firstText(entry, ["detail", "note", "description"], 120),
      };
    })
    .filter(Boolean)
    .slice(0, 6);
}

function observedTransports(connections) {
  return (Array.isArray(connections) ? connections : [])
    .filter((c) => c && REMOTE_TRANSPORT_TYPES.includes(String(c.system_type)))
    .slice(0, 6)
    .map((c) => ({
      name: REMOTE_TRANSPORT_HE[c.system_type] || systemTypeLabel(c.system_type),
      status: transportStatus(c.last_test_status),
      detail: boundedText(c.display_name, 80),
    }));
}

function normalizeTargetBinding(raw) {
  if (!raw || typeof raw !== "object") return null;
  return {
    requirements: textList(pickField(raw, ["requirements", "rules", "notes"]), 140, 4),
    customer: firstText(raw, ["customer_name", "bound_customer_name", "customer", "bound_customer"], 60),
    ticket: scalarText(pickField(raw, ["ticket_number", "bound_ticket_number", "ticket"]), 24),
  };
}

function normalizeApproval(raw) {
  if (typeof raw === "string") return { label: PERMISSION_TIER_HE[raw] || boundedText(raw, 40), description: null };
  if (!raw || typeof raw !== "object") return null;
  const tier = firstText(raw, ["tier", "approval_tier", "level"], 40);
  const label = firstText(raw, ["label", "name"], 60) || (tier ? PERMISSION_TIER_HE[tier] || `רמה "${tier}"` : null);
  const description = firstText(raw, ["description", "note"], 140);
  if (!label && !description) return null;
  return { label, description };
}

function normalizeRemoteActions(value) {
  return value
    .map((entry) => {
      if (!entry || typeof entry !== "object") return null;
      const name = firstText(entry, ["name", "action", "tool"], 60);
      if (!name) return null;
      const strong = entry.strong_approval_required === true;
      return {
        name,
        strong,
        approval: strong || entry.approval_required === true,
        detail: firstText(entry, ["description", "note", "detail"], 120),
      };
    })
    .filter(Boolean)
    .slice(0, 8);
}

function normalizePostconditions(value) {
  return value
    .map((entry) => {
      const text = firstText(entry, ["text", "description", "name", "requirement", "detail"], 160);
      if (!text) return null;
      const optional = entry && typeof entry === "object" && (entry.required === false || entry.mandatory === false);
      return { text, required: !optional };
    })
    .filter(Boolean)
    .slice(0, 6);
}

function ReadinessBlock({ title, children }) {
  return (
    <div className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1.5">
      <div className="text-xs font-medium text-gray-700">{title}</div>
      {children}
    </div>
  );
}

function RuleLine({ children }) {
  return (
    <p className="text-xs text-gray-600">
      <span className="text-gray-400">כלל מערכת · </span>{children}
    </p>
  );
}

function TransportList({ items }) {
  return (
    <ul className="space-y-1">
      {items.map((t, i) => {
        const meta = TRANSPORT_STATUS_META[t.status];
        return (
          <li key={i} className="flex flex-wrap items-center gap-2 text-xs text-gray-700">
            <span>{t.name}</span>
            <span className={`px-1.5 py-0.5 rounded ${meta.cls}`}>{meta.label}</span>
            {t.detail && <span className="text-gray-400 truncate">{t.detail}</span>}
          </li>
        );
      })}
    </ul>
  );
}

export function RemoteOperationsReadiness({ workspace, agentDetail }) {
  const raw = workspace?.remote_readiness ?? workspace?.agent?.remote_readiness ?? agentDetail?.remote_readiness ?? null;
  const contract = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;

  const stateRaw = contract ? pickField(contract, ["state", "status", "readiness"]) : undefined;
  const state = normalizeReadinessState(stateRaw) || "unknown";
  const stateReported = boundedText(stateRaw, 40);

  const reasons = contract ? textList(pickField(contract, ["reasons", "blockers", "why"]), 180, 5) : [];
  const transportsRaw = contract ? pickField(contract, ["transports", "adapters", "channels"]) : undefined;
  const actionsRaw = contract ? pickField(contract, ["actions", "bounded_actions", "supported_actions"]) : undefined;
  const postRaw = contract ? pickField(contract, ["postconditions", "postcondition_requirements", "evidence", "evidence_requirements"]) : undefined;
  const targetRaw = contract ? pickField(contract, ["target_binding", "target", "binding"]) : undefined;
  const approvalRaw = contract ? pickField(contract, ["approval", "approval_requirement", "approval_tier"]) : undefined;
  const generatedAt = contract ? pickField(contract, ["generated_at", "as_of", "updated_at"]) : undefined;

  const contractTransports = Array.isArray(transportsRaw) ? normalizeContractTransports(transportsRaw) : [];
  const observed = observedTransports(workspace?.capabilities?.connections);
  const target = normalizeTargetBinding(targetRaw);
  const approval = normalizeApproval(approvalRaw);
  const actions = Array.isArray(actionsRaw) ? normalizeRemoteActions(actionsRaw) : [];
  const postconditions = Array.isArray(postRaw) ? normalizePostconditions(postRaw) : [];

  const stateMeta = READINESS_META[state];
  const targetReported = target && (target.requirements.length > 0 || target.customer || target.ticket);

  return (
    <section aria-label="מוכנות לפעולות מרחוק" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700">מוכנות לפעולות מרחוק</h3>
        <span
          className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${stateMeta.cls}`}
          title={stateReported ? `דווח על-ידי השרת: ${stateReported}` : undefined}
        >
          {stateMeta.label}
        </span>
        {generatedAt && <span className="text-[11px] text-gray-400 mr-auto">עודכן: {fmt(generatedAt)}</span>}
      </div>

      {state === "unknown" && (
        <p className="text-xs text-gray-500">מצב המוכנות טרם דווח באופן מזוהה על-ידי השרת. להלן כללי המערכת והנתונים הקיימים.</p>
      )}
      {state === "ready" && reasons.length > 0 && (
        <div className="text-xs text-green-800 space-y-0.5">
          <div className="font-medium">מה מאפשר את המוכנות:</div>
          <ul className="list-disc pr-4 space-y-0.5">
            {reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}
      {state === "not_ready" && reasons.length > 0 && (
        <div className="text-xs text-amber-800 space-y-0.5">
          <div className="font-medium">מה חוסם כרגע:</div>
          <ul className="list-disc pr-4 space-y-0.5">
            {reasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ReadinessBlock title={Array.isArray(transportsRaw) ? "אמצעי חיבור מאושרים" : "אמצעי חיבור מרחוק שזוהו"}>
          {Array.isArray(transportsRaw) ? (
            contractTransports.length > 0 ? (
              <TransportList items={contractTransports} />
            ) : (
              <p className="text-xs text-gray-500">לא דווחו אמצעי חיבור מאושרים.</p>
            )
          ) : observed.length > 0 ? (
            <>
              <TransportList items={observed} />
              <p className="text-[11px] text-gray-400">לפי החיבורים המוגדרים לעובד ותוצאת בדיקת החיבור האחרונה; אישור התעבורה נאכף בזמן הפעולה.</p>
            </>
          ) : (
            <p className="text-xs text-gray-500">לא זוהו חיבורי TeamViewer או OpenClaw מוגדרים לעובד.</p>
          )}
        </ReadinessBlock>

        <ReadinessBlock title="קישור יעד נדרש">
          <RuleLine>פעולה מרחוק מתבצעת רק מול יעד מקושר במפורש — לקוח וקריאה מדויקים; אין יעד כללי, משוער או מחושב.</RuleLine>
          {targetReported && (
            <>
              {target.requirements.length > 0 && (
                <ul className="list-disc pr-4 text-xs text-gray-600 space-y-0.5">
                  {target.requirements.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              )}
              {(target.customer || target.ticket) && (
                <div className="text-xs text-gray-700">
                  יעד מקושר שדווח: {target.customer || "—"}{target.ticket ? ` · קריאה #${target.ticket}` : ""}
                </div>
              )}
            </>
          )}
          {target && !targetReported && <p className="text-xs text-gray-500">לא דווח יעד מקושר נוכחי.</p>}
        </ReadinessBlock>

        <ReadinessBlock title="אישור נדרש">
          <RuleLine>פעולות מרחוק אינן מבוצעות אוטומטית: הן מוצגות לאישור טכנאי לפני ביצוע, והרמה הנדרשת נגזרת מסוג הפעולה ומהסיכון.</RuleLine>
          {approval ? (
            <>
              {approval.label && (
                <div className="text-xs text-gray-700">רמת אישור נדרשת: <span className="font-medium">{approval.label}</span></div>
              )}
              {approval.description && <p className="text-xs text-gray-500">{approval.description}</p>}
            </>
          ) : (
            <p className="text-xs text-gray-500">רמת האישור המדויקת טרם דווחה מהשרת.</p>
          )}
        </ReadinessBlock>

        <ReadinessBlock title="פעולות תחומות נתמכות">
          {actions.length > 0 ? (
            <ul className="space-y-1">
              {actions.map((a, i) => (
                <li key={i} className="text-xs text-gray-700 space-y-0.5">
                  <span className="inline-flex flex-wrap items-center gap-1.5">
                    <span className="font-mono" dir="ltr">{a.name}</span>
                    <span className={`px-1.5 py-0.5 rounded border ${
                      a.strong ? "bg-purple-50 border-purple-200 text-purple-700"
                      : a.approval ? "bg-orange-50 border-orange-200 text-orange-700"
                      : "bg-blue-50 border-blue-200 text-blue-700"
                    }`}>
                      {a.strong ? "דורש אישור מוגבר" : a.approval ? "דורש אישור" : "קריאה בלבד"}
                    </span>
                  </span>
                  {a.detail && <div className="text-gray-500">{a.detail}</div>}
                </li>
              ))}
            </ul>
          ) : Array.isArray(actionsRaw) ? (
            <p className="text-xs text-gray-500">לא הוגדרו פעולות תחומות נתמכות.</p>
          ) : (
            <p className="text-xs text-gray-500">טרם דווח מהשרת אילו פעולות תחומות נתמכות.</p>
          )}
        </ReadinessBlock>
      </div>

      <ReadinessBlock title="אימות תנאי סיום (postcondition)">
        <RuleLine>סיום פעולה מרחוק מחייב אימות תנאי סיום מתועד לפני שהיא מסומנת כמוצלחת.</RuleLine>
        {postconditions.length > 0 && (
          <ul className="list-disc pr-4 text-xs text-gray-600 space-y-0.5">
            {postconditions.map((p, i) => (
              <li key={i}>
                {p.text}{" "}
                <span className={`text-[11px] px-1.5 py-0.5 rounded ${
                  p.required ? "bg-amber-50 text-amber-700 border border-amber-200" : "bg-gray-100 text-gray-500"
                }`}>
                  {p.required ? "נדרש" : "אופציונלי"}
                </span>
              </li>
            ))}
          </ul>
        )}
        {Array.isArray(postRaw) && postconditions.length === 0 && (
          <p className="text-xs text-gray-500">לא דווחו דרישות אימות נוספות.</p>
        )}
      </ReadinessBlock>

      <p className="text-[11px] text-gray-400">
        תצוגת מידע בלבד — הקטע אינו מפעיל פעולות מרחוק, אינו מאשר אותן ואינו עוקף את תהליכי האישור הקיימים.
      </p>
    </section>
  );
}

// ─── Guardian workspace (DIGEMP-GUARDIAN-READONLY-FRONTEND-038) ───────────────
//
// The read-only guardian summary for the Platform Guardian employee page. It reads the existing
// admin-gated endpoints (platform-guardian status/findings/summary, backup-guardian health) and
// presents platform/security/backup findings in operator language with severity, freshness and
// provenance. Deliberately absent: the run trigger, finding-status changes, ticket creation and
// the backup check/config controls the admin pages own — this surface must not remediate anything.
// A refused (admin-only, 403) or failed source degrades to an explicit unknown, never to a guess.

// Guardian's scheduled audit cadence is 24h (platform_guardian.seed_guardian_scheduled_check);
// two missed cycles mark the audit stale. Findings only refresh when their rule fires during a
// scan, so "not re-observed" is per-finding and marked after three missed cycles.
const AUDIT_STALE_HOURS = 48;
const FINDING_STALE_HOURS = 72;

const GUARDIAN_SEVERITY_HE = { critical: "קריטי", high: "גבוה", medium: "בינוני", low: "נמוך", info: "מידע" };
const GUARDIAN_SEVERITY_CLS = {
  critical: "bg-red-100 text-red-700 border-red-300",
  high:     "bg-orange-100 text-orange-700 border-orange-300",
  medium:   "bg-yellow-100 text-yellow-700 border-yellow-300",
  low:      "bg-blue-100 text-blue-700 border-blue-300",
};
const FINDING_STATUS_HE = { open: "פתוח", acknowledged: "אושר", watching: "במעקב", resolved: "נפתר", ignored: "מוסתר" };
const FINDING_STATUS_CLS = {
  open:         "bg-red-50 text-red-700",
  acknowledged: "bg-amber-50 text-amber-700",
  watching:     "bg-blue-50 text-blue-700",
  resolved:     "bg-green-50 text-green-700",
  ignored:      "bg-gray-100 text-gray-400",
};
const UNRESOLVED_FINDING_STATUSES = new Set(["open", "acknowledged", "watching"]);
const GUARDIAN_CATEGORY_HE = {
  ticket_health: "בריאות טיפול בקריאות",
  ai_health: "בריאות ניתוח AI",
  user_access: "הרשאות משתמשים",
};
const BACKUP_SEVERITY_HE = { ok: "תקין", warning: "אזהרה", critical: "קריטי" };
const BACKUP_SEVERITY_CLS = {
  ok:       "bg-green-100 text-green-700 border-green-300",
  warning:  "bg-amber-100 text-amber-700 border-amber-300",
  critical: "bg-red-100 text-red-700 border-red-300",
};

function ageHours(iso) {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? (Date.now() - t) / 3600000 : null;
}

function relAge(iso) {
  const hours = ageHours(iso);
  if (hours == null) return null;
  const mins = Math.round(hours * 60);
  if (mins < 1) return "זה עתה";
  if (mins < 60) return `לפני ${mins} דק׳`;
  if (hours < 48) return `לפני ${Math.round(hours)} שעות`;
  return `לפני ${Math.round(hours / 24)} ימים`;
}

function GuardianStateChip({ state, testId }) {
  const META = {
    fresh:   { label: "מעודכן", cls: "bg-green-100 text-green-700 border-green-300" },
    stale:   { label: "מיושן",  cls: "bg-amber-100 text-amber-700 border-amber-300" },
    unknown: { label: "לא ידוע", cls: "bg-gray-100 text-gray-500 border-gray-300" },
  };
  const meta = META[state];
  return (
    <span data-testid={testId} className={`${CHIP_CLS} ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function SourceUnknown({ error, subject, onRetry }) {
  const refused = error?.response?.status === 403;
  return (
    <p role="alert" className="text-sm text-gray-500 italic">
      {refused
        ? `אין הרשאה לצפות בנתוני ${subject} — המידע מוצג למנהלים בלבד (לא ידוע)`
        : `לא ניתן לטעון את נתוני ${subject} (לא ידוע)`}
      <button type="button" onClick={onRetry} className="mr-2 underline text-gray-600">נסה שוב</button>
    </p>
  );
}

// Evidence is rendered through a bounded whitelist: flat scalar history entries (time + bounded
// text), never a raw JSON dump — the same rule that keeps run context off the timeline (024).
const EVIDENCE_KEEP = new Set(["found_at", "summary", "run_id", "run", "at", "note", "detail", "count", "value"]);

function boundedEvidence(evidence) {
  if (!evidence || typeof evidence !== "object" || Array.isArray(evidence)) return [];
  const history = Array.isArray(evidence.history) ? evidence.history : Array.isArray(evidence.entries) ? evidence.entries : null;
  if (!history) return [];
  return history.slice(-5).map((entry) => {
    if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null;
    const fields = Object.keys(entry)
      .filter((key) => EVIDENCE_KEEP.has(key))
      .map((key) => {
        const value = entry[key];
        if (typeof value === "string") return { key, text: value.trim().slice(0, 160) };
        if (typeof value === "number" && Number.isFinite(value)) return { key, text: String(value) };
        return null;
      })
      .filter((f) => f && f.text);
    return fields.length ? fields : null;
  }).filter(Boolean);
}

const EVIDENCE_FIELD_HE = { found_at: "מתי", at: "מתי", summary: "מה נצפה", run_id: "ריצה", run: "ריצה", note: "הערה", detail: "פירוט", count: "כמות", value: "ערך" };

function GuardianEvidence({ finding }) {
  const [open, setOpen] = useState(false);
  const entries = boundedEvidence(finding?.evidence);
  if (entries.length === 0 && !finding?.fingerprint) return null;
  return (
    <div className="text-[11px] text-gray-500">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="underline decoration-dotted hover:text-gray-700"
      >
        {open ? "הסתר ראיות" : `ראיות (${entries.length})`}
      </button>
      {open && (
        <div className="mt-1 rounded border border-gray-200 bg-gray-50 p-2 space-y-1">
          {entries.map((fields, i) => (
            <div key={i} className="flex flex-wrap gap-x-2">
              {fields.map(({ key, text }) => (
                <span key={key}>
                  <span className="text-gray-400">{EVIDENCE_FIELD_HE[key] || key}: </span>
                  <span dir={key === "run_id" || key === "run" ? "ltr" : undefined}>{text}</span>
                </span>
              ))}
            </div>
          ))}
          <div className="text-gray-400">
            טביעת אצבע: <span className="font-mono" dir="ltr">{String(finding.fingerprint).slice(0, 12)}…</span>
          </div>
        </div>
      )}
    </div>
  );
}

function GuardianFindingRow({ finding }) {
  const severity = GUARDIAN_SEVERITY_HE[finding.severity] ? finding.severity : "medium";
  const lastSeenHours = ageHours(finding.last_seen_at);
  const stale = lastSeenHours != null && lastSeenHours > FINDING_STALE_HOURS;
  const lastSeen = relAge(finding.last_seen_at);
  const title = boundedText(finding.title, 160) || "ממצא ללא כותרת";
  const summary = boundedText(finding.summary, 300);
  const recommendation = boundedText(finding.recommendation, 300);
  return (
    <li className={`rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1 ${finding.status === "ignored" || finding.status === "resolved" ? "opacity-60" : ""}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded-full border whitespace-nowrap ${GUARDIAN_SEVERITY_CLS[severity]}`}>
          {GUARDIAN_SEVERITY_HE[finding.severity] || finding.severity}
        </span>
        <span className="text-sm font-medium text-gray-800">{title}</span>
        <span className={`text-[11px] px-1.5 py-0.5 rounded-full whitespace-nowrap mr-auto ${FINDING_STATUS_CLS[finding.status] || "bg-gray-100 text-gray-500"}`}>
          {FINDING_STATUS_HE[finding.status] || finding.status}
        </span>
      </div>

      {summary && <p className="text-xs text-gray-700">{summary}</p>}
      {recommendation && (
        <p className="text-[11px] text-indigo-700 bg-indigo-50/70 border border-indigo-100 rounded px-2 py-1">
          המלצה: {recommendation}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px]">
        <span className={stale ? "text-amber-700" : lastSeenHours == null ? "text-gray-400" : "text-gray-500"}>
          {stale ? `טרם נצפה מחדש — אחרונה ${lastSeen}` : lastSeen ? `נצפה לאחרונה ${lastSeen}` : "מועד תצפית אחרון לא ידוע"}
        </span>
        {Number.isInteger(finding.occurrence_count) && (
          <span className="text-gray-500">נצפה {finding.occurrence_count} פעמים</span>
        )}
        {finding.first_seen_at && <span className="text-gray-400">ראשונה: {fmt(finding.first_seen_at)}</span>}
      </div>

      <div className="flex flex-wrap items-center gap-3 text-[11px] text-gray-500">
        <span>מקור: <span dir="ltr">{boundedText(finding.source_agent_name, 80) || "Guardian"}</span></span>
        {finding.category && <span>{GUARDIAN_CATEGORY_HE[finding.category] || boundedText(finding.category, 60)}</span>}
        {finding.source_run_id && (
          <Link to={`/agent-runs/${finding.source_run_id}`} className="text-blue-600 hover:underline" aria-label={`פתח את ריצת הפיקוח שממנה נגזר הממצא: ${title}`}>
            ריצת הפיקוח
          </Link>
        )}
      </div>

      <GuardianEvidence finding={finding} />
    </li>
  );
}

export function GuardianWorkspace() {
  const audit = useQuery({ queryKey: ["guardian-workspace-audit"], queryFn: platformGuardianApi.status, staleTime: 60_000 });
  const summary = useQuery({ queryKey: ["guardian-workspace-summary"], queryFn: platformGuardianApi.findingsSummary, staleTime: 60_000 });
  const findings = useQuery({
    queryKey: ["guardian-workspace-findings"],
    queryFn: () => platformGuardianApi.findings({ sort_by: "severity" }),
    staleTime: 60_000,
  });
  const backup = useQuery({ queryKey: ["guardian-workspace-backup"], queryFn: backupGuardianApi.health, staleTime: 60_000 });

  const auditData = audit.data;
  // An unparseable last-run timestamp is unknown, not fresh: freshness may only be claimed from a
  // timestamp this client could actually read.
  const auditAge = auditData?.last_run_at ? ageHours(auditData.last_run_at) : null;
  const auditEvidenceState = auditAge == null ? "unknown" : auditAge > AUDIT_STALE_HOURS ? "stale" : "fresh";

  const findingList = Array.isArray(findings.data) ? findings.data : [];
  const unresolved = findingList.filter((f) => UNRESOLVED_FINDING_STATUSES.has(f.status));
  const orderedFindings = [...unresolved, ...findingList.filter((f) => !UNRESOLVED_FINDING_STATUSES.has(f.status))].slice(0, 10);

  const summaryData = summary.data;
  const backupData = backup.data;

  return (
    <section aria-label="סביבת Guardian" data-testid="guardian-workspace" className="bg-white rounded-xl border shadow-sm p-5 space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h2 className="font-semibold text-gray-700">סביבת Guardian · פיקוח פלטפורמה ואבטחה</h2>
        <span className="text-[11px] text-gray-400">מבוסס ראיות מערכת בלבד · לקריאה בלבד — המסך אינו מבצע פעולות ואינו משנה הגדרות</span>
      </div>

      {/* Audit freshness — when Guardian itself last ran. */}
      <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-3 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-700">סריקת הפיקוח</h3>
          {audit.isLoading && <span className="text-xs text-gray-400">טוען…</span>}
          {!audit.isLoading && (audit.isError || !auditData) && <GuardianStateChip state="unknown" testId="guardian-audit-state" />}
          {!audit.isLoading && !audit.isError && auditData && <GuardianStateChip state={auditEvidenceState} testId="guardian-audit-state" />}
          {!audit.isLoading && !audit.isError && auditData && (
            <span className="text-[11px] text-gray-400 mr-auto">
              {auditEvidenceState === "fresh" && "הנתונים מהסריקה האחרונה; סריקה תקינה מתבצעת מדי 24 שעות"}
              {auditEvidenceState === "stale" && `הסריקה האחרונה ישנה מ-${AUDIT_STALE_HOURS} שעות — ייתכן שהפיקוח הופסק`}
              {auditEvidenceState === "unknown" && "טרם בוצעה סריקת פיקוח מתועדת"}
            </span>
          )}
        </div>
        {audit.isError && <SourceUnknown error={audit.error} subject="סריקת הפיקוח" onRetry={() => audit.refetch()} />}
        {!audit.isLoading && !audit.isError && auditData && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-600">
            {auditData.last_run_at ? (
              <span>סריקה אחרונה: {fmt(auditData.last_run_at)} ({relAge(auditData.last_run_at) || "מועד לא זוהה"})</span>
            ) : (
              <span className="text-gray-400">טרם בוצעה סריקה מתועדת</span>
            )}
            {auditData.last_run_status && (
              <span>מצב הריצה: {runStatusLabel(auditData.last_run_status)}</span>
            )}
            {typeof auditData.agent_enabled === "boolean" && (
              <span className={auditData.agent_enabled ? "text-green-700" : "text-amber-700"}>
                {auditData.agent_enabled ? "העובד מופעל" : "העובד מושבת"}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Platform / security findings. */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-700">ממצאי אבטחה ופלטפורמה</h3>
          {summaryData && (
            <span className="text-[11px] text-gray-400">
              {summaryData.open_total} פתוחים מתוך {summaryData.total} ממצאים
              {summaryData.open_critical > 0 && <span className="text-red-700 font-medium"> · {summaryData.open_critical} קריטיים פתוחים</span>}
            </span>
          )}
          <span className="text-[11px] text-gray-400">מעודכן: נצפה בסריקה האחרונה · מיושן: לא נצפה מחדש מעל 3 ימים · לא ידוע: אין מידע</span>
        </div>

        {findings.isLoading && <p className="text-sm text-gray-400 italic">טוען ממצאים…</p>}
        {!findings.isLoading && findings.isError && (
          <SourceUnknown error={findings.error} subject="ממצאי הפיקוח" onRetry={() => findings.refetch()} />
        )}
        {!findings.isLoading && !findings.isError && findingList.length === 0 && (
          <p className="text-sm text-gray-400 italic">אין ממצאי אבטחה מתועדים</p>
        )}
        {orderedFindings.length > 0 && (
          <>
            <ol className="space-y-2">
              {orderedFindings.map((finding) => <GuardianFindingRow key={finding.id || finding.fingerprint} finding={finding} />)}
            </ol>
            {findingList.length > orderedFindings.length && (
              <p className="text-[11px] text-gray-400">מציג {orderedFindings.length} מתוך {findingList.length} ממצאים</p>
            )}
          </>
        )}
      </div>

      {/* Backup evidence. */}
      <div className="rounded-lg border border-gray-200 bg-gray-50/70 p-3 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="text-sm font-semibold text-gray-700">גיבויים</h3>
          {backup.isLoading && <span className="text-xs text-gray-400">טוען…</span>}
          {!backup.isLoading && (backup.isError || !backupData) && <GuardianStateChip state="unknown" testId="guardian-backup-state" />}
          {!backup.isLoading && !backup.isError && backupData && (
            <span
              data-testid="guardian-backup-state"
              className={`${CHIP_CLS} ${
                BACKUP_SEVERITY_CLS[backupData.severity] || BACKUP_SEVERITY_CLS.warning
              }`}
            >
              {BACKUP_SEVERITY_HE[backupData.severity] || backupData.severity}
            </span>
          )}
        </div>
        {backup.isError && <SourceUnknown error={backup.error} subject="מצב הגיבויים" onRetry={() => backup.refetch()} />}
        {!backup.isLoading && !backup.isError && backupData && (
          <>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-600">
              {backupData.last_backup ? (
                <span>גיבוי אחרון: {fmt(backupData.last_backup)} ({relAge(backupData.last_backup) || "מועד לא זוהה"})</span>
              ) : (
                <span className="text-red-700">לא נמצא גיבוי תקין</span>
              )}
              <span>
                {backupData.backup_count} קבצי גיבוי
                {Number.isFinite(backupData.max_age_hours) && ` · מגבלת גיל: ${backupData.max_age_hours} שעות`}
              </span>
              {backupData.checked_at && <span className="text-gray-400">נבדק: {fmt(backupData.checked_at)}</span>}
            </div>
            {Array.isArray(backupData.findings) && backupData.findings.length > 0 && (
              <ul className="text-xs text-gray-700 list-disc pr-4 space-y-0.5">
                {backupData.findings.map((line, i) => <li key={i}>{String(line).slice(0, 240)}</li>)}
              </ul>
            )}
            {backupData.severity === "ok" && (!backupData.findings || backupData.findings.length === 0) && (
              <p className="text-xs text-green-700">הגיבויים תקינים לפי הספים המוגדרים</p>
            )}
          </>
        )}
      </div>
    </section>
  );
}

// ─── Expert Pack catalog (DIGEMP-EXPERT-PACK-CATALOG-FRONTEND-032) ────────────
//
// The read-only catalog of professional domains (expert packs) this employee can be
// equipped for. Each domain card shows the approved/declared readiness at a glance and
// the four boundaries kept visually apart: what the employee may read, what it may
// recommend, what runs only with technician approval, and the conditions under which it
// must escalate to a human — plus the evidence sources with their freshness and approval
// state, and the known limits. Everything comes from the additive `expert_pack_catalog`
// contract, accepted (like the other additive contracts) on the workspace payload, its
// agent projection, or the page's agent detail. When the server has not supplied the
// catalog — or a field within it — the section says so explicitly and never invents
// domains, readiness, actions, sources or freshness. Only whitelisted scalar fields are
// rendered (bounded lengths, capped lists), so credential material stored next to a
// descriptor can not leak. Informational only: no plugin management, no permission or
// approval control, no execute control — actions keep going through the existing
// proposal → approval → action flow.

const EXPERT_DOMAIN_HE = {
  helpdesk: "Helpdesk", help_desk: "Helpdesk", service_desk: "Helpdesk",
  eset: "ESET PROTECT", eset_protect: "ESET PROTECT",
  acronis: "Acronis", acronis_backup: "Acronis",
  ...Object.fromEntries(M365_DOMAIN_TOKENS.map((token) => [token, "Microsoft 365 / Entra"])),
};

const EXPERT_STATE_META = {
  ready: { label: "מוכן", cls: "bg-green-100 text-green-700" },
  partial: { label: "מוכן חלקית", cls: "bg-amber-100 text-amber-800 border border-amber-200" },
  not_ready: { label: "לא מוכן", cls: "bg-red-100 text-red-700" },
  pending: { label: "טרם אושר", cls: "bg-slate-100 text-slate-600 border border-slate-200" },
  unknown: { label: "לא ידוע", cls: "bg-gray-100 text-gray-600" },
};

const EXPERT_READ_CHIP = { label: "קריאה בלבד", cls: "bg-blue-100 border-blue-200 text-blue-800" };
const EXPERT_RECOMMEND_CHIP = { label: "המלצה", cls: "bg-indigo-100 border-indigo-200 text-indigo-800" };
const EXPERT_APPROVAL_CHIP = { label: "אישור טכנאי", cls: "bg-orange-100 border-orange-200 text-orange-800" };
const EXPERT_STRONG_APPROVAL_CHIP = { label: "אישור מוגבר", cls: "bg-purple-100 border-purple-200 text-purple-800" };

const EXPERT_EVIDENCE_STATUS = {
  approved: { label: "מאושר", cls: "bg-green-100 border-green-200 text-green-700" },
  pending: { label: "טרם אושר", cls: "bg-amber-100 border-amber-200 text-amber-800" },
};

const EXPERT_ROOT_KEYS = ["expert_pack_catalog", "expert_packs", "specialist_packs", "specialist_pack_descriptors"];
const EXPERT_LIST_KEYS = ["packs", "descriptors", "pack_descriptors", "items"];
const EXPERT_VERSION_KEYS = ["version", "pack_version", "contract_version"];
const EXPERT_READ_KEYS = ["read", "read_actions", "read_only", "read_only_actions"];
const EXPERT_RECOMMEND_KEYS = ["recommend", "recommend_actions", "recommendations"];
const EXPERT_EXECUTE_KEYS = ["execute_with_approval", "execute_actions", "approval_actions", "approval_bound_actions", "write_actions", "execute"];
const EXPERT_ESCALATION_KEYS = ["escalation", "escalation_conditions", "escalate_when", "escalation_triggers", "handoff_conditions"];
const EXPERT_EVIDENCE_KEYS = ["evidence", "knowledge", "knowledge_provenance", "provenance", "sources", "knowledge_sources"];
const EXPERT_LIMITS_KEYS = ["limits", "known_limits", "constraints", "boundaries"];
const EXPERT_FRESHNESS_KEYS = ["last_verified", "verified_at", "last_updated", "updated_at", "as_of"];
const EXPERT_EVIDENCE_FRESHNESS_KEYS = ["updated_at", "last_updated", "last_verified", "verified_at", "checked_at", "as_of"];

function expertField(raw, keys) {
  if (!raw || typeof raw !== "object") return undefined;
  for (const key of keys) {
    if (raw[key] !== undefined && raw[key] !== null) return raw[key];
  }
  return undefined;
}

function expertToken(value) {
  return typeof value === "string" ? value.trim().toLowerCase().replace(/[\s-]+/g, "_") : "";
}

function expertLeaf(value, max) {
  if (typeof value === "number" && Number.isFinite(value)) return String(value).slice(0, max);
  return boundedText(value, max);
}

function expertText(item, keys, max = 160) {
  if (typeof item === "string" || typeof item === "number") return expertLeaf(item, max);
  if (!item || typeof item !== "object") return null;
  for (const key of keys) {
    const found = expertLeaf(item[key], max);
    if (found) return found;
  }
  return null;
}

function expertEntries(value) {
  if (Array.isArray(value)) return value;
  return typeof value === "string" ? [value] : [];
}

function expertDate(value) {
  if (typeof value !== "string") return null;
  const ms = Date.parse(value);
  if (Number.isNaN(ms)) return null;
  return { ms, text: fmt(value) || null };
}

function normalizeExpertState(value) {
  if (typeof value !== "string") return null;
  const v = expertToken(value);
  if (["ready", "available", "ok", "approved", "enabled", "active", "configured", "verified"].includes(v)) return "ready";
  if (["partial", "limited", "degraded", "onboarding", "provisioning"].includes(v)) return "partial";
  if (["not_ready", "blocked", "unavailable", "unconfigured", "not_configured", "disabled", "error", "failed"].includes(v)) return "not_ready";
  if (["pending", "proposed", "draft", "under_review", "unverified", "candidate"].includes(v)) return "pending";
  return "unknown";
}

function normalizeExpertActions(value, cap = 8) {
  return expertEntries(value)
    .map((entry) => {
      const label = expertText(entry, ["name", "action", "tool", "label", "title", "operation"], 60);
      if (!label) return null;
      const detail = expertText(entry, ["description", "detail", "note"], 120);
      const isObj = !!entry && typeof entry === "object";
      return {
        label,
        detail: detail && detail !== label ? detail : null,
        strong: isObj && entry.strong_approval_required === true,
      };
    })
    .filter(Boolean)
    .slice(0, cap);
}

function normalizeExpertNotes(value, keys, cap = 6) {
  return expertEntries(value)
    .map((entry) => expertText(entry, keys, 140))
    .filter(Boolean)
    .slice(0, cap);
}

function normalizeExpertEvidence(value, cap = 6) {
  return expertEntries(value)
    .map((entry) => {
      const source = expertText(entry, ["source", "name", "title", "vendor", "knowledge_base", "base"], 120);
      if (!source) return null;
      const version = expertText(entry, ["version", "revision", "version_label"], 40);
      const freshnessRaw = entry && typeof entry === "object" ? expertField(entry, EXPERT_EVIDENCE_FRESHNESS_KEYS) : undefined;
      const statusRaw = entry && typeof entry === "object"
        ? expertText(entry, ["status", "state", "verification", "review_status", "approval"], 40)
        : null;
      let status = { kind: "none", raw: null };
      if (statusRaw) {
        const token = expertToken(statusRaw);
        if (["approved", "verified", "current", "active", "ok", "published"].includes(token)) status = { kind: "approved", raw: statusRaw };
        else if (["pending", "proposed", "draft", "unverified", "candidate", "under_review"].includes(token)) status = { kind: "pending", raw: statusRaw };
        else status = { kind: "unknown", raw: statusRaw };
      }
      return { source, version, freshness: expertDate(freshnessRaw), status };
    })
    .filter(Boolean)
    .slice(0, cap);
}

/**
 * Normalizes one expert pack descriptor from the additive catalog contract. Returns null when the
 * entry carries no recognizable domain label. A per-field null/undefined means "the server did not
 * supply this field" (rendered as an explicit unknown), while `*Defined` empty arrays mean the
 * server explicitly reported "none".
 */
export function normalizeExpertPack(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const rawLabel = expertText(raw, ["domain", "domain_name", "domain_label", "subdomain", "name", "pack", "title", "specialty", "id"], 80);
  if (!rawLabel) return null;
  const stateRaw = expertField(raw, ["state", "status", "readiness", "evaluation_state"]);
  const readRaw = expertField(raw, EXPERT_READ_KEYS);
  const recommendRaw = expertField(raw, EXPERT_RECOMMEND_KEYS);
  const executeRaw = expertField(raw, EXPERT_EXECUTE_KEYS);
  const escalationRaw = expertField(raw, EXPERT_ESCALATION_KEYS);
  const evidenceRaw = expertField(raw, EXPERT_EVIDENCE_KEYS);
  const limitsRaw = expertField(raw, EXPERT_LIMITS_KEYS);
  const evidence = normalizeExpertEvidence(evidenceRaw);
  const packFresh = expertDate(expertField(raw, EXPERT_FRESHNESS_KEYS));
  const newestEvidenceMs = evidence.reduce(
    (max, entry) => (entry.freshness && entry.freshness.ms > max ? entry.freshness.ms : max),
    -Infinity,
  );
  const freshness = packFresh
    || (newestEvidenceMs > -Infinity ? { ms: newestEvidenceMs, text: fmt(new Date(newestEvidenceMs).toISOString()) || null } : null);
  return {
    label: EXPERT_DOMAIN_HE[expertToken(rawLabel)] || rawLabel,
    state: normalizeExpertState(stateRaw),
    stateRaw: typeof stateRaw === "string" ? boundedText(stateRaw, 40) : null,
    version: expertLeaf(expertField(raw, EXPERT_VERSION_KEYS), 40),
    freshness,
    read: normalizeExpertActions(readRaw),
    readDefined: readRaw !== undefined,
    recommend: normalizeExpertActions(recommendRaw),
    recommendDefined: recommendRaw !== undefined,
    execute: normalizeExpertActions(executeRaw),
    executeDefined: executeRaw !== undefined,
    escalation: normalizeExpertNotes(escalationRaw, ["condition", "text", "title", "trigger", "description"], 6),
    escalationDefined: escalationRaw !== undefined,
    evidence,
    evidenceDefined: evidenceRaw !== undefined,
    limits: normalizeExpertNotes(limitsRaw, ["limit", "text", "title", "description"], 6),
    limitsDefined: limitsRaw !== undefined,
  };
}

function ExpertChip({ meta }) {
  return <span className={`px-1.5 py-0.5 rounded border whitespace-nowrap ${meta.cls}`}>{meta.label}</span>;
}

function ExpertActionList({ items, chipOf, fallback }) {
  if (!items.length) return <p className="text-xs text-gray-500">{fallback}</p>;
  return (
    <ul className="space-y-1">
      {items.map((action, i) => (
        <li key={`${action.label}-${i}`} className="text-xs text-gray-700 space-y-0.5">
          <span className="inline-flex flex-wrap items-center gap-1.5">
            <span className="font-mono" dir="ltr">{action.label}</span>
            <ExpertChip meta={chipOf(action)} />
          </span>
          {action.detail && <div className="text-gray-500">{action.detail}</div>}
        </li>
      ))}
    </ul>
  );
}

function ExpertBlock({ tone, title, testId, children }) {
  return (
    <div data-testid={testId} className={`rounded-lg border p-2.5 space-y-1.5 ${tone}`}>
      <div className="text-xs font-medium text-gray-700">{title}</div>
      {children}
    </div>
  );
}

function ExpertPackCard({ pack, index }) {
  const stateMeta = pack.state ? EXPERT_STATE_META[pack.state] : null;
  return (
    <div data-testid={`expert-pack-${index}`} className="rounded-lg border border-gray-200 bg-white p-3 space-y-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-semibold text-gray-800">{pack.label}</h4>
        {stateMeta && (
          <span
            className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${stateMeta.cls}`}
            title={pack.state === "unknown" && pack.stateRaw ? `דווח על-ידי השרת: ${pack.stateRaw}` : undefined}
          >
            {stateMeta.label}
          </span>
        )}
        {pack.version && (
          <span className="text-[10px] text-gray-400">
            גרסת חבילה: <span className="font-mono" dir="ltr">{pack.version}</span>
          </span>
        )}
        <span
          data-testid="expert-pack-freshness"
          className={`text-[10px] px-1.5 py-0.5 rounded-full mr-auto ${pack.freshness ? "bg-gray-100 text-gray-500" : "bg-gray-100 text-gray-400 italic"}`}
        >
          טריות ראיות: {pack.freshness ? pack.freshness.text : "לא ידועה"}
        </span>
      </div>

      <ExpertBlock testId="expert-pack-read" tone="border-blue-100 bg-blue-50/40" title="מה העובד יכול לקרוא">
        <ExpertActionList
          items={pack.read}
          chipOf={() => EXPERT_READ_CHIP}
          fallback={pack.readDefined ? "לא הוגדרו פעולות קריאה לחבילה זו." : "לא ידוע — טרם דווחו פעולות קריאה מהשרת."}
        />
      </ExpertBlock>

      <ExpertBlock testId="expert-pack-recommend" tone="border-indigo-100 bg-indigo-50/40" title="מה העובד יכול להמליץ">
        <ExpertActionList
          items={pack.recommend}
          chipOf={() => EXPERT_RECOMMEND_CHIP}
          fallback={pack.recommendDefined ? "לא הוגדרו המלצות לחבילה זו." : "לא ידוע — טרם דווחו יכולות המלצה מהשרת."}
        />
      </ExpertBlock>

      <ExpertBlock testId="expert-pack-execute" tone="border-orange-200 bg-orange-50/40" title="ביצוע — רק באישור טכנאי">
        <ExpertActionList
          items={pack.execute}
          chipOf={(action) => (action.strong ? EXPERT_STRONG_APPROVAL_CHIP : EXPERT_APPROVAL_CHIP)}
          fallback={pack.executeDefined ? "לא הוגדרו פעולות ביצוע באישור לחבילה זו." : "לא ידוע — טרם דווחו פעולות ביצוע באישור מהשרת."}
        />
      </ExpertBlock>

      <ExpertBlock testId="expert-pack-escalate" tone="border-rose-100 bg-rose-50/40" title="מתי מוסלים לטכנאי">
        {pack.escalation.length > 0 ? (
          <>
            <p className="text-[11px] text-rose-700">תנאים שבהם העבודה עוברת להמשך טיפול אנושי:</p>
            <ul className="list-disc pr-4 text-xs text-gray-700 space-y-0.5">
              {pack.escalation.map((condition, i) => <li key={i}>{condition}</li>)}
            </ul>
          </>
        ) : (
          <p className="text-xs text-gray-500">
            {pack.escalationDefined ? "לא דווחו תנאי הסלמה לחבילה זו." : "לא ידוע — טרם דווחו תנאי הסלמה מהשרת."}
          </p>
        )}
      </ExpertBlock>

      <ExpertBlock testId="expert-pack-evidence" tone="border-gray-100 bg-gray-50/60" title="מקורות ראיות וטריות">
        {pack.evidence.length > 0 ? (
          <ul className="space-y-1">
            {pack.evidence.map((entry, i) => (
              <li key={`${entry.source}-${i}`} className="text-xs text-gray-700 flex flex-wrap items-center gap-1.5">
                <span>{entry.source}</span>
                {entry.version && <span className="text-gray-400">· גרסה {entry.version}</span>}
                {entry.freshness
                  ? <span className="text-gray-400">· עודכן: {entry.freshness.text}</span>
                  : <span className="text-gray-400 italic">· טריות לא ידועה</span>}
                {entry.status.kind === "approved" && <ExpertChip meta={EXPERT_EVIDENCE_STATUS.approved} />}
                {entry.status.kind === "pending" && <ExpertChip meta={EXPERT_EVIDENCE_STATUS.pending} />}
                {entry.status.kind === "unknown" && entry.status.raw && (
                  <ExpertChip meta={{ label: entry.status.raw, cls: "bg-gray-100 border-gray-200 text-gray-500" }} />
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-gray-500">
            {pack.evidenceDefined
              ? "השרת דיווח שלא דווחו מקורות ראיות."
              : "טריות הראיות אינה ידועה — מקורות ראיות טרם דווחו על-ידי השרת."}
          </p>
        )}
      </ExpertBlock>

      <ExpertBlock testId="expert-pack-limits" tone="border-gray-100 bg-gray-50/60" title="מגבלות ידועות בחבילה">
        {pack.limits.length > 0 ? (
          <ul className="list-disc pr-4 text-xs text-gray-700 space-y-0.5">
            {pack.limits.map((limit, i) => <li key={i}>{limit}</li>)}
          </ul>
        ) : (
          <p className="text-xs text-gray-500">
            {pack.limitsDefined ? "לא דווחו מגבלות ידועות." : "לא ידוע — מגבלות ידועות טרם דווחו על-ידי השרת."}
          </p>
        )}
      </ExpertBlock>
    </div>
  );
}

export function ExpertPacksSection({ workspace, agentDetail }) {
  const pickRoot = (source) => {
    if (!source || typeof source !== "object") return null;
    for (const key of EXPERT_ROOT_KEYS) {
      if (source[key] !== undefined && source[key] !== null) return source[key];
    }
    return null;
  };
  const raw = pickRoot(workspace) ?? pickRoot(workspace?.agent) ?? pickRoot(agentDetail) ?? null;
  const root = raw && typeof raw === "object" ? raw : null;
  const listRaw = Array.isArray(root) ? root : root ? expertField(root, EXPERT_LIST_KEYS) : undefined;
  const list = Array.isArray(listRaw) ? listRaw : null;
  const packs = (list || []).map(normalizeExpertPack).filter(Boolean).slice(0, 8);

  const meta = (keys) => (root && !Array.isArray(root) ? expertField(root, keys) : undefined);
  const version = expertLeaf(meta(EXPERT_VERSION_KEYS), 40);
  const generatedAt = expertDate(meta(["generated_at", "as_of", "updated_at", "last_verified"]));

  const explicitlyEmpty = list !== null && list.length === 0;
  const unusable = list !== null && list.length > 0 && packs.length === 0;

  return (
    <section
      aria-label="קטלוג חבילות מומחה"
      data-testid="expert-packs-section"
      className="bg-white rounded-xl border shadow-sm p-4 space-y-3"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700">קטלוג חבילות מומחה</h3>
        {root && <span className="text-[11px] text-gray-400">גרסת קטלוג: {version || "לא ידועה"}</span>}
        {generatedAt && <span className="text-[11px] text-gray-400">· עודכן: {generatedAt.text}</span>}
        <span className="text-[11px] text-gray-400 mr-auto">תחומים: Helpdesk · ESET · Acronis · Microsoft 365 / Entra</span>
      </div>
      <p className="text-[11px] text-gray-400">
        לכל חבילה: מה העובד יכול לקרוא, להמליץ, לבצע רק באישור טכנאי — ומתי עליו להסלים לטכנאי. הקטע אינו מעניק הרשאות ואינו מוסיף אמצעי ביצוע.
      </p>

      {packs.length > 0 && (
        <div data-testid="expert-pack-summary" className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-gray-600 ml-1">מוכנות לפי תחום:</span>
          {packs.map((pack, i) => {
            const stateMeta = pack.state ? EXPERT_STATE_META[pack.state] : null;
            return (
              <span key={`${pack.label}-${i}`} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50 text-gray-700">
                {pack.label}
                {stateMeta && <span className={`px-1.5 rounded-full ${stateMeta.cls}`}>{stateMeta.label}</span>}
              </span>
            );
          })}
        </div>
      )}

      {packs.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {packs.map((pack, i) => <ExpertPackCard key={`${pack.label}-${i}`} pack={pack} index={i} />)}
        </div>
      )}

      {packs.length === 0 && explicitlyEmpty && (
        <p className="text-sm text-gray-500">השרת דיווח שאין חבילות מומחה בקטלוג לעובד זה.</p>
      )}
      {packs.length === 0 && unusable && (
        <p className="text-sm text-gray-500">הקטלוג נמסר על-ידי השרת אך לא זוהו בו חבילות מומחה תקפות (לא ידוע).</p>
      )}
      {packs.length === 0 && !explicitlyEmpty && !unusable && (
        <p className="text-sm text-gray-500">
          קטלוג חבילות המומחה טרם נמסר על-ידי השרת — לא מוצגות כאן יכולות, מקורות ראיות, מגבלות או תנאי הסלמה ללא נתונים מגבים.
        </p>
      )}

      <p className="text-[11px] text-gray-400">
        תצוגת מידע בלבד — הקטע אינו מאשר פעולות ואינו מעניק הרשאות; כל ביצוע ממשיך לעבור במסלול ההצעה, האישור והביצוע הקיים.
      </p>
    </section>
  );
}

// ─── Explicit browser work request (DIGEMP-BROWSER-WORK-REQUEST-FRONTEND-022) ─────────────────
//
// The operator asks THIS employee to do browser work against ONE exact ticket, one server-allowed
// system and an objective (POST /api/agents/:id/browser-work-requests, BACKEND-022). mode:"browser"
// is an explicit opt-in and this flow never falls back to the API analysis path. The panel renders
// only what the server returns — the receipt carries identifiers and the *redacted* objective, so
// no credential, token or raw browser secret is ever rendered. The panel grants nothing: the entry
// point is shown only to holders of agents_run (the grant the backend requires), approval stays
// with agents_approve and tools stay under the Tool Registry default-deny.
//
// Integrated onto the evolved canonical workspace: the grant check reuses the component's own
// canRun (useAgentPermissions), not a page-passed prop, and the root carries a data-testid because
// E2E-CLOSEOUT-095's per-ticket control shares the same button label.

// The recorded execution-boundary constants (app.services.browser_work_executor): the live write
// arm is unavailable by code in every configuration and a state-changing action has no automated
// rollback. Mirrored for display only — rendered as limitations, never as a capability or a switch.
const BROWSER_WRITE_BOUNDARY_HE = "כתיבה מול יעד אמיתי: חסומה בכל תצורה — אין מסלול כתיבה חי.";
const BROWSER_ROLLBACK_BOUNDARY_HE = "שחזור אוטומטי: לא קיים — היפוך שינוי (אם יידרש) הוא ידני בלבד.";
const BROWSER_REASON_LIVE_WRITE_ARM_UNAVAILABLE = "live_browser_write_arm_unavailable";
const BROWSER_REASON_POSTCONDITION_MISMATCH = "postcondition_read_back_mismatch";
const BROWSER_VERIFICATION_POSTCONDITION_READ_BACK = "postcondition_read_back";
const BROWSER_VERIFICATION_PROVIDER_RUN = "provider_run_success";

const isWriteBoundedAction = (receipt) => Boolean(receipt?.bounded_action)
  && (receipt.bounded_action.operation === "write"
    || receipt.bounded_action.kind === "create_record"
    || receipt.bounded_action.kind === "update_record");

const BROWSER_REQUEST_STATE_HE = {
  recorded: "נרשמה",
};

function BrowserWorkReceipt({ receipt, onReset }) {
  const { execution } = receipt;
  const blockedLabel = browserCapabilityBlockedLabel(execution.blocked_reason);
  // `execution.available` is live-read-only usability. A state-changing bounded action has no live
  // arm in any configuration, so an available capability never renders as write readiness.
  const writeBoundedAction = isWriteBoundedAction(receipt);
  return (
    <div role="status" className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
          {BROWSER_REQUEST_STATE_HE[receipt.state] || receipt.state}
        </span>
        <span className="text-xs text-gray-500">
          מספר בקשה: <span dir="ltr" className="font-mono">{receipt.request_id}</span>
        </span>
        <span className="text-xs text-gray-500">
          מצב: <span dir="ltr" className="font-mono">{receipt.mode}</span>
        </span>
      </div>

      <ol className="text-xs text-gray-700 space-y-1">
        <li>✓ הבקשה נשלחה ואוחזרה · {fmt(receipt.requested_at)}</li>
        <li>✓ נרשמה בהיסטוריית הקריאה ובביקורת האבטחה (state: <span dir="ltr" className="font-mono">{receipt.state}</span>)</li>
        <li>
          {writeBoundedAction
            ? <span className="text-red-700">⛔ כתיבה מול יעד אמיתי: חסומה בכל תצורה — אין מסלול כתיבה חי{execution.available ? " (מסלול קריאה בלבד זמין)" : ""}.</span>
            : execution.available
              ? "▶ ביצוע קריאה חיה (קריאה בלבד) זמין"
              : <span className="text-red-700">⛔ ביצוע חסום — {blockedLabel}</span>}
          {" · אין נפילה חזרה ל-API (fallback: "}<span dir="ltr" className="font-mono">{execution.fallback}</span>{", api_fallback: "}<span dir="ltr" className="font-mono">{String(receipt.api_fallback)}</span>{")"}
        </li>
      </ol>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-700">
        <div>עובד: <span className="font-medium">{receipt.employee.name}</span> ({receipt.employee.permission_tier})</div>
        <div>קריאה: #{receipt.ticket.ticket_number}</div>
        <div>לקוח (נגזר בשרת): {receipt.customer.name}</div>
        <div>מערכת: <span dir="ltr" className="font-mono">{receipt.allowed_system}</span></div>
        <div>מבקש: {receipt.requested_by}</div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 text-xs">
        <div className="font-medium text-gray-600 mb-0.5">מטרה שנרשמה (אחרי הסרת סודות בשרת)</div>
        <div className="text-gray-700 whitespace-pre-wrap break-words">{receipt.objective.text}</div>
        {receipt.objective.redactions > 0 && (
          <div className="text-amber-700 mt-1">הוסרו {receipt.objective.redactions} מקטעים שנראו כסוד לפני השמירה.</div>
        )}
      </div>

      {receipt.bounded_action && (
        <div data-testid="browser-work-bounded-action" className="rounded-lg border border-gray-200 bg-gray-50 p-2 text-xs text-gray-700">
          פעולה מוגבלת: <span className="font-medium">{BROWSER_ACTION_KIND_HE[receipt.bounded_action.kind] || receipt.bounded_action.kind}</span>
          {" · "}יעד: <span dir="ltr" className="font-mono">{receipt.bounded_action.target_selector}</span>
          {receipt.login_required && " · נדרשת התחברות אנושית ליעד (האישורים יימסרו ידנית בזמן הביצוע)"}
        </div>
      )}

      <div className="rounded-lg border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
        אישור אנושי ({execution.approval_permission}) נדרש לפני כל ביצוע עתידי · מדיניות כלים: <span dir="ltr">{execution.tool_policy}</span>
      </div>

      <button
        type="button"
        onClick={onReset}
        className="text-xs px-3 py-1.5 rounded border hover:bg-gray-50"
      >
        בקש בקשה נוספת
      </button>
    </div>
  );
}

// ─── Bounded fallback execution flow (DIGEMP-BROWSER-FALLBACK-EXEC-124) ──────────────────────
//
// After the recorded request, the operator drives the bounded session through the EXISTING human
// approval policy: approve (agents_approve) → start → optional credential handoff → resume. The
// handoff form is the only place a credential value is ever typed; the values are cleared from
// component state the moment they are submitted and are never rendered back — the receipts carry
// identifiers and sanitized credential *lifecycle* facts only.

const BROWSER_ACTION_KIND_HE = {
  read_only_inspection: "בדיקת קריאה בלבד",
  create_record: "יצירת רשומה",
  update_record: "עדכון רשומה",
};

function BrowserWorkExecutionResult({ state, execution }) {
  const credential = state.credential || {};
  const verificationOk = state.success === true;
  // Verification kind decides what the success actually proves: a postcondition read-back proves a
  // state-changing action landed on the target; a provider run only proves the read path answered.
  // An unreported kind is rendered as unknown — never assumed to be the harmless one.
  const verifiedWrite = verificationOk && state.verification === BROWSER_VERIFICATION_POSTCONDITION_READ_BACK;
  const verifiedRead = verificationOk && state.verification === BROWSER_VERIFICATION_PROVIDER_RUN;
  const verifiedUnknown = verificationOk && !verifiedWrite && !verifiedRead;
  const verificationFailed = state.success === false;
  const writeBlocked = state.blocked_reason === BROWSER_REASON_LIVE_WRITE_ARM_UNAVAILABLE;
  const postconditionMismatch = state.blocked_reason === BROWSER_REASON_POSTCONDITION_MISMATCH;
  return (
    <div data-testid="browser-work-execution-result" className="rounded-lg border border-gray-200 bg-gray-50 p-2.5 space-y-1.5 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`px-2 py-0.5 rounded-full border font-medium ${
          verificationOk
            ? "bg-green-100 text-green-700 border-green-200"
            : "bg-gray-100 text-gray-700 border-gray-200"
        }`}>
          {browserExecutionStateLabel(state.state)}
        </span>
        {verificationFailed && (
          <span className="px-2 py-0.5 rounded-full border bg-red-100 text-red-700 border-red-200">
            ✗ האימות לא עבר
          </span>
        )}
        {state.verification && (
          <span className="text-gray-500">
            אימות: <span dir="ltr" className="font-mono">{state.verification}</span>
          </span>
        )}
      </div>
      {verifiedWrite && (
        <div data-testid="browser-work-verified-write" className="text-green-700">
          הצלחה מאושרת — פעולה משנה־מצב: השינוי אומת בקריאה חוזרת מהיעד. {BROWSER_ROLLBACK_BOUNDARY_HE}
        </div>
      )}
      {verifiedRead && (
        <div data-testid="browser-work-verified-read" className="text-green-700">
          הצלחה מאושרת — קריאה בלבד: בדיקת הקריאה אומתה בהרצת הספק; לא בוצע שינוי ביעד.
        </div>
      )}
      {verifiedUnknown && (
        <div data-testid="browser-work-verified-unknown" className="text-green-700">
          ההצלחה מדווחת, אך סוג האימות לא דווח — לא ניתן לקבוע אם בוצע שינוי ביעד. {BROWSER_ROLLBACK_BOUNDARY_HE}
        </div>
      )}
      {verificationFailed && (
        <div data-testid="browser-work-verification-failed" className="text-red-700">
          {postconditionMismatch
            ? <>האימות לא עבר: הקריאה החוזרת לא תאמה את הפעולה המאושרת לאחר ביצועה — ייתכן שהשינוי בוצע ואינו מאומת. יש לאמת ידנית מול הראיות. {BROWSER_ROLLBACK_BOUNDARY_HE}</>
            : <>האימות לא עבר — הסשן הסתיים ללא אימות הצלחה, ודבר אינו מדווח כמוצלח. {BROWSER_ROLLBACK_BOUNDARY_HE}</>}
        </div>
      )}
      {writeBlocked && (
        <div data-testid="browser-work-write-blocked" className="text-red-700">
          כתיבה חסומה — אין מסלול כתיבה חי; הפעולה נעצרה לפני כל מגע עם היעד ולא בוצע שינוי.
        </div>
      )}
      {state.blocked_reason && (
        <div className="text-red-700">סיבת חסימה/כשלון: {browserExecutionReasonLabel(state.blocked_reason)}</div>
      )}
      <div className="text-gray-600">
        אישורים: נמסרו לסשן {credential.received ? "כן" : "לא"} · נצרכו פעם אחת {credential.consumed ? "כן" : "לא"} · נמחקו בסיום הסשן {Number(credential.purged || 0)}
      </div>
      {Array.isArray(state.steps) && state.steps.length > 0 && (
        <ol className="list-decimal pr-4 text-gray-700 space-y-0.5">
          {state.steps.map((step, index) => <li key={index}>{step}</li>)}
        </ol>
      )}
      {Array.isArray(state.evidence) && state.evidence.length > 0 && (
        <ul data-testid="browser-work-execution-evidence" className="list-disc pr-4 text-gray-700 space-y-0.5">
          {state.evidence.map((item, index) => (
            <li key={index}>
              <span dir="ltr" className="font-mono text-[11px] text-gray-400">{item.kind}</span>{" "}
              {item.summary}
            </li>
          ))}
        </ul>
      )}
      <div className="text-[11px] text-gray-400">
        הראיות מסוננות בשרת; ערכי אישורים אינם מוצגים באף שלב.
        {execution && execution.backend ? <> מנוע ביצוע: <span dir="ltr" className="font-mono">{execution.backend}</span></> : null}
      </div>
    </div>
  );
}

function BrowserWorkExecutionFlow({ agentId, receipt }) {
  const { can: canAgent } = useAgentPermissions();
  const canApprove = canAgent("agents_approve");
  const [approval, setApproval] = useState(null);
  const [execution, setExecution] = useState(null);
  const [deposited, setDeposited] = useState(false);
  const [error, setError] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");

  const base = `/api/agents/${agentId}/browser-work-requests/${receipt.request_id}`;
  const ticketParams = { params: { ticket_id: receipt.ticket.id } };

  const onFailure = (fallback) => (e) =>
    setError(detailText(e?.response?.data?.detail, fallback));

  const approveMutation = useMutation({
    mutationFn: () => api.post(`${base}/approval`, null, ticketParams).then((r) => r.data),
    onSuccess: (data) => { setApproval(data); setError(null); },
    onError: onFailure("אישור הבקשה נכשל"),
  });
  const startMutation = useMutation({
    mutationFn: () => api.post(
      `${base}/execution`,
      { approval_token_id: approval.approval_token_id },
      ticketParams,
    ).then((r) => r.data),
    onSuccess: (data) => { setExecution(data); setError(null); },
    onError: onFailure("התחלת הביצוע נכשלה"),
  });
  const depositMutation = useMutation({
    mutationFn: (values) => api.post(
      `${base}/credential`, { kind: "login", ...values }, ticketParams,
    ).then((r) => r.data),
    onSuccess: () => { setDeposited(true); setError(null); },
    onError: onFailure("מסירת האישורים נכשלה"),
  });
  const resumeMutation = useMutation({
    mutationFn: () => api.post(`${base}/resume`, null, ticketParams).then((r) => r.data),
    onSuccess: (data) => { setExecution(data); setError(null); },
    onError: onFailure("המשך הביצוע נכשל"),
  });
  const refreshMutation = useMutation({
    mutationFn: () => api.get(`${base}/execution`, ticketParams).then((r) => r.data),
    onSuccess: (data) => { setExecution(data); setError(null); },
    onError: onFailure("רענון המצב נכשל"),
  });

  if (!receipt.execution.available) {
    // The receipt already carries the recorded blocked verdict; execution controls would only
    // repeat it as a disabled button.
    return null;
  }

  const state = execution?.state || null;
  const waiting = state?.state === "waiting_for_credential";
  const settle = state ? (BROWSER_EXECUTION_TERMINAL_STATES.includes(state.state) ? state.state : null) : null;

  const handleDeposit = (event) => {
    event.preventDefault();
    const values = { username: username.trim(), password };
    if (!values.username || !values.password || depositMutation.isPending) return;
    // Clear the typed values before the request leaves: they exist in this closure only, and the
    // component keeps no copy to re-render, log or re-send.
    setUsername("");
    setPassword("");
    depositMutation.mutate(values);
  };

  return (
    <div data-testid="browser-work-execution-flow" className="rounded-lg border border-indigo-200 bg-indigo-50/40 p-2.5 space-y-2 text-xs">
      <div className="font-medium text-gray-700">
        מסלול ביצוע מאושר — אישור אנושי, מסירת אישורים ידנית ובקרת אימות
      </div>
      <p className="text-[11px] text-gray-500">
        הבקשה מחייבת אישור אנושי ({receipt.execution.approval_permission}) לפני כל ביצוע. הסוכן אינו
        מאשר לעצמו, הביצוע כבול לפעולה ולמטרה שאושרו, והצלחה מדווחת רק לאחר אימות. פעולה משנה־מצב
        (כתיבה) מול יעד אמיתי אינה זמינה ואין לה שחזור אוטומטי — היפוך שינוי, אם יידרש, הוא ידני בלבד.
      </p>

      {!canApprove && (
        <p className="text-amber-700">
          אישור והרצה של הבקשה דורשים הרשאת {receipt.execution.approval_permission}.
        </p>
      )}

      {canApprove && !approval && (
        <button
          type="button"
          data-testid="browser-work-approve"
          onClick={() => approveMutation.mutate()}
          disabled={approveMutation.isPending}
          className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
        >
          {approveMutation.isPending ? "מאשר…" : "אשר בקשה"}
        </button>
      )}

      {approval && (
        <div data-testid="browser-work-approval" className="text-gray-700">
          הבקשה אושרה על ידי <span className="font-medium">{approval.approved_by}</span>
          {" · "}האישור אינו מקנה הרצה מעצמו ({String(approval.grants_execution_authority)}).
        </div>
      )}

      {approval && !execution && (
        <button
          type="button"
          data-testid="browser-work-start"
          onClick={() => startMutation.mutate()}
          disabled={startMutation.isPending}
          className="text-xs px-3 py-1.5 rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
        >
          {startMutation.isPending ? "מתחיל…" : "התחל ביצוע"}
        </button>
      )}

      {waiting && (
        <form
          data-testid="browser-work-credential-form"
          onSubmit={handleDeposit}
          className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 space-y-2"
        >
          <div className="text-amber-900 font-medium">
            הסשן ממתין לאישורי התחברות ליעד — המסירה ידנית ומפורשת
          </div>
          <p className="text-[11px] text-amber-900">
            האישורים נמסרים פעם אחת לסשן המזוהה בלבד, נצרכים פעם אחת ונמחקים בתום הסשן או בכשלון.
            הערכים אינם נרשמים בכרטיס, ביומן, בביקורת או בבסיס הנתונים ואינם מוצגים חזרה.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input
              data-testid="browser-work-credential-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="off"
              dir="ltr"
              placeholder="שם משתמש"
              className="text-sm border rounded px-2 py-1.5 bg-white"
            />
            <input
              data-testid="browser-work-credential-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="off"
              dir="ltr"
              placeholder="סיסמה"
              className="text-sm border rounded px-2 py-1.5 bg-white"
            />
          </div>
          <button
            type="submit"
            data-testid="browser-work-credential-submit"
            disabled={!username.trim() || !password || depositMutation.isPending}
            className="text-xs px-3 py-1.5 rounded bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
          >
            {depositMutation.isPending ? "מוסר…" : "מסור אישורים לסשן"}
          </button>
        </form>
      )}

      {waiting && deposited && (
        <div className="space-y-2">
          <div data-testid="browser-work-credential-deposited" className="text-green-700">
            האישורים נמסרו לסשן המזוהה בלבד; השדות נוקו והערכים אינם נשמרים בשום מקום.
          </div>
          <button
            type="button"
            data-testid="browser-work-resume"
            onClick={() => resumeMutation.mutate()}
            disabled={resumeMutation.isPending}
            className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {resumeMutation.isPending ? "ממשיך…" : "המשך ביצוע"}
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="browser-work-refresh"
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending}
          className="text-[11px] px-2 py-1 rounded border hover:bg-white disabled:opacity-50"
        >
          רענן מצב מהשרת
        </button>
        {settle && (
          <span className="text-[11px] text-gray-400">
            הסשן הסתיים במצב <span dir="ltr" className="font-mono">{settle}</span> — לא ניתן להריץ שוב את אותה בקשה.
          </span>
        )}
      </div>

      {execution && <BrowserWorkExecutionResult state={execution.state} execution={execution.execution} />}

      {error && (
        <p role="alert" className="text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {error}
        </p>
      )}
    </div>
  );
}

function BrowserWorkRequestSection({ agentId, agentName, tickets }) {
  const [open, setOpen] = useState(false);
  const [browserMode, setBrowserMode] = useState(false);
  const [ticketId, setTicketId] = useState("");
  const [system, setSystem] = useState("");
  const [objective, setObjective] = useState("");
  // The optional bounded action (DIGEMP-BROWSER-FALLBACK-EXEC-124): without one the request stays
  // the recorded-only 022 request; with one the exact action is bound server-side and becomes the
  // only action the session can ever run. No customer field (derived server-side) and no
  // credential field exists here — credentials are handed over only in the execution handoff.
  const [actionKind, setActionKind] = useState("none");
  const [actionTarget, setActionTarget] = useState("");
  const [actionParamField, setActionParamField] = useState("");
  const [actionParamValue, setActionParamValue] = useState("");
  const [loginRequired, setLoginRequired] = useState(false);
  const [receipt, setReceipt] = useState(null);

  // The allowed systems and the execution boundary come from the server (/api/connections/health
  // > browser_worker), not from a client-side copy of the policy — a failed load disables the form
  // rather than falling back to a guess.
  const capabilityQuery = useQuery({
    queryKey: ["browser-worker-capability"],
    queryFn: async () => (await connectionsApi.health())?.browser_worker || null,
    enabled: open,
    retry: false,
    staleTime: 60000,
  });
  const capability = capabilityQuery.data || null;
  const allowedSystems = capability?.allowed_systems || [];

  const submitMutation = useMutation({
    mutationFn: (payload) => api.post(`/api/agents/${agentId}/browser-work-requests`, payload).then((r) => r.data),
    onSuccess: (data) => setReceipt(data),
  });

  // Conversation-only tasks appear in assigned_tickets with ticket_id null; a request must bind to
  // a real ticket (the backend derives the customer from it), so those rows are not selectable.
  const selectableTickets = tickets.filter((t) => t.ticket_id);

  const trimmedObjective = objective.trim();
  const actionReady =
    actionKind === "none" ||
    (Boolean(actionTarget.trim()) &&
      (actionKind !== "update_record" ||
        (Boolean(actionParamField.trim()) && Boolean(actionParamValue.trim()))));
  const canSubmit = Boolean(
    browserMode && ticketId && trimmedObjective && allowedSystems.includes(system) && actionReady && !submitMutation.isPending
  );

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSubmit) return;
    // No customer field: the customer is derived server-side from the ticket. No credential field
    // exists anywhere in this form — credentials are handed over only in the execution handoff.
    const payload = {
      mode: "browser",
      ticket_id: ticketId,
      objective: trimmedObjective,
      allowed_system: system,
    };
    if (actionKind !== "none") {
      payload.action = {
        kind: actionKind,
        target_selector: actionTarget.trim(),
        parameters:
          actionKind === "update_record"
            ? { field: actionParamField.trim(), value: actionParamValue }
            : {},
      };
      payload.login_required = actionKind === "update_record" ? loginRequired : false;
    }
    submitMutation.mutate(payload);
  };

  const resetRequest = () => {
    setReceipt(null);
    setBrowserMode(false);
    setObjective("");
    setActionKind("none");
    setActionTarget("");
    setActionParamField("");
    setActionParamValue("");
    setLoginRequired(false);
    submitMutation.reset();
  };

  return (
    <div data-testid="browser-work-section" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700">עבודת דפדפן מפורשת</h3>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">
          מצב browser
        </span>
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-xs px-3 py-1.5 rounded border hover:bg-gray-50"
        >
          {open ? "סגור בקשה" : "🌐 בקש עבודת דפדפן"}
        </button>
      </div>

      {open && receipt && <BrowserWorkReceipt receipt={receipt} onReset={resetRequest} />}

      {open && receipt && <BrowserWorkExecutionFlow agentId={agentId} receipt={receipt} />}

      {open && !receipt && (
        <form onSubmit={handleSubmit} className="space-y-3">
          <fieldset disabled={submitMutation.isPending} className="space-y-3">
            <p className="text-xs text-gray-500">
              הבקשה משויכת לעובד <span className="font-medium text-gray-700">{agentName}</span> ולקריאה אחת מדויקת.
            </p>

            {selectableTickets.length === 0 ? (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                אין קריאות פעילות המשויכות לעובד — יש קריאה פעילה כדי לשייך בקשה מדויקת.
              </p>
            ) : (
              <>
                <div>
                  <label htmlFor="browser-work-ticket" className="block text-xs font-medium text-gray-600 mb-0.5">
                    קריאה מדויקת
                  </label>
                  <select
                    id="browser-work-ticket"
                    value={ticketId}
                    onChange={(e) => setTicketId(e.target.value)}
                    className="w-full text-sm border rounded px-2 py-1.5 bg-white"
                  >
                    <option value="">בחר קריאה…</option>
                    {selectableTickets.map((t) => (
                      <option key={t.ticket_id} value={t.ticket_id}>
                        {t.ticket_number ? `#${t.ticket_number} · ` : ""}{t.ticket_title || "קריאה"}
                      </option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    הלקוח נקבע בשרת לפי הקריאה ואינו נשלח מהדפדפן; הוא יוצג בקבלה.
                  </p>
                </div>

                <div>
                  <label htmlFor="browser-work-system" className="block text-xs font-medium text-gray-600 mb-0.5">
                    מערכת מורשית
                  </label>
                  <select
                    id="browser-work-system"
                    value={system}
                    onChange={(e) => setSystem(e.target.value)}
                    className="w-full text-sm border rounded px-2 py-1.5 bg-white"
                    dir="ltr"
                  >
                    <option value="">—</option>
                    {allowedSystems.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {capabilityQuery.isError
                      ? "לא ניתן לטעון את רשימת המערכות המורשות מהשרת — השליחה מושבתת."
                      : "רשימת המערכות המורשות נקבעת בשרת."}
                  </p>
                </div>

                <fieldset>
                  <legend className="text-xs font-medium text-gray-600 mb-0.5">מצב הבקשה (בחירה מפורשת)</legend>
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="radio"
                      name="browser-work-mode"
                      checked={browserMode}
                      onChange={() => setBrowserMode(true)}
                      className="mt-0.5"
                    />
                    <span>
                      בקשת עבודת דפדפן — מצב browser
                      <span className="block text-[11px] text-gray-400">
                        אין ברירת מחדל ואין מעבר אוטומטי: בקשה זו מפורשת למצב דפדפן ואינה נופלת חזרה למסלול ה-API.
                      </span>
                    </span>
                  </label>
                </fieldset>

                <fieldset className="space-y-2">
                  <legend className="text-xs font-medium text-gray-600 mb-0.5">
                    פעולה מוגבלת (אופציונלית — נדרשת כדי לאשר ולהוציא לפועל)
                  </legend>
                  <select
                    data-testid="browser-work-action-kind"
                    value={actionKind}
                    onChange={(e) => setActionKind(e.target.value)}
                    className="w-full text-sm border rounded px-2 py-1.5 bg-white"
                  >
                    <option value="none">ללא פעולה מוגבלת — הבקשה נרשמת בלבד</option>
                    <option value="read_only_inspection">בדיקת קריאה בלבד</option>
                    <option value="update_record">עדכון רשומה</option>
                  </select>
                  {actionKind === "update_record" && (
                    <p data-testid="browser-work-write-boundary" className="text-[11px] text-amber-700">
                      פעולה משנה־מצב: הבקשה נרשמת בלבד. {BROWSER_WRITE_BOUNDARY_HE} אין נפילה חזרה
                      למסלול ה-API. {BROWSER_ROLLBACK_BOUNDARY_HE}
                    </p>
                  )}
                  {actionKind !== "none" && (
                    <div className="space-y-2">
                      <div>
                        <label htmlFor="browser-work-action-target" className="block text-xs font-medium text-gray-600 mb-0.5">
                          מזהה היעד (רשומה מדויקת)
                        </label>
                        <input
                          id="browser-work-action-target"
                          data-testid="browser-work-action-target"
                          type="text"
                          value={actionTarget}
                          onChange={(e) => setActionTarget(e.target.value)}
                          maxLength={128}
                          dir="ltr"
                          className="w-full text-sm border rounded px-2 py-1.5"
                          placeholder="record-id"
                        />
                        <p className="text-[11px] text-gray-400 mt-0.5">
                          מזהה חשבון/רשומה — אין כתובות URL ואין פקודות; הפעולה כבולה ליעד אחד מדויק.
                        </p>
                      </div>
                      {actionKind === "update_record" && (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            <div>
                              <label htmlFor="browser-work-action-field" className="block text-xs font-medium text-gray-600 mb-0.5">
                                שדה
                              </label>
                              <input
                                id="browser-work-action-field"
                                data-testid="browser-work-action-field"
                                type="text"
                                value={actionParamField}
                                onChange={(e) => setActionParamField(e.target.value)}
                                maxLength={64}
                                dir="ltr"
                                className="w-full text-sm border rounded px-2 py-1.5"
                                placeholder="status"
                              />
                            </div>
                            <div>
                              <label htmlFor="browser-work-action-value" className="block text-xs font-medium text-gray-600 mb-0.5">
                                ערך חדש
                              </label>
                              <input
                                id="browser-work-action-value"
                                data-testid="browser-work-action-value"
                                type="text"
                                value={actionParamValue}
                                onChange={(e) => setActionParamValue(e.target.value)}
                                maxLength={512}
                                className="w-full text-sm border rounded px-2 py-1.5"
                              />
                            </div>
                          </div>
                          <label className="flex items-start gap-2 text-sm">
                            <input
                              type="checkbox"
                              data-testid="browser-work-login-required"
                              checked={loginRequired}
                              onChange={(e) => setLoginRequired(e.target.checked)}
                              className="mt-0.5"
                            />
                            <span>
                              היעד דורש התחברות אנושית
                              <span className="block text-[11px] text-gray-400">
                                הסשן יעצור במצב "ממתין לאישורים" ולא ייכשל; האישורים יימסרו ידנית במסלול הביצוע,
                                נצרכים פעם אחת ונמחקים בסוף הסשן. אין להזין כאן סיסמאות.
                              </span>
                            </span>
                          </label>
                        </>
                      )}
                    </div>
                  )}
                </fieldset>

                <div>
                  <label htmlFor="browser-work-objective" className="block text-xs font-medium text-gray-600 mb-0.5">
                    מטרה
                  </label>
                  <textarea
                    id="browser-work-objective"
                    value={objective}
                    onChange={(e) => setObjective(e.target.value)}
                    maxLength={2000}
                    rows={3}
                    className="w-full text-sm border rounded px-2 py-1.5"
                    placeholder="מה העובד אמור לעשות בדפדפן, מול איזו מערכת ובאיזה הקשר"
                  />
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    אין להזין סיסמאות, מפתחות או טוקנים — בקשה שנראית כמכילה סוד תידחה, וסודות שיזוהו יוסרו לפני השמירה.
                  </p>
                </div>
              </>
            )}

            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 space-y-1">
              <div className="font-medium">גבולות אישור ובטיחות — לפני השליחה</div>
              <ul className="list-disc pr-4 space-y-0.5">
                <li>מצב דפדפן מפורש — אין מסלול נפילה ל-API (api_fallback=false).</li>
                <li>אישור אנושי (agents_approve) נדרש לפני כל ביצוע עתידי; הסוכן אינו מאשר פעולות לעצמו.</li>
                <li>כלים לפי Tool Registry (deny-by-default) — אין כלי דפדפן רשום.</li>
                <li>הלקוח נגזר מהקריאה בשרת; עובד של לקוח אחר נדחה.</li>
                {capabilityQuery.isError ? (
                  <li className="text-red-700">
                    לא ניתן לטעון את גבולות הביצוע מהשרת — השליחה מושבתת.
                    <button type="button" onClick={() => capabilityQuery.refetch()} className="mr-2 underline">נסה שוב</button>
                  </li>
                ) : capability ? (
                  <li data-testid="browser-capability-boundary">
                    {capability.execution_available
                      ? "קריאה חיה (קריאה בלבד): זמינה. כתיבה מול יעד אמיתי: חסומה בכל תצורה — אין מסלול כתיבה חי."
                      : `קריאה חיה (קריאה בלבד): חסומה כרגע — ${browserCapabilityBlockedLabel(capability.blocked_reason)} (${capability.blocked_reason}). הבקשה תירשם בלבד. כתיבה מול יעד אמיתי: חסומה בכל תצורה — אין מסלול כתיבה חי.`}
                  </li>
                ) : (
                  <li className="text-gray-500">טוען את גבולות הביצוע מהשרת…</li>
                )}
                <li data-testid="browser-rollback-boundary">{BROWSER_ROLLBACK_BOUNDARY_HE}</li>
              </ul>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="submit"
                disabled={!canSubmit}
                className="text-xs px-3 py-1.5 rounded bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {submitMutation.isPending ? "שולח…" : "שלח בקשת עבודת דפדפן"}
              </button>
              <span className="text-[11px] text-gray-400">
                הבקשה נרשמת ומאוחזרת בלבד — אינה מתחילה ריצה, אינה מנפיקה אישור ואינה מבצעת כלי.
              </span>
            </div>
          </fieldset>

          {submitMutation.isError && (
            <p role="alert" className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {detailText(submitMutation.error?.response?.data?.detail, "שליחת בקשת עבודת הדפדפן נכשלה")}
            </p>
          )}
        </form>
      )}
    </div>
  );
}

// ─── Learning candidates vs approved knowledge (DIGEMP-KNOWLEDGE-CANDIDATES-FRONTEND-033) ─────
//
// The governed knowledge lifecycle (CANDIDATE → eligible evaluation → VERIFIED / REJECTED /
// DEPRECATED) is owned by the backend; this surface only renders it. Two truths stay visibly
// apart: unverified learning candidates raised from the employee's own work (no behavior change)
// and approved knowledge that passed an eligible evaluation. The view is read-only by
// construction — it has no approve/promote/execute control — and absent payload fields degrade
// to an explicit unknown marker instead of an invented state. Only whitelisted scalar fields are
// read (never run.context, prompts or payloads), so credentials stored in a source record cannot
// leak onto this screen.

const KNOWLEDGE_STATE_META = {
  CANDIDATE: { label: "ממתין לאימות", icon: "◷", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  VERIFIED: { label: "מאושר", icon: "✓", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  REJECTED: { label: "נדחה", icon: "✕", cls: "bg-red-100 text-red-700 border-red-200" },
  DEPRECATED: { label: "הוצא משימוש", icon: "⊘", cls: "bg-slate-200 text-slate-700 border-slate-300" },
};

const UNKNOWN_STATE_META = { label: "מצב לא ידוע", icon: "◌", cls: "bg-gray-100 text-gray-600 border-gray-200" };

// Synonyms the backend may emit for the same lifecycle state; anything outside the canonical set
// renders as an explicit unknown instead of being guessed at.
const KNOWLEDGE_STATE_ALIASES = {
  PENDING: "CANDIDATE", UNVERIFIED: "CANDIDATE",
  APPROVED: "VERIFIED",
  RETIRED: "DEPRECATED",
};

const SOURCE_KIND_HE = {
  official_documentation: "תיעוד רשמי",
  official_api_schema: "סכימת API רשמית",
  official_release_notes: "הערות גרסה רשמיות",
  community_reference: "מקור קהילתי",
  observed_runtime_behavior: "התנהגות שנצפתה בריצה",
  internal_evaluation: "הערכה פנימית",
};

const EVALUATOR_KIND_HE = {
  human_reviewer: "מבקר אנושי",
  deterministic_evaluation_suite: "מערכת הערכה דטרמיניסטית",
};

const plainText = (value) => (typeof value === "string" && value.trim() ? value.trim() : null);

function knowledgeFirstText(source, keys) {
  if (typeof source === "string") return plainText(source);
  if (!source || typeof source !== "object") return null;
  for (const key of keys) {
    const found = plainText(source[key]);
    if (found) return found;
  }
  return null;
}

function firstArrayField(source, keys) {
  if (!source || typeof source !== "object") return null;
  for (const key of keys) {
    if (Array.isArray(source[key])) return source[key];
  }
  return null;
}

const numericOrNull = (value) => {
  const n = typeof value === "string" && value.trim() ? Number(value) : value;
  return typeof n === "number" && Number.isFinite(n) ? n : null;
};

function normalizeKnowledgeState(value) {
  const raw = plainText(value);
  if (!raw) return null;
  const canonical = KNOWLEDGE_STATE_ALIASES[raw.toUpperCase()] || raw.toUpperCase();
  return KNOWLEDGE_STATE_META[canonical] ? canonical : raw;
}

function normalizeKnowledgeSource(item) {
  const raw = item?.source ?? item?.provenance ?? null;
  if (typeof raw === "string") {
    const kind = plainText(raw);
    return kind ? { label: boundedText(SOURCE_KIND_HE[kind] || kind, 60), detail: null } : null;
  }
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const kind = knowledgeFirstText(raw, ["kind", "type", "source_kind", "source_type", "name", "label"]);
  const detail = knowledgeFirstText(raw, ["detail", "reference", "note", "description", "title"]);
  if (!kind && !detail) return null;
  return {
    label: boundedText(kind ? SOURCE_KIND_HE[kind] || kind : "מקור לא ידוע", 60),
    detail: detail && detail !== kind ? boundedText(detail, 120) : null,
  };
}

function normalizeKnowledgeEvaluator(item) {
  const raw = item?.evaluated_by ?? item?.evaluator ?? null;
  if (typeof raw === "string") return boundedText(raw, 80);
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const name = knowledgeFirstText(raw, ["name", "display_name", "reviewer", "id"]);
    if (name) return boundedText(name, 80);
    const kind = knowledgeFirstText(raw, ["kind", "type", "evaluator_kind"]);
    if (kind) return boundedText(EVALUATOR_KIND_HE[kind] || kind, 40);
  }
  return null;
}

function normalizeRelatedWork(item) {
  const runObj = [item?.related_run, item?.related_work?.run].find((v) => v && typeof v === "object") || null;
  const ticketObj = [item?.ticket, item?.related_work?.ticket, item?.related_run?.ticket]
    .find((v) => v && typeof v === "object") || null;
  const runId = knowledgeFirstText(item, ["related_run_id", "run_id", "source_run_id"]) || plainText(runObj?.id);
  const ticketId = knowledgeFirstText(item, ["related_ticket_id", "ticket_id"])
    || plainText(ticketObj?.id) || plainText(runObj?.ticket_id);
  return {
    runId: runId ? boundedText(runId, 80) : null,
    ticketId: ticketId ? boundedText(ticketId, 80) : null,
    ticketNumber: numericOrNull(item?.ticket_number) ?? numericOrNull(ticketObj?.number)
      ?? numericOrNull(ticketObj?.ticket_number) ?? numericOrNull(runObj?.ticket_number) ?? null,
  };
}

function normalizeKnowledgeItem(item) {
  if (typeof item === "string") {
    const value = boundedText(item, 240);
    return value
      ? { id: null, text: value, state: null, source: null, related: normalizeRelatedWork(null), addedAt: null, evaluatedAt: null, evaluator: null }
      : null;
  }
  if (!item || typeof item !== "object" || Array.isArray(item)) return null;
  const statement = knowledgeFirstText(item, ["statement", "claim", "title", "text", "summary", "content", "label", "knowledge"]);
  const text = statement ? boundedText(statement, 240) : null;
  if (!text) return null;
  return {
    id: knowledgeFirstText(item, ["id", "candidate_id", "knowledge_id"]),
    text,
    state: normalizeKnowledgeState(item.state ?? item.status ?? item.candidate_state),
    source: normalizeKnowledgeSource(item),
    related: normalizeRelatedWork(item),
    addedAt: knowledgeFirstText(item, ["observed_at", "created_at", "raised_at", "detected_at", "at"]),
    evaluatedAt: knowledgeFirstText(item, ["evaluated_at", "verified_at", "approved_at"]),
    evaluator: normalizeKnowledgeEvaluator(item),
  };
}

/**
 * Tolerantly normalizes the additive knowledge payload. Accepts a plain candidate array, the
 * combined `{candidates, approved}` form, or a separate `approved_knowledge` list. Returns null
 * when the server supplied nothing at all; per-panel `supplied: false` then renders as unknown,
 * while `supplied: true` with an empty list means the server explicitly reported "none".
 * Candidates in a decided VERIFIED state render on the approved side (that is the only state the
 * served lifecycle allows to become approved knowledge); everything else — including rejected,
 * deprecated and unrecognized states — stays on the candidate side.
 */
export function normalizeKnowledgeCandidates(rawCandidates, rawApproved) {
  let candidatesRaw = null;
  let approvedRaw = null;
  let candidatesSupplied = false;
  let approvedSupplied = false;

  if (Array.isArray(rawCandidates)) {
    candidatesRaw = rawCandidates;
    candidatesSupplied = true;
  } else if (rawCandidates && typeof rawCandidates === "object") {
    const candidates = firstArrayField(rawCandidates, ["candidates", "unverified", "pending"]);
    if (candidates) { candidatesRaw = candidates; candidatesSupplied = true; }
    const approved = firstArrayField(rawCandidates, ["approved", "verified"]);
    if (approved) { approvedRaw = approved; approvedSupplied = true; }
  }
  if (rawApproved != null) {
    const approved = Array.isArray(rawApproved)
      ? rawApproved
      : firstArrayField(rawApproved, ["items", "approved", "knowledge", "entries"]);
    if (approved) { approvedRaw = approved; approvedSupplied = true; }
  }
  if (!candidatesSupplied && !approvedSupplied) return null;

  const keyOf = (item) => item.id || `text:${item.text}`;
  const approvedItems = [];
  const seenApproved = new Set();
  for (const item of (approvedRaw || []).map(normalizeKnowledgeItem)) {
    if (item && !seenApproved.has(keyOf(item))) {
      approvedItems.push(item);
      seenApproved.add(keyOf(item));
    }
  }

  const candidateItems = [];
  for (const item of (candidatesRaw || []).map(normalizeKnowledgeItem)) {
    if (!item) continue;
    if (item.state === "VERIFIED") {
      if (!seenApproved.has(keyOf(item))) {
        approvedItems.push(item);
        seenApproved.add(keyOf(item));
      }
      continue;
    }
    candidateItems.push(item);
  }

  return {
    candidates: { supplied: candidatesSupplied, items: candidateItems },
    approved: { supplied: approvedSupplied || candidatesSupplied, items: approvedItems },
  };
}

function RelatedWorkLinks({ related }) {
  const { runId, ticketId, ticketNumber } = related;
  if (!runId && !ticketId) {
    return <p className="text-[11px] text-gray-400">אין עבודה מקושרת מתועדת</p>;
  }
  return (
    <div className="flex flex-wrap items-center gap-3 text-xs">
      {ticketId && (
        <Link
          to={`/tickets/${ticketId}`}
          className="text-blue-600 hover:underline"
          aria-label={`פתח את הקריאה${ticketNumber ? ` #${ticketNumber}` : ""} שממנה הגיע הידע`}
        >
          {ticketNumber ? `קריאה #${ticketNumber}` : "קריאה מקושרת"}
        </Link>
      )}
      {runId && (
        <Link
          to={`/agent-runs/${runId}`}
          className="text-blue-600 hover:underline"
          aria-label={`פתח את הריצה${ticketNumber ? ` של קריאה #${ticketNumber}` : ""} שממנה הגיע הידע`}
        >
          צפה בריצה
        </Link>
      )}
    </div>
  );
}

function SourceChip({ source }) {
  if (!source) return null;
  return (
    <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 border border-gray-200">
      מקור: {source.label}
    </span>
  );
}

function KnowledgeItem({ item, zone }) {
  const stateMeta = KNOWLEDGE_STATE_META[item.state] || null;
  const meta = stateMeta || (zone === "approved"
    ? KNOWLEDGE_STATE_META.VERIFIED
    : UNKNOWN_STATE_META);
  return (
    <li className={`rounded-lg border bg-white/70 p-2.5 space-y-1.5 ${zone === "approved" ? "border-emerald-100" : "border-amber-100"}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-medium border whitespace-nowrap ${meta.cls}`}>
          {meta.icon} {meta.label}
          {!stateMeta && zone !== "approved" && typeof item.state === "string" ? ` (${boundedText(item.state, 40)})` : ""}
        </span>
        <SourceChip source={item.source} />
        <span className="text-[11px] text-gray-400 whitespace-nowrap mr-auto">{fmt(item.evaluatedAt || item.addedAt)}</span>
      </div>

      <p className="text-sm text-gray-800">{item.text}</p>

      {item.evaluator && (
        <p className="text-[11px] text-emerald-700">
          {zone === "approved" ? "אושר על ידי " : "נבדק על ידי "}
          <span dir="ltr">{item.evaluator}</span>
        </p>
      )}
      {item.source?.detail && <p className="text-[11px] text-gray-500">פירוט המקור: {item.source.detail}</p>}

      <RelatedWorkLinks related={item.related} />
    </li>
  );
}

/**
 * The operator-facing "what has this employee learned" surface: unverified learning candidates
 * and approved knowledge in two visually separate regions, each entry carrying its status,
 * provenance and the work it came from. Read-only by construction — navigation links are the only
 * interactive elements; there is deliberately no approve/promote control, because promotion is
 * owned by the governed evaluation gate, never by this screen.
 */
export function LearningKnowledgeSection({ knowledge }) {
  const candidates = (knowledge && knowledge.candidates) || { supplied: false, items: [] };
  const approved = (knowledge && knowledge.approved) || { supplied: false, items: [] };

  const renderItems = (panel, zone) => (
    <ul className="space-y-2">
      {panel.items.map((item, i) => <KnowledgeItem key={`${item.id || item.text}-${i}`} item={item} zone={zone} />)}
    </ul>
  );

  return (
    <section
      aria-label="למידה וידע"
      data-testid="learning-knowledge"
      className="bg-white rounded-xl border shadow-sm p-4 space-y-3"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-gray-700">למידה וידע</h3>
        <span className="text-[11px] text-gray-400">קריאה בלבד · אין כאן אישור או קידום ידע — ידע נכנס לאישור רק דרך הערכה מוסמכת</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div data-testid="knowledge-candidates" className="rounded-lg border border-amber-200 bg-amber-50/40 p-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-amber-800">מועמדים שטרם אומתו</h4>
            <span className="text-[10px] rounded-full border border-amber-200 bg-amber-100 text-amber-700 px-1.5 py-0.5">טרם אומת</span>
          </div>
          <p className="text-[11px] text-amber-700/80">נאספו מהעבודה של העובד — אינם ידע מאושר ואינם משנים התנהגות עד הערכה מוסמכת</p>
          {!candidates.supplied ? (
            <p className="text-sm text-gray-400 italic">לא ידוע — מועמדי ידע טרם נמסרו על-ידי השרת</p>
          ) : candidates.items.length === 0 ? (
            <p className="text-sm text-gray-400 italic">אין מועמדים שטרם אומתו</p>
          ) : (
            renderItems(candidates, "candidates")
          )}
        </div>

        <div data-testid="approved-knowledge" className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-3 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="text-sm font-semibold text-emerald-800">ידע מאושר</h4>
            <span className="text-[10px] rounded-full border border-emerald-200 bg-emerald-100 text-emerald-700 px-1.5 py-0.5">מאושר</span>
          </div>
          <p className="text-[11px] text-emerald-800/80">עבר הערכה מוסמכת — הידע התקף שהעובד רשאי להסתמך עליו</p>
          {!approved.supplied ? (
            <p className="text-sm text-gray-400 italic">לא ידוע — ידע מאושר טרם נמסר על-ידי השרת</p>
          ) : approved.items.length === 0 ? (
            <p className="text-sm text-gray-400 italic">אין ידע מאושר עדיין</p>
          ) : (
            renderItems(approved, "approved")
          )}
        </div>
      </div>
    </section>
  );
}

// ─── M365/Entra specialist readiness (DIGEMP-M365-ENTRA-SPECIALIST-FRONTEND-037) ─────
//
// The operator view of what this employee can actually do against customer Microsoft 365 /
// Entra tenants: which reads it is cleared for, which organisation (tenant) the work is bound
// to, which permissions are still unverified, where its known limits are, and when the work
// must escalate to a technician. Evidence comes from two sources: the observed Microsoft Graph
// connection the workspace (or the page's agent detail) already projects — last verification
// status and when it happened — and the additive per-domain readiness contract
// (`m365_readiness` — accepted on the workspace payload, its agent projection or the page's
// agent detail, and as the M365 pack inside the specialist_packs descriptor list). A tenant
// binding declared in the contract is shown as declared; otherwise the binding observed in the
// existing connection configuration is shown as observed evidence; when neither exists the
// card says the binding is unknown instead of hiding it, and a declared/observed disagreement
// is flagged instead of silently resolved. Missing, stale, undated or unverified evidence is
// labelled as such; the section never invents capabilities, limits, timestamps, tenants or
// sources, and it adds no write, grant or admin control — every state-changing action keeps
// going through the existing proposal → approval → execution flow. Only whitelisted scalar
// fields are rendered (bounded lengths, capped lists), so credential material stored next to
// the connection or the contract can not leak onto this screen.

const M365_SYSTEM_TYPES = new Set(M365_DOMAIN_TOKENS);
// Same day-cycle convention as backup_guardian's default max age (25h): a verification older
// than that is stale, and says so.
const M365_STALE_HOURS = 25;
const M365_FUTURE_SKEW_HOURS = 5 / 60;

const M365_STATE_META = {
  ready: { label: "מוכן", cls: "bg-green-100 text-green-700" },
  partial: { label: "מוכן חלקית", cls: "bg-amber-100 text-amber-800 border border-amber-200" },
  not_ready: { label: "לא מוכן", cls: "bg-red-100 text-red-700" },
  unknown: { label: "לא ידוע", cls: "bg-gray-100 text-gray-600" },
};

const M365_TEST_STATUS_META = {
  ok: { label: "אימות תקין", cls: "bg-green-100 text-green-700" },
  success: { label: "אימות תקין", cls: "bg-green-100 text-green-700" },
  config_error: { label: "שגיאת תצורה", cls: "bg-red-100 text-red-700" },
  auth_error: { label: "שגיאת אימות", cls: "bg-red-100 text-red-700" },
  permission_error: { label: "אין הרשאה", cls: "bg-red-100 text-red-700" },
  network_error: { label: "שגיאת רשת", cls: "bg-red-100 text-red-700" },
  rate_limited: { label: "הוגבל קצב בקשות", cls: "bg-amber-100 text-amber-800" },
  timeout: { label: "פסק זמן", cls: "bg-amber-100 text-amber-800" },
  not_implemented: { label: "לא נתמך", cls: "bg-amber-100 text-amber-800" },
  unsupported: { label: "לא נתמך", cls: "bg-amber-100 text-amber-800" },
  cross_customer: { label: "חשד לחציית לקוחות", cls: "bg-red-100 text-red-700" },
  error: { label: "שגיאה", cls: "bg-red-100 text-red-700" },
};

const M365_BINDING_META = {
  bound: { label: "מוצמד", cls: "bg-green-100 text-green-700" },
  verified: { label: "מאומת", cls: "bg-green-100 text-green-700" },
  active: { label: "פעיל", cls: "bg-green-100 text-green-700" },
  unbound: { label: "לא מוצמד", cls: "bg-red-100 text-red-700" },
  missing: { label: "חסר", cls: "bg-red-100 text-red-700" },
  unknown: { label: "לא ידוע", cls: "bg-gray-100 text-gray-600" },
};

const M365_STATE_KEYS = ["state", "readiness", "status"];
const M365_READ_KEYS = ["supported_reads", "read", "reads", "read_actions", "read_only_actions", "supported_read_capabilities", "read_capabilities"];
const M365_LIMITS_KEYS = ["limits", "known_limits", "constraints", "known_constraints"];
const M365_ESCALATION_KEYS = ["escalation", "escalation_conditions", "escalate_when", "escalation_triggers", "approval_boundary", "approval_required_for", "handoff_conditions"];
const M365_UNKNOWN_PERMISSION_KEYS = ["unknown_permissions", "unverified_permissions", "not_verified_permissions", "unconfirmed_permissions", "unknown_reads", "unverified_reads", "unknown_operations", "unverified_operations", "unknown_capabilities", "unverified_capabilities"];
const M365_TENANT_KEYS = ["tenant_binding", "tenant", "customer_binding", "tenant_id", "bound_tenant", "bound_tenant_id", "customer_tenant_id", "external_tenant_id", "tenant_name"];
const M365_EVIDENCE_AT_KEYS = ["last_verified_at", "last_tested_at", "verified_at", "checked_at", "evidence_at", "updated_at", "as_of"];
const M365_GENERATED_KEYS = ["generated_at", "as_of", "updated_at"];

function m365Field(raw, keys) {
  if (!raw || typeof raw !== "object") return undefined;
  for (const key of keys) {
    if (raw[key] !== undefined && raw[key] !== null) return raw[key];
  }
  return undefined;
}

function m365Token(value) {
  return typeof value === "string" ? value.trim().toLowerCase().replace(/[\s-]+/g, "_") : "";
}

function m365Text(value, max) {
  return typeof value === "string" && value.trim() ? value.trim().slice(0, max) : null;
}

function m365Entries(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) return [value];
  if (value && typeof value === "object") return [value];
  return [];
}

function m365Notes(value, keys, max, limit) {
  return m365Entries(value)
    .map((entry) => (typeof entry === "string" ? m365Text(entry, max) : m365Text(m365Field(entry, keys), max)))
    .filter(Boolean)
    .slice(0, limit);
}

function m365Reads(value, limit = 8) {
  return m365Entries(value)
    .map((entry) => {
      if (typeof entry === "string") {
        const label = m365Text(entry, 60);
        return label ? { label, detail: null } : null;
      }
      const label = m365Text(m365Field(entry, ["name", "operation", "action", "tool", "label", "title", "id"]), 60);
      if (!label) return null;
      return { label, detail: m365Text(m365Field(entry, ["description", "detail", "note"]), 140) };
    })
    .filter(Boolean)
    .slice(0, limit);
}

function m365Date(value) {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function m365AgeLabel(hours) {
  if (hours < 1) return "לפני פחות משעה";
  const whole = Math.floor(hours);
  if (whole < 2) return "לפני שעה";
  if (hours < 48) return `לפני ${whole} שעות`;
  const days = Math.floor(hours / 24);
  return days < 2 ? "לפני יום" : `לפני ${days} ימים`;
}

function m365AgeLine(at, nowMs, { freshVerb, freshCls, undatedText }) {
  const date = m365Date(at);
  if (!date) return { text: undatedText, cls: "text-amber-700" };
  const age = (nowMs - date.getTime()) / 3600000;
  if (age < -M365_FUTURE_SKEW_HOURS) {
    return { text: "חתימת הזמן של הראיה עתידית — הראיה אינה אמינה עד לאימות מחדש.", cls: "text-amber-700" };
  }
  const base = `${freshVerb}: ${fmt(at)} (${m365AgeLabel(age)})`;
  if (age > M365_STALE_HOURS) {
    return { text: `ראיה ישנה — יש לאמת מחדש · ${base}.`, cls: "text-amber-700" };
  }
  return { text: `${base}.`, cls: freshCls };
}

function m365ConnectionOf(connections) {
  return (Array.isArray(connections) ? connections : []).find(
    (connection) => connection && M365_SYSTEM_TYPES.has(m365Token(connection.system_type))
  ) || null;
}

function m365ObservedBindingOf(workspace, agentDetail) {
  for (const list of [workspace?.capabilities?.connections, agentDetail?.connections]) {
    const bindings = (Array.isArray(list) ? list : [])
      .filter((connection) => connection && M365_SYSTEM_TYPES.has(m365Token(connection.system_type)))
      .map((connection) => ({
        external: m365Text(connection.external_tenant_id, 128),
        tenant: m365Text(connection.tenant_id, 128),
      }))
      .filter((binding) => binding.external || binding.tenant);
    if (bindings.length > 0) {
      return { id: bindings[0].external || bindings[0].tenant, fromExternal: Boolean(bindings[0].external) };
    }
  }
  return null;
}

function m365BindingOf(raw) {
  if (raw == null) return null;
  if (typeof raw === "string" || typeof raw === "number") {
    const id = m365Text(String(raw), 128);
    return id ? { id, name: null, status: null } : null;
  }
  if (typeof raw !== "object" || Array.isArray(raw)) return null;
  const id = m365Text(m365Field(raw, ["tenant_id", "tenant", "id", "external_tenant_id", "bound_tenant_id", "customer_tenant_id", "tenant_name", "name", "display_name"]), 128);
  const name = m365Text(m365Field(raw, ["tenant_name", "name", "display_name", "label"]), 120);
  const status = m365Text(m365Field(raw, ["binding_status", "status", "state", "verification_status", "verification"]), 40);
  if (!id && !name && !status) return null;
  return { id, name: name && name !== id ? name : null, status };
}

function m365ContractOf(workspace, agentDetail) {
  const pick = (source) =>
    source?.m365_readiness ?? source?.m365_specialist_readiness ?? source?.m365_entra_readiness
    ?? source?.m365_entra_specialist ?? source?.microsoft_365_readiness ?? source?.entra_readiness
    ?? source?.m365_specialist ?? source?.m365 ?? null;
  const direct = pick(workspace) ?? pick(workspace?.agent) ?? pick(agentDetail) ?? null;
  if (direct && typeof direct === "object" && !Array.isArray(direct)) return { block: direct, pack: null };
  const packsRoot = workspace?.specialist_packs ?? workspace?.specialist_pack_descriptors
    ?? workspace?.agent?.specialist_packs ?? workspace?.agent?.specialist_pack_descriptors
    ?? agentDetail?.specialist_packs ?? agentDetail?.specialist_pack_descriptors ?? null;
  const list = Array.isArray(packsRoot)
    ? packsRoot
    : packsRoot && typeof packsRoot === "object"
      ? m365Field(packsRoot, ["packs", "descriptors", "pack_descriptors", "items"])
      : undefined;
  const pack = (Array.isArray(list) ? list : []).find((entry) => {
    if (!entry || typeof entry !== "object") return false;
    const token = m365Token(m365Field(entry, ["domain", "domain_name", "domain_label", "name", "pack", "title", "specialty", "id"]));
    return M365_SYSTEM_TYPES.has(token);
  }) || null;
  return { block: null, pack };
}

function M365Block({ title, testId, children }) {
  return (
    <div data-testid={testId} className="rounded-lg border border-gray-200 bg-gray-50/60 p-2.5 space-y-1.5">
      <div className="text-xs font-medium text-gray-700">{title}</div>
      {children}
    </div>
  );
}

export function M365EntraSpecialistSection({ workspace, agentDetail }) {
  const nowMs = Date.now();
  const connection = m365ConnectionOf(workspace?.capabilities?.connections) || m365ConnectionOf(agentDetail?.connections);
  const { block, pack } = m365ContractOf(workspace, agentDetail);
  const contractReported = Boolean(block || pack);

  const field = (keys) => {
    const fromBlock = block ? m365Field(block, keys) : undefined;
    if (fromBlock !== undefined) return fromBlock;
    return pack ? m365Field(pack, keys) : undefined;
  };

  const stateRaw = contractReported ? field(M365_STATE_KEYS) : undefined;
  const stateToken = M365_STATE_META[m365Token(stateRaw)] ? m365Token(stateRaw) : "unknown";
  const stateMeta = M365_STATE_META[stateToken];
  const stateReported = m365Text(stateRaw, 40);

  const readsRaw = field(M365_READ_KEYS);
  const limitsRaw = field(M365_LIMITS_KEYS);
  const escalationRaw = field(M365_ESCALATION_KEYS);
  const unknownsRaw = field(M365_UNKNOWN_PERMISSION_KEYS);
  const reads = m365Reads(readsRaw);
  const limits = m365Notes(limitsRaw, ["limit", "text", "title", "description"], 140, 6);
  const escalation = m365Notes(escalationRaw, ["condition", "text", "title", "trigger", "description"], 160, 6);
  const unknowns = m365Notes(unknownsRaw, ["permission", "operation", "name", "text", "title", "description"], 140, 8);

  const declaredBinding = contractReported ? m365BindingOf(field(M365_TENANT_KEYS)) : null;
  const observedBinding = m365ObservedBindingOf(workspace, agentDetail);
  const declaredIdentity = declaredBinding ? (declaredBinding.id || declaredBinding.name) : null;
  const bindingStatusMeta = declaredBinding?.status ? M365_BINDING_META[m365Token(declaredBinding.status)] || null : null;
  const bindingMismatch = Boolean(
    declaredBinding?.id && observedBinding?.id
    && declaredBinding.id.trim().toLowerCase() !== observedBinding.id.trim().toLowerCase()
  );

  const generatedAt = field(M365_GENERATED_KEYS);

  const statusRaw = m365Token(connection?.last_test_status);
  const statusMeta = M365_TEST_STATUS_META[statusRaw] || null;
  const statusLabel = statusMeta
    ? statusMeta.label
    : statusRaw
      ? m365Text(connection.last_test_status, 40)
      : "טרם אומת";
  const statusCls = statusMeta
    ? statusMeta.cls
    : statusRaw ? "bg-gray-100 text-gray-600" : "bg-amber-100 text-amber-800";

  const verifiedOk = statusRaw === "ok" || statusRaw === "success";

  const connectionEvidence = connection
    ? m365AgeLine(connection.last_tested_at, nowMs, {
        freshVerb: verifiedOk ? "אומת לאחרונה" : "נבדק לאחרונה",
        freshCls: verifiedOk ? "text-green-700" : "text-gray-500",
        undatedText: verifiedOk
          ? "האימות דווח תקין אך מועדו טרם תועד — אין ראיה מתוארכת."
          : "מועד האימות האחרון טרם תועד — אין ראיה מתוארכת לאימות.",
      })
    : null;
  const contractEvidence = !connection && field(M365_EVIDENCE_AT_KEYS) !== undefined
    ? m365AgeLine(field(M365_EVIDENCE_AT_KEYS), nowMs, {
        freshVerb: "עודכן לאחרונה",
        freshCls: "text-gray-600",
        undatedText: "מועד עדכון המוכנות טרם תועד — אין ראיה מתוארכת.",
      })
    : null;

  return (
    <section aria-label="מומחה M365/Entra" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700">מומחה M365/Entra</h3>
        <span
          className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${stateMeta.cls}`}
          title={stateToken === "unknown" && stateReported ? `דווח על-ידי השרת: ${stateReported}` : undefined}
        >
          {stateMeta.label}
        </span>
        {generatedAt != null && <span className="text-[11px] text-gray-400 mr-auto">עודכן: {fmt(generatedAt)}</span>}
      </div>
      <p className="text-[11px] text-gray-400">
        ראיות קריאה בלבד לעבודת Microsoft 365 / Entra: מצב החיבור, הקריאות המאושרות, הצמדות הארגון (Tenant), הרשאות שטרם אומתו, ומתי נדרשת הסלמה או אישור טכנאי.
      </p>
      {!contractReported && (
        <p className="text-xs text-gray-500">
          מוכנות מומחה M365/Entra טרם נמסרה על-ידי השרת — לא מוצגות כאן יכולות קריאה, הצמדות ארגון או תנאי הסלמה ללא נתונים מגבים.
        </p>
      )}

      <M365Block title="מצב החיבור" testId="m365-evidence">
        {connection ? (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-700">
              <span>חיבור {SYSTEM_TYPE_HE[connection.system_type] || "Microsoft Graph (M365)"}</span>
              <span className={`px-1.5 py-0.5 rounded border whitespace-nowrap ${statusCls}`}>{statusLabel}</span>
            </div>
            <p className={`text-xs ${connectionEvidence.cls}`}>{connectionEvidence.text}</p>
          </>
        ) : contractEvidence ? (
          <>
            <p className="text-xs text-gray-500">לא נמצא חיבור Microsoft Graph מוגדר לעובד זה.</p>
            <p className={`text-xs ${contractEvidence.cls}`}>ראיית המוכנות: {contractEvidence.text}</p>
          </>
        ) : (
          <p className="text-xs text-gray-500">לא נמצא חיבור Microsoft Graph מוגדר לעובד זה — אין ראיות קריאה זמינות להצגה.</p>
        )}
      </M365Block>

      <M365Block title="קריאות נתמכות" testId="m365-reads">
        {reads.length > 0 ? (
          <ul className="space-y-1">
            {reads.map((read, i) => (
              <li key={`${read.label}-${i}`} className="text-xs text-gray-700 space-y-0.5">
                <span className="font-mono" dir="ltr">{read.label}</span>
                {read.detail && <div className="text-gray-500">{read.detail}</div>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-gray-500">
            {readsRaw !== undefined
              ? "השרת דיווח שלא הוגדרו יכולות קריאה לעובד זה."
              : "טרם דווחו יכולות קריאה על-ידי השרת."}
          </p>
        )}
      </M365Block>

      <M365Block title="הצמדות ארגון (Tenant)" testId="m365-tenant">
        {declaredIdentity ? (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-700">
              <span>ארגון לקוח מוצמד:</span>
              <span className="font-mono" dir="ltr">{declaredIdentity}</span>
              {declaredBinding.status && (
                <span className={`px-1.5 py-0.5 rounded border whitespace-nowrap ${bindingStatusMeta ? bindingStatusMeta.cls : "bg-gray-100 text-gray-600"}`}>
                  {bindingStatusMeta ? bindingStatusMeta.label : declaredBinding.status}
                </span>
              )}
            </div>
            {declaredBinding.name && declaredBinding.id && (
              <div className="text-xs text-gray-500">{declaredBinding.name}</div>
            )}
            <p className="text-xs text-gray-500">מדווח על-ידי השרת כחלק מחוזה המוכנות.</p>
            {bindingMismatch && (
              <p className="text-xs text-amber-700">
                שימו לב: הגדרת החיבור מצביעה על ארגון (Tenant) אחר — נדרש בירור מול מנהל לפני הסתמכות על ההצמדות.
              </p>
            )}
          </>
        ) : declaredBinding ? (
          <p className="text-xs text-gray-500">
            השרת דיווח מצב הצמדות ללא מזהה ארגון (Tenant) מוצמד — ההצמדות אינה ידועה.
          </p>
        ) : observedBinding ? (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-700">
              <span>{observedBinding.fromExternal ? "מיפוי ארגון חיצוני מפורש:" : "ארגון מוצמד בהגדרת החיבור:"}</span>
              <span className="font-mono" dir="ltr">{observedBinding.id}</span>
            </div>
            <p className="text-xs text-gray-500">נלקח מהגדרת החיבור הקיים — ראיה תצורתית בלבד, לא הענקת הרשאות.</p>
          </>
        ) : connection ? (
          <p className="text-xs text-gray-500">החיבור הקיים אינו נושא מזהה ארגון (Tenant) — הצמדות הארגון אינה ידועה.</p>
        ) : (
          <p className="text-xs text-gray-500">טרם דווחה הצמדות ארגון (Tenant) — הצמדות הארגון אינה ידועה.</p>
        )}
      </M365Block>

      <M365Block title="הרשאות לא מאומתות" testId="m365-unknowns">
        {unknowns.length > 0 ? (
          <ul className="list-disc pr-4 space-y-1">
            {unknowns.map((permission, i) => <li key={i} className="text-xs text-gray-700">{permission}</li>)}
          </ul>
        ) : (
          <p className="text-xs text-gray-500">
            {unknownsRaw !== undefined
              ? "השרת דיווח שלא נותרו הרשאות בלתי-מאומתות."
              : "טרם דווח על הרשאות בלתי-מאומתות על-ידי השרת."}
          </p>
        )}
      </M365Block>

      <M365Block title="מגבלות המומחה" testId="m365-limits">
        {limits.length > 0 ? (
          <ul className="list-disc pr-4 space-y-1">
            {limits.map((limit, i) => <li key={i} className="text-xs text-gray-700">{limit}</li>)}
          </ul>
        ) : (
          <p className="text-xs text-gray-500">
            {limitsRaw !== undefined
              ? "השרת דיווח שלא תועדו מגבלות ידועות."
              : "טרם דווחו מגבלות ידועות על-ידי השרת."}
          </p>
        )}
      </M365Block>

      <M365Block title="הסלמה ואישור" testId="m365-escalation">
        {escalation.length > 0 ? (
          <ul className="list-disc pr-4 space-y-1">
            {escalation.map((condition, i) => <li key={i} className="text-xs text-gray-700">{condition}</li>)}
          </ul>
        ) : (
          <p className="text-xs text-gray-500">
            {escalationRaw !== undefined
              ? "השרת דיווח שלא הוגדרו תנאי הסלמה."
              : "טרם דווחו תנאי הסלמה על-ידי השרת."}
          </p>
        )}
      </M365Block>

      <p className="text-[11px] text-gray-400">
        תצוגת ראיות בלבד — הכרטיס אינו כולל פעולות כתיבה, הענקת הרשאות או שינוי הרשאות; כל פעולה משנה-מצב ממשיכה לעבור במסלול ההצעה, האישור והביצוע הקיים.
      </p>
    </section>
  );
}

// ─── Acronis specialist readiness (DIGEMP-ACRONIS-SPECIALIST-FRONTEND-036) ────
//
// The operator view of what this employee can actually do against customer backups. Evidence
// comes from two sources: the observed Acronis connection the workspace already projects (last
// verification status and when it happened) and the additive per-domain readiness contract
// (`acronis_readiness` — accepted on the workspace payload, its agent projection or the page's
// agent detail, and as the Acronis pack inside the specialist_packs descriptor list). Missing,
// stale, undated or unverified evidence is labelled as such; the section never invents
// capabilities, limits, timestamps or sources, and it adds no write, restore or remediation
// control — every state-changing backup action keeps going through the existing proposal →
// approval → execution flow. Only whitelisted scalar fields are rendered (bounded lengths,
// capped lists), so credential material stored next to the connection or the contract can not
// leak onto this screen.

const ACRONIS_SYSTEM_TYPES = new Set(["acronis", "acronis_backup"]);
// Same day-cycle convention as backup_guardian's default max age (25h): a verification older
// than that is stale, and says so.
const ACRONIS_STALE_HOURS = 25;
const ACRONIS_FUTURE_SKEW_HOURS = 5 / 60;

const ACRONIS_STATE_META = {
  ready: { label: "מוכן", cls: "bg-green-100 text-green-700" },
  partial: { label: "מוכן חלקית", cls: "bg-amber-100 text-amber-800 border border-amber-200" },
  not_ready: { label: "לא מוכן", cls: "bg-red-100 text-red-700" },
  unknown: { label: "לא ידוע", cls: "bg-gray-100 text-gray-600" },
};

const ACRONIS_TEST_STATUS_META = {
  ok: { label: "אימות תקין", cls: "bg-green-100 text-green-700" },
  success: { label: "אימות תקין", cls: "bg-green-100 text-green-700" },
  config_error: { label: "שגיאת תצורה", cls: "bg-red-100 text-red-700" },
  auth_error: { label: "שגיאת אימות", cls: "bg-red-100 text-red-700" },
  permission_error: { label: "אין הרשאה", cls: "bg-red-100 text-red-700" },
  network_error: { label: "שגיאת רשת", cls: "bg-red-100 text-red-700" },
  rate_limited: { label: "הוגבל קצב בקשות", cls: "bg-amber-100 text-amber-800" },
  timeout: { label: "פסק זמן", cls: "bg-amber-100 text-amber-800" },
  not_implemented: { label: "לא נתמך", cls: "bg-amber-100 text-amber-800" },
  unsupported: { label: "לא נתמך", cls: "bg-amber-100 text-amber-800" },
  cross_customer: { label: "חשד לחציית לקוחות", cls: "bg-red-100 text-red-700" },
  error: { label: "שגיאה", cls: "bg-red-100 text-red-700" },
};

const ACRONIS_STATE_KEYS = ["state", "readiness", "status"];
const ACRONIS_READ_KEYS = ["supported_reads", "read", "reads", "read_actions", "read_only_actions", "supported_read_capabilities", "read_capabilities"];
const ACRONIS_LIMITS_KEYS = ["limits", "known_limits", "constraints", "known_constraints"];
const ACRONIS_ESCALATION_KEYS = ["escalation", "escalation_conditions", "escalate_when", "escalation_triggers", "approval_boundary", "approval_required_for", "handoff_conditions"];
const ACRONIS_EVIDENCE_AT_KEYS = ["last_verified_at", "last_tested_at", "verified_at", "checked_at", "evidence_at", "updated_at", "as_of"];
const ACRONIS_GENERATED_KEYS = ["generated_at", "as_of", "updated_at"];

function acrField(raw, keys) {
  if (!raw || typeof raw !== "object") return undefined;
  for (const key of keys) {
    if (raw[key] !== undefined && raw[key] !== null) return raw[key];
  }
  return undefined;
}

function acrToken(value) {
  return typeof value === "string" ? value.trim().toLowerCase().replace(/[\s-]+/g, "_") : "";
}

function acrText(value, max) {
  return typeof value === "string" && value.trim() ? value.trim().slice(0, max) : null;
}

function acrEntries(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string" && value.trim()) return [value];
  if (value && typeof value === "object") return [value];
  return [];
}

function acrNotes(value, keys, max, limit) {
  return acrEntries(value)
    .map((entry) => (typeof entry === "string" ? acrText(entry, max) : acrText(acrField(entry, keys), max)))
    .filter(Boolean)
    .slice(0, limit);
}

function acrReads(value, limit = 8) {
  return acrEntries(value)
    .map((entry) => {
      if (typeof entry === "string") {
        const label = acrText(entry, 60);
        return label ? { label, detail: null } : null;
      }
      const label = acrText(acrField(entry, ["name", "operation", "action", "tool", "label", "title", "id"]), 60);
      if (!label) return null;
      return { label, detail: acrText(acrField(entry, ["description", "detail", "note"]), 140) };
    })
    .filter(Boolean)
    .slice(0, limit);
}

function acrDate(value) {
  if (typeof value !== "string" && typeof value !== "number") return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function acrAgeLabel(hours) {
  if (hours < 1) return "לפני פחות משעה";
  const whole = Math.floor(hours);
  if (whole < 2) return "לפני שעה";
  if (hours < 48) return `לפני ${whole} שעות`;
  const days = Math.floor(hours / 24);
  return days < 2 ? "לפני יום" : `לפני ${days} ימים`;
}

function acronisAgeLine(at, nowMs, { freshVerb, freshCls, undatedText }) {
  const date = acrDate(at);
  if (!date) return { text: undatedText, cls: "text-amber-700" };
  const age = (nowMs - date.getTime()) / 3600000;
  if (age < -ACRONIS_FUTURE_SKEW_HOURS) {
    return { text: "חתימת הזמן של הראיה עתידית — הראיה אינה אמינה עד לאימות מחדש.", cls: "text-amber-700" };
  }
  const base = `${freshVerb}: ${fmt(at)} (${acrAgeLabel(age)})`;
  if (age > ACRONIS_STALE_HOURS) {
    return { text: `ראיה ישנה — יש לאמת מחדש · ${base}.`, cls: "text-amber-700" };
  }
  return { text: `${base}.`, cls: freshCls };
}

function acronisConnectionOf(connections) {
  return (Array.isArray(connections) ? connections : []).find(
    (connection) => connection && ACRONIS_SYSTEM_TYPES.has(acrToken(connection.system_type))
  ) || null;
}

function acronisContractOf(workspace, agentDetail) {
  const pick = (source) =>
    source?.acronis_readiness ?? source?.acronis_specialist_readiness ?? source?.acronis_specialist
    ?? source?.acronis_backup_readiness ?? source?.acronis ?? null;
  const direct = pick(workspace) ?? pick(workspace?.agent) ?? pick(agentDetail) ?? null;
  if (direct && typeof direct === "object" && !Array.isArray(direct)) return { block: direct, pack: null };
  const packsRoot = workspace?.specialist_packs ?? workspace?.specialist_pack_descriptors
    ?? workspace?.agent?.specialist_packs ?? workspace?.agent?.specialist_pack_descriptors
    ?? agentDetail?.specialist_packs ?? agentDetail?.specialist_pack_descriptors ?? null;
  const list = Array.isArray(packsRoot)
    ? packsRoot
    : packsRoot && typeof packsRoot === "object"
      ? acrField(packsRoot, ["packs", "descriptors", "pack_descriptors", "items"])
      : undefined;
  const pack = (Array.isArray(list) ? list : []).find((entry) => {
    if (!entry || typeof entry !== "object") return false;
    const token = acrToken(acrField(entry, ["domain", "domain_name", "domain_label", "name", "pack", "title", "specialty", "id"]));
    return token === "acronis" || token === "acronis_backup";
  }) || null;
  return { block: null, pack };
}

function AcronisBlock({ title, testId, children }) {
  return (
    <div data-testid={testId} className="rounded-lg border border-gray-200 bg-gray-50/60 p-2.5 space-y-1.5">
      <div className="text-xs font-medium text-gray-700">{title}</div>
      {children}
    </div>
  );
}

export function AcronisSpecialistSection({ workspace, agentDetail }) {
  const nowMs = Date.now();
  const connection = acronisConnectionOf(workspace?.capabilities?.connections);
  const { block, pack } = acronisContractOf(workspace, agentDetail);
  const contractReported = Boolean(block || pack);

  const field = (keys) => {
    const fromBlock = block ? acrField(block, keys) : undefined;
    if (fromBlock !== undefined) return fromBlock;
    return pack ? acrField(pack, keys) : undefined;
  };

  const stateRaw = contractReported ? field(ACRONIS_STATE_KEYS) : undefined;
  const stateToken = ACRONIS_STATE_META[acrToken(stateRaw)] ? acrToken(stateRaw) : "unknown";
  const stateMeta = ACRONIS_STATE_META[stateToken];
  const stateReported = acrText(stateRaw, 40);

  const readsRaw = field(ACRONIS_READ_KEYS);
  const limitsRaw = field(ACRONIS_LIMITS_KEYS);
  const escalationRaw = field(ACRONIS_ESCALATION_KEYS);
  const reads = acrReads(readsRaw);
  const limits = acrNotes(limitsRaw, ["limit", "text", "title", "description"], 140, 6);
  const escalation = acrNotes(escalationRaw, ["condition", "text", "title", "trigger", "description"], 160, 6);

  const generatedAt = field(ACRONIS_GENERATED_KEYS);

  const statusRaw = acrToken(connection?.last_test_status);
  const statusMeta = ACRONIS_TEST_STATUS_META[statusRaw] || null;
  const statusLabel = statusMeta
    ? statusMeta.label
    : statusRaw
      ? acrText(connection.last_test_status, 40)
      : "טרם אומת";
  const statusCls = statusMeta
    ? statusMeta.cls
    : statusRaw ? "bg-gray-100 text-gray-600" : "bg-amber-100 text-amber-800";

  const connectionEvidence = connection
    ? acronisAgeLine(connection.last_tested_at, nowMs, {
        freshVerb: statusRaw === "ok" || statusRaw === "success" ? "אומת לאחרונה" : "נבדק לאחרונה",
        freshCls: statusRaw === "ok" || statusRaw === "success" ? "text-green-700" : "text-gray-500",
        undatedText: statusRaw === "ok" || statusRaw === "success"
          ? "האימות דווח תקין אך מועדו טרם תועד — אין ראיה מתוארכת."
          : "מועד האימות האחרון טרם תועד — אין ראיה מתוארכת לאימות.",
      })
    : null;
  const contractEvidence = !connection && field(ACRONIS_EVIDENCE_AT_KEYS) !== undefined
    ? acronisAgeLine(field(ACRONIS_EVIDENCE_AT_KEYS), nowMs, {
        freshVerb: "עודכן לאחרונה",
        freshCls: "text-gray-600",
        undatedText: "מועד עדכון המוכנות טרם תועד — אין ראיה מתוארכת.",
      })
    : null;

  return (
    <section aria-label="מומחה Acronis" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700">מומחה Acronis</h3>
        <span
          className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${stateMeta.cls}`}
          title={stateToken === "unknown" && stateReported ? `דווח על-ידי השרת: ${stateReported}` : undefined}
        >
          {stateMeta.label}
        </span>
        {generatedAt != null && <span className="text-[11px] text-gray-400 mr-auto">עודכן: {fmt(generatedAt)}</span>}
      </div>
      <p className="text-[11px] text-gray-400">
        ראיות קריאה בלבד לעבודת גיבוי: מה העובד יכול לקרוא, מגבלות ידועות, ומתי נדרשת הסלמה או אישור טכנאי.
      </p>
      {!contractReported && (
        <p className="text-xs text-gray-500">
          מוכנות מומחה Acronis טרם נמסרה על-ידי השרת — לא מוצגות כאן יכולות קריאה, מגבלות או תנאי הסלמה ללא נתונים מגבים.
        </p>
      )}

      <AcronisBlock title="מצב הגיבוי" testId="acronis-evidence">
        {connection ? (
          <>
            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-700">
              <span>{acrText(connection.display_name, 80) || SYSTEM_TYPE_HE[connection.system_type] || "Acronis"}</span>
              <span className={`px-1.5 py-0.5 rounded border whitespace-nowrap ${statusCls}`}>{statusLabel}</span>
            </div>
            <p className={`text-xs ${connectionEvidence.cls}`}>{connectionEvidence.text}</p>
          </>
        ) : contractEvidence ? (
          <>
            <p className="text-xs text-gray-500">לא נמצא חיבור Acronis מוגדר לעובד זה.</p>
            <p className={`text-xs ${contractEvidence.cls}`}>ראיית המוכנות: {contractEvidence.text}</p>
          </>
        ) : (
          <p className="text-xs text-gray-500">לא נמצא חיבור Acronis מוגדר לעובד זה — אין ראיות גיבוי זמינות להצגה.</p>
        )}
      </AcronisBlock>

      <AcronisBlock title="קריאות נתמכות" testId="acronis-reads">
        {reads.length > 0 ? (
          <ul className="space-y-1">
            {reads.map((read, i) => (
              <li key={`${read.label}-${i}`} className="text-xs text-gray-700 space-y-0.5">
                <span className="font-mono" dir="ltr">{read.label}</span>
                {read.detail && <div className="text-gray-500">{read.detail}</div>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-gray-500">
            {readsRaw !== undefined
              ? "השרת דיווח שלא הוגדרו יכולות קריאה לעובד זה."
              : "טרם דווחו יכולות קריאה על-ידי השרת."}
          </p>
        )}
      </AcronisBlock>

      <AcronisBlock title="מגבלות המומחה" testId="acronis-limits">
        {limits.length > 0 ? (
          <ul className="list-disc pr-4 space-y-1">
            {limits.map((limit, i) => <li key={i} className="text-xs text-gray-700">{limit}</li>)}
          </ul>
        ) : (
          <p className="text-xs text-gray-500">
            {limitsRaw !== undefined
              ? "השרת דיווח שלא תועדו מגבלות ידועות."
              : "טרם דווחו מגבלות ידועות על-ידי השרת."}
          </p>
        )}
      </AcronisBlock>

      <AcronisBlock title="הסלמה ואישור" testId="acronis-escalation">
        {escalation.length > 0 ? (
          <ul className="list-disc pr-4 space-y-1">
            {escalation.map((condition, i) => <li key={i} className="text-xs text-gray-700">{condition}</li>)}
          </ul>
        ) : (
          <p className="text-xs text-gray-500">
            {escalationRaw !== undefined
              ? "השרת דיווח שלא הוגדרו תנאי הסלמה."
              : "טרם דווחו תנאי הסלמה על-ידי השרת."}
          </p>
        )}
      </AcronisBlock>

      <p className="text-[11px] text-gray-400">
        תצוגת ראיות בלבד — הכרטיס אינו כולל פעולות כתיבה, שחזור או שינוי מדיניות גיבוי; כל פעולה משנה-מצב ממשיכה לעבור במסלול ההצעה, האישור והביצוע הקיים.
      </p>
    </section>
  );
}

// ─── Scheduled operations (DIGEMP-SCHEDULED-OPERATIONS-FRONTEND-040) ──────────
//
// Read-only "what is this employee scheduled to do" surface, built only from the employee's
// existing scheduled-check records (GET /api/scheduled-checks/, filtered to this agent). Every
// schedule shows the next run and the last run with operator-readable timing, the schedule's
// current state, the last run's outcome with a link to the audited run record, and the failure
// state when the last run failed. It adds no authority: creating, running, pausing, editing or
// deleting schedules stays in the permission-gated management section further down the page —
// this view renders no controls at all. The schedules API is admin/owner-only, so a viewer
// without that role — or any failed load — degrades to an explicit "state unknown" line instead
// of an empty-looking list, and missing fields are shown as unknown rather than invented.

const STALE_GRACE_MS = 60 * 60 * 1000;
// The scheduler polls every 60 seconds and advances next_run_at on every firing attempt (see
// backend/app/services/scheduler.py), so a due schedule normally clears within about a minute.
// Still overdue after a full hour (the shortest allowed interval) means the scheduler is not
// actually running it — a stopped process, crisis mode, or repeatedly failing firings — which is
// worth surfacing to the operator, while short restarts stay below the noise floor.

const SCHEDULE_RUN_STATUS_HE = {
  pending: "מאתחל", analyzing: "מנתח", diagnosing: "מאבחן", executing: "מבצע",
  awaiting_technician_review: "ממתין לסקירת טכנאי", awaiting_approval: "ממתין לאישור",
  waiting_for_customer_connection: "ממתין לחיבור", connection_active: "חיבור פעיל",
  waiting_for_customer_answer: "ממתין לתשובה", paused: "הופסק",
  completed: "הושלם", resolved: "נפתרה", approved: "מאושר", rejected: "נדחה", failed: "נכשל",
};

const SCHEDULE_INTERVAL_HE = { 1: "כל שעה", 6: "כל 6 שעות", 12: "כל 12 שעות", 24: "יומי", 48: "כל יומיים", 168: "שבועי" };

function scheduleIntervalHe(hours) {
  if (SCHEDULE_INTERVAL_HE[hours]) return SCHEDULE_INTERVAL_HE[hours];
  return Number.isFinite(hours) ? `כל ${hours} שעות` : "מרווח לא ידוע";
}

function parseTs(value) {
  if (typeof value !== "string" || !value.trim()) return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

function heAmount(n, one, two, many) {
  if (n === 1) return one;
  if (n === 2) return two;
  return `${n} ${many}`;
}

function relTime(ms, nowMs) {
  const diff = ms - nowMs;
  const abs = Math.abs(diff);
  let amount = null;
  if (abs >= 60_000) {
    if (abs < 3_600_000) amount = heAmount(Math.round(abs / 60_000), "דקה", "שתי דקות", "דקות");
    else if (abs < 48 * 3_600_000) amount = heAmount(Math.round(abs / 3_600_000), "שעה", "שעתיים", "שעות");
    else amount = heAmount(Math.round(abs / 86_400_000), "יום", "יומיים", "ימים");
  }
  if (amount == null) return diff >= 0 ? "בעוד פחות מדקה" : "לפני פחות מדקה";
  return diff >= 0 ? `בעוד ${amount}` : `לפני ${amount}`;
}

function scheduleChips(check, nextMs, status, nowMs) {
  const enabledKnown = typeof check?.is_enabled === "boolean";
  const enabled = check?.is_enabled === true;
  const stale = enabled && nextMs != null && nowMs - nextMs >= STALE_GRACE_MS;
  const unknownNext = enabled && nextMs == null;
  const failed = status === "failed";

  const chips = [];
  if (!enabledKnown) {
    chips.push({ label: "מצב לא ידוע", title: "לא ניתן לקבוע מהנתונים אם התזמון פעיל", cls: "bg-gray-100 text-gray-600 border border-gray-300" });
  } else if (!enabled) {
    chips.push({ label: "מושהה", title: "התזמון הושהה ולא ייווצרו ממנו ריצות חדשות", cls: "bg-gray-100 text-gray-600 border border-gray-300" });
  } else if (unknownNext) {
    chips.push({ label: "מועד הריצה הבאה אינו ידוע", title: "התזמון פעיל אך אין מועד ריצה מחושב", cls: "bg-amber-50 text-amber-800 border border-amber-300" });
  } else if (stale) {
    chips.push({ label: "באיחור", title: "המועד המתוזמן עבר ולא נוצרה ריצה בזמן", cls: "bg-amber-100 text-amber-800 border border-amber-300" });
  } else {
    chips.push({ label: "מתוזמן", title: "הריצה הבאה מתוכננת במועד", cls: "bg-green-100 text-green-700" });
  }
  if (failed) {
    chips.push({ label: "הריצה האחרונה נכשלה", title: "הריצה האחרונה של הבדיקה הסתיימה בכשל", cls: "bg-red-100 text-red-700 border border-red-200" });
  }

  // attention first: overdue → failed while active → unknown → on schedule → paused
  const rank = stale ? 0 : (enabled && failed) ? 1 : (!enabledKnown || unknownNext) ? 2 : enabled ? 3 : 4;
  return { chips, rank };
}

export function ScheduledOperationsSection({ agentId }) {
  const { data: allChecks, isLoading, isError, error } = useQuery({
    // Same key as the page's management section: one shared fetch and cache for the page.
    queryKey: ["scheduled-checks"],
    queryFn: scheduledChecksApi.list,
    refetchInterval: 30000,
  });
  const nowMs = Date.now();
  const forbidden = error?.response?.status === 403;

  const rows = (Array.isArray(allChecks) ? allChecks : [])
    .filter((check) => check && typeof check === "object" && check.agent_id === agentId)
    .map((check) => {
      const name = boundedText(check.name, 120) || "בדיקה ללא שם";
      const nextMs = parseTs(check.next_run_at);
      const lastMs = parseTs(check.last_run_at);
      const runId = boundedText(check.latest_run_id, 80);
      const status = boundedText(check.latest_run_status, 60);
      const { chips, rank } = scheduleChips(check, nextMs, status, nowMs);
      return {
        check, name, nextMs, lastMs, runId, status, rank, chips,
        enabledKnown: typeof check.is_enabled === "boolean",
        enabled: check.is_enabled === true,
        owner: boundedText(check.created_by, 120),
        description: boundedText(check.description, 160),
      };
    })
    .sort((a, b) => a.rank - b.rank
      || (a.nextMs == null) - (b.nextMs == null)
      || (a.nextMs ?? 0) - (b.nextMs ?? 0)
      || a.name.localeCompare(b.name));

  return (
    <div role="region" aria-label="תפעול מתוזמן" className="bg-white rounded-xl border shadow-sm p-4 space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700">תפעול מתוזמן{rows.length > 0 ? ` (${rows.length})` : ""}</h3>
      </div>
      <p className="text-[11px] text-gray-400">
        לוח הזמנים האוטומטי של העובד — מתי הריצה הבאה, מה קרה בריצה האחרונה ומה מצב התזמון. לקריאה בלבד: אין כאן הרצה, יצירה או עריכה.
      </p>

      {isLoading && <p className="text-sm text-gray-400 italic">טוען תפעול מתוזמן...</p>}

      {!isLoading && isError && (forbidden ? (
        <p role="status" className="text-sm text-gray-500 bg-gray-50 border border-gray-200 rounded px-3 py-2">
          אין הרשאה לצפות בתפעול המתוזמן (למנהלים בלבד) — מצב התזמון לא ידוע
        </p>
      ) : (
        <p role="status" className="text-sm text-red-600">לא ניתן לטעון את התפעול המתוזמן</p>
      ))}

      {!isLoading && !isError && !Array.isArray(allChecks) && (
        <p role="status" className="text-sm text-gray-500">נתוני התפעול המתוזמן אינם מזוהים — מצב התזמון לא ידוע</p>
      )}

      {!isLoading && !isError && Array.isArray(allChecks) && rows.length === 0 && (
        <p className="text-sm text-gray-400 italic">אין תפעול מתוזמן לעובד זה</p>
      )}

      {!isLoading && !isError && rows.length > 0 && (
        <>
          <ol className="space-y-2">
            {rows.map((row) => (
              <li
                key={row.check.id || row.name}
                data-testid="scheduled-op-row"
                className="rounded-lg border border-gray-100 bg-gray-50/60 p-2.5 space-y-1.5"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium text-gray-800 truncate">{row.name}</span>
                  {row.chips.map((chip) => (
                    <span key={chip.label} title={chip.title} className={`text-[11px] px-1.5 py-0.5 rounded-full whitespace-nowrap ${chip.cls}`}>
                      {chip.label}
                    </span>
                  ))}
                  <span className="text-[11px] text-gray-400 whitespace-nowrap mr-auto">{scheduleIntervalHe(row.check.interval_hours)}</span>
                </div>

                {row.description && <p className="text-xs text-gray-500">{row.description}</p>}

                <div className="text-xs text-gray-600 space-y-0.5">
                  <div>
                    {!row.enabledKnown ? "ריצה הבאה: לא ידוע"
                      : !row.enabled ? "ריצה הבאה: אין — התזמון מושהה"
                      : row.nextMs == null ? "ריצה הבאה: מועד לא ידוע"
                      : `ריצה הבאה: ${relTime(row.nextMs, nowMs)} (${fmt(row.check.next_run_at)})`}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2">
                    {row.runId || row.status ? (
                      <>
                        <span>ריצה אחרונה: {row.lastMs != null ? relTime(row.lastMs, nowMs) : "מועד לא ידוע"}</span>
                        <span className={row.status === "failed" ? "text-red-600 font-medium" : ""}>
                          {row.status ? (SCHEDULE_RUN_STATUS_HE[row.status] || row.status) : "סטטוס לא ידוע"}
                        </span>
                        {row.runId && (
                          <Link
                            to={`/agent-runs/${row.runId}`}
                            aria-label={`פתח את הריצה האחרונה של "${row.name}"`}
                            className="text-blue-600 hover:underline"
                          >
                            צפה בריצה
                          </Link>
                        )}
                      </>
                    ) : row.lastMs != null ? (
                      <span>ניסיון הרצה אחרון: {relTime(row.lastMs, nowMs)} — אין ריצה מתועדת</span>
                    ) : (
                      <span>ריצה אחרונה: טרם רצה</span>
                    )}
                  </div>
                  {row.owner && (
                    <div>אחראי: <span dir="ltr">{row.owner}</span></div>
                  )}
                </div>
              </li>
            ))}
          </ol>
          <p className="text-[11px] text-gray-400">
            מקרא: מתוזמן — הריצה הבאה מתוכננת · באיחור — המועד עבר ולא נוצרה ריצה · מושהה — התזמון הופסק · הריצה האחרונה נכשלה — הפעולה האחרונה הסתיימה בכשל
          </p>
        </>
      )}
    </div>
  );
}

// ─── Model & provider assignment (DIGEMP-PROVIDER-MODEL-ASSIGNMENT-FRONTEND-042) ──────────────
//
// Read-only "which provider and model is this employee assigned to, what is the fallback, where
// does the assignment come from, and what is the last piece of evidence" surface. The
// assignment/fallback/source facts come from the additive provider-model contract (accepted on
// the workspace payload, its agent projection, or the page's agent detail, like the other
// additive contracts). When the server has not supplied the contract, the section shows the
// model from the employee's own record plus the last observed provider/model telemetry from the
// runs this page already fetched — never an invented assignment, fallback or source. Values are
// bounded, and credential-shaped strings (pasted keys, JWTs/PEM, long base64/hex blobs) are
// refused rather than rendered — mirroring how the backend capability registry refuses
// credential-shaped model ids. The section adds no controls: changing the model or the routing
// policy stays in the existing permission-gated agent settings, and nothing here activates,
// routes, approves or edits anything.

const MP_CONTRACT_KEYS = ["provider_model", "provider_model_assignment", "model_assignment"];

const MP_PROVIDER_LABEL = {
  anthropic: "Anthropic", openai: "OpenAI", openclaw: "OpenClaw", azure: "Azure OpenAI",
  google: "Google", gemini: "Google Gemini", ollama: "Ollama (מקומי)", local: "מקומי",
};

const MP_CREDENTIAL_PREFIX_RE = /^(sk[-_]|AKIA|AIza|ghp_|github_pat_|xox|hvs\.|eyJ|Bearer\s|-----BEGIN)/;
const MP_CREDENTIAL_BLOB_RE = /^[A-Za-z0-9+/=]{40,}$/;
const MP_CREDENTIAL_HEX_RE = /^[0-9a-f]{32,}$/i;
const MP_CREDENTIAL_RUN_RE = /[A-Za-z0-9]{40,}/;

function mpCredentialShaped(value) {
  return MP_CREDENTIAL_PREFIX_RE.test(value)
    || MP_CREDENTIAL_BLOB_RE.test(value)
    || MP_CREDENTIAL_HEX_RE.test(value)
    || MP_CREDENTIAL_RUN_RE.test(value);
}

function mpSelect(source, keys, max = 80) {
  if (!source || typeof source !== "object") return null;
  for (const key of keys) {
    const value = boundedText(source[key], max);
    if (value) return value;
  }
  return null;
}

function mpSafe(value, max = 80) {
  const text = boundedText(value, max);
  if (!text) return { text: null, blocked: false };
  return mpCredentialShaped(text) ? { text: null, blocked: true } : { text, blocked: false };
}

function mpProviderLabel(value) {
  const label = MP_PROVIDER_LABEL[String(value).trim().toLowerCase()];
  return label || value;
}

function mpAssignment(raw) {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const provider = mpSelect(raw, ["provider", "provider_name", "vendor"], 80);
  const model = mpSelect(raw, ["model", "model_name", "model_id"], 80);
  if (!provider && !model) return null;
  return { provider, model };
}

function mpContract(workspace, agentDetail) {
  for (const source of [workspace, workspace?.agent, agentDetail]) {
    if (!source || typeof source !== "object") continue;
    for (const key of MP_CONTRACT_KEYS) {
      const raw = source[key];
      if (raw && typeof raw === "object" && !Array.isArray(raw)) return raw;
    }
  }
  return null;
}

function mpFallback(contract) {
  if (!contract) return { kind: "unknown" };
  const keys = ["fallback", "fallback_chain", "fallbacks", "fallback_assignment"];
  if (!keys.some((k) => k in contract)) return { kind: "unknown" };
  let value;
  for (const key of keys) {
    if (contract[key] !== undefined && contract[key] !== null) { value = contract[key]; break; }
  }
  // present but null: not supplied (unknown); explicitly empty: none configured
  if (value === undefined) return { kind: "unknown" };
  if (value === "" || (Array.isArray(value) && value.length === 0)) return { kind: "none" };
  const items = (Array.isArray(value) ? value : [value])
    .map((item) => {
      if (typeof item === "string") {
        const label = boundedText(item, 80);
        return label ? { label, provider: null, model: null } : null;
      }
      const assignment = mpAssignment(item);
      return assignment ? { label: null, provider: assignment.provider, model: assignment.model } : null;
    })
    .filter(Boolean);
  if (!items.length) return { kind: "unknown" };
  return { kind: "reported", items };
}

function mpContractEvidence(contract) {
  if (!contract) return null;
  const raw = contract.last_evidence ?? contract.last_observed ?? contract.routing_evidence ?? null;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    const flat = {
      at: mpSelect(contract, ["last_observed_at", "last_evidence_at"], 40),
      provider: mpSelect(contract, ["last_observed_provider", "last_provider"], 80),
      model: mpSelect(contract, ["last_observed_model", "last_model"], 80),
      runId: mpSelect(contract, ["last_run_id", "last_evidence_run_id"], 64),
    };
    return flat.at || flat.provider || flat.model || flat.runId ? flat : null;
  }
  const evidence = {
    at: mpSelect(raw, ["observed_at", "at", "timestamp", "time", "last_seen"], 40),
    provider: mpSelect(raw, ["provider", "provider_name"], 80),
    model: mpSelect(raw, ["model", "model_name", "model_id"], 80),
    runId: mpSelect(raw, ["run_id", "agent_run_id", "run"], 64),
  };
  return evidence.at || evidence.provider || evidence.model || evidence.runId ? evidence : null;
}

// Observed evidence fallback: the latest run that carries provider/model telemetry — a recorded
// fact from the runs the page already fetched, not a routing policy claim.
function mpRunEvidence(runs) {
  const list = (Array.isArray(runs) ? runs : [])
    .filter((run) => boundedText(run?.provider, 80) || boundedText(run?.model, 80));
  if (!list.length) return null;
  list.sort((a, b) => String(runTimestamp(b) || "").localeCompare(String(runTimestamp(a) || "")));
  return list[0];
}

function MpValue({ safe, fallbackText, provider = false }) {
  if (safe?.text) {
    return provider
      ? <span className="text-gray-700">{mpProviderLabel(safe.text)}</span>
      : <span className="font-mono" dir="ltr">{safe.text}</span>;
  }
  if (safe?.blocked) return <span className="text-amber-700">לא מוצג — הערך בצורת סוד</span>;
  return <span className="text-gray-400">{fallbackText}</span>;
}

export function ModelProviderAssignment({ workspace, agentDetail, agent, runs = [] }) {
  const contract = mpContract(workspace, agentDetail);
  const active = contract
    ? (mpAssignment(contract.active)
      || mpAssignment(contract.current)
      || mpAssignment(contract.assignment)
      || mpAssignment(contract))
    : null;
  const recordModel = contract ? null : mpSafe(agent?.model || agentDetail?.model);
  const fallback = mpFallback(contract);
  const source = contract
    ? mpSafe(mpSelect(contract, ["source", "policy_source", "assignment_source", "assigned_via", "configured_via", "origin"], 120))
    : null;
  const updatedAt = contract ? mpSelect(contract, ["updated_at", "assigned_at", "updated_on"], 40) : null;
  const contractEvidence = mpContractEvidence(contract);
  const runEvidence = contractEvidence ? null : mpRunEvidence(runs);

  return (
    <section aria-label="מודל וספק" className="bg-white rounded-xl border shadow-sm p-4 space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700">מודל וספק</h3>
        <span className="text-[10px] rounded-full border border-gray-200 bg-gray-100 text-gray-600 px-1.5 py-0.5">תצוגה בלבד</span>
      </div>
      <p className="text-[11px] text-gray-400">
        השיוך כפי שמדווח על-ידי השירות; כשלא דווח חוזה שיוך — מוצגות רשומת העובד והראיות הנצפות בלבד, בלי להמציא מדיניות.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <div data-testid="active-assignment" className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-1.5">
          <h4 className="text-xs font-semibold text-gray-700">שיוך פעיל</h4>
          {active ? (
            <>
              <div className="text-sm text-gray-700">ספק: <MpValue safe={mpSafe(active.provider)} fallbackText="ספק לא נמסר בשיוך" provider /></div>
              <div className="text-sm text-gray-700">מודל: <MpValue safe={mpSafe(active.model)} fallbackText="מודל לא נמסר בשיוך" /></div>
            </>
          ) : contract ? (
            <p className="text-sm text-gray-400 italic">לא ידוע — השיוך הפעיל טרם דווח על-ידי השרת</p>
          ) : recordModel && (recordModel.text || recordModel.blocked) ? (
            <>
              <div className="text-sm text-gray-700">ספק: <span className="text-gray-400">ספק לא מתועד ברשומת העובד</span></div>
              <div className="text-sm text-gray-700">מודל: <MpValue safe={recordModel} fallbackText="מודל לא מתועד" /></div>
              <p className="text-[11px] text-gray-400">לפי רשומת העובד — לא התקבל חוזה שיוך מהשרת</p>
            </>
          ) : (
            <p className="text-sm text-gray-400 italic">לא ידוע — לא הוקצה מודל מתועד ברשומת העובד</p>
          )}
        </div>

        <div data-testid="fallback-assignment" className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-1.5">
          <h4 className="text-xs font-semibold text-gray-700">גיבוי (Fallback)</h4>
          {fallback.kind === "reported" ? (
            <ul className="space-y-1">
              {fallback.items.map((item, i) => (
                <li key={i} className="text-sm text-gray-700">
                  {item.label != null ? (
                    <MpValue safe={mpSafe(item.label)} fallbackText="—" />
                  ) : (
                    <>
                      <MpValue safe={mpSafe(item.model)} fallbackText="מודל לא נמסר" />
                      {item.provider ? <> · <span className="text-gray-500">{mpProviderLabel(item.provider)}</span></> : null}
                    </>
                  )}
                </li>
              ))}
            </ul>
          ) : fallback.kind === "none" ? (
            <p className="text-sm text-gray-400 italic">אין גיבוי מוגדר (דווח במפורש)</p>
          ) : (
            <p className="text-sm text-gray-400 italic">לא ידוע — מדיניות גיבוי טרם דווחה על-ידי השרת</p>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-1.5">
          <h4 className="text-xs font-semibold text-gray-700">מקור השיוך</h4>
          {source && (source.text || source.blocked) ? (
            <>
              <div className="text-sm text-gray-700"><MpValue safe={source} fallbackText="" /></div>
              {updatedAt && <p className="text-[11px] text-gray-400">עודכן: {fmt(updatedAt)}</p>}
            </>
          ) : (
            <p className="text-sm text-gray-400 italic">לא ידוע — מקור השיוך טרם דווח</p>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-1.5">
          <h4 className="text-xs font-semibold text-gray-700">ראיה אחרונה</h4>
          {contractEvidence ? (
            <>
              <div className="text-sm text-gray-700">
                {contractEvidence.provider || contractEvidence.model ? (
                  <>
                    <MpValue safe={mpSafe(contractEvidence.provider)} fallbackText="ספק לא תועד" provider />
                    {" · "}
                    <MpValue safe={mpSafe(contractEvidence.model)} fallbackText="מודל לא תועד" />
                  </>
                ) : (
                  <span className="text-gray-500">דווח ללא פרטי ספק/מודל</span>
                )}
              </div>
              {contractEvidence.at && <p className="text-[11px] text-gray-400">נצפה: {fmt(contractEvidence.at)}</p>}
              {contractEvidence.runId && (
                <Link
                  to={`/agent-runs/${contractEvidence.runId}`}
                  aria-label="פתח את הריצה של הראיה שדווחה"
                  className="text-xs text-blue-600 hover:underline"
                >
                  לריצה של הראיה
                </Link>
              )}
            </>
          ) : runEvidence ? (
            <>
              <div className="text-sm text-gray-700">
                נצפה בריצה: <MpValue safe={mpSafe(runEvidence.provider)} fallbackText="ספק לא תועד" provider />
                {" · "}
                <MpValue safe={mpSafe(runEvidence.model)} fallbackText="מודל לא תועד" />
              </div>
              {runTimestamp(runEvidence) && <p className="text-[11px] text-gray-400">{fmt(runTimestamp(runEvidence))}</p>}
              {runEvidence.id && (
                <Link
                  to={`/agent-runs/${runEvidence.id}`}
                  aria-label="פתח את הריצה שממנה נלקחה הראיה האחרונה"
                  className="text-xs text-blue-600 hover:underline"
                >
                  לריצה שממנה הראיה
                </Link>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-400 italic">אין עדיין ריצות עם טלמטריית ספק/מודל</p>
          )}
        </div>
      </div>

      <p className="text-[11px] text-gray-400 border-t border-gray-100 pt-2">
        לקריאה בלבד — הקטע אינו משנה שיוך, מדיניות ניתוב או הפעלת ספק. עריכת המודל נעשית בהגדרות העובד לפי הרשאות.
      </p>
    </section>
  );
}

// ─── Employee flow map — "מפת זרימה: איך העבודה עוברת" (DIGEMP-SYSTEM-MAP-FINAL-POLISH-123) ────
//
// Read-only explainability surface of the finished digital-employee flow: intake/routing, who is
// handling (employee or human), conversation/context, skills & knowledge, connections, approval,
// the three execution paths (API / browser / remote), evidence verification and governed
// learning/profile updates. Every live status is derived from a contract this screen already
// loads — the agent-workspace payload, the connection-readiness projection, the connections-health
// boundary and the agent's run records — and a source that is missing, loading or failed degrades
// to an explicit unknown, never to a fabricated zero or a false green. The map explains only: it
// never starts, approves, grants or changes anything.

// Flow-stage chips draw their tone from the shared live-status vocabulary — the same table the
// System Map renders — so one status key never carries two different tones across the surfaces.
const flowChip = (key, label) => ({ key, label, cls: LIVE_STATUS_CLS[key] || LIVE_STATUS_CLS.unknown });

const flowRemoteState = (workspace, agentDetail) => {
  const raw = workspace?.remote_readiness ?? workspace?.agent?.remote_readiness ?? agentDetail?.remote_readiness ?? null;
  const contract = raw && typeof raw === "object" && !Array.isArray(raw) ? raw : null;
  const stateRaw = contract ? pickField(contract, ["state", "status", "readiness"]) : undefined;
  return normalizeReadinessState(stateRaw) || "unknown";
};

export function deriveEmployeeFlowMap({
  workspace = null,
  agentDetail = null,
  runs = [],
  runsLoading = false,
  runsError = false,
  connectionsHealth = null,
  connectionsHealthFailed = false,
  readiness = null,
  skillState = null,
  knowledge = null,
  learningHistory = null,
} = {}) {
  const agent = workspace?.agent || {};
  const assigned = Array.isArray(workspace?.assigned_tickets) ? workspace.assigned_tickets : [];
  const currentWork = workspace?.current_work || null;
  const conversations = Array.isArray(workspace?.recent_conversations) ? workspace.recent_conversations : [];
  const connections = Array.isArray(workspace?.capabilities?.connections) ? workspace.capabilities.connections : [];
  const tools = Array.isArray(workspace?.capabilities?.tools) ? workspace.capabilities.tools : [];
  // "no tools" and "the payload never listed tools" are different states; stage 7 must not merge them
  const toolsReported = Array.isArray(workspace?.capabilities?.tools);
  // Both stages that name the tool list as this employee's authority carry the same caveat: the
  // handling rule states the boundary as runtime behaviour, and the execution row counts the list
  // as an available path. Whatever the stage, the list means the same thing.
  const entitlementNote = entitlementBoundaryNote(workspace?.capabilities);
  // Stage 6 states the approval's payload binding as this deployment's runtime rule. Whether that
  // binding is enforced is a separate switch the screen cannot read, so the server states it on the
  // same payload, and the stage carries what the rule currently amounts to. One wording, one owner
  // (approvalBindingVocabulary) — never restated per surface.
  const approvalBindingNotice = approvalBindingNote(workspace?.capabilities);
  const summary = workspace?.agent ? deriveOperatorSummary(workspace) : null;
  const attention = workspace?.agent ? deriveAttentionItems(workspace) : [];

  // ── shared conversation counters (stages 3 and 6) ──
  const openConversations = conversations.filter((c) => c.status !== "closed").length;
  const awaitingHumanConversations = conversations.filter((c) => c.status === "awaiting_human").length;
  const awaitingApprovalConversations = conversations.filter((c) => c.status === "awaiting_approval").length;

  // ── stage 1: intake & routing ──
  const intakeStage = {
    id: "intake",
    icon: "📨",
    label: "קליטה וניתוב",
    chip: currentWork
      ? flowChip("active", "עובד על משימה משויכת")
      : assigned.length > 0
        ? flowChip("active", oneMany(assigned.length, "קריאה אחת ממתינה", `${assigned.length} קריאות ממתינות`))
        : flowChip("idle", "אין עבודה משויכת"),
    rows: [
      { label: "עבודה משויכת לעובד", value: assigned.length === 0 ? "אין" : String(assigned.length) },
      {
        label: "פעיל כרגע",
        value: currentWork
          ? currentWork.ticket_number ? `קריאה #${currentWork.ticket_number}` : "משימה מתוך שיחה"
          : "לא",
      },
      { label: "מצב העובד", value: agent.is_enabled === false ? "מושבת — לא ניתן לשייך עבודה חדשה" : "מופעל" },
    ],
    rules: [
      "פנייה חדשה נכנסת מהאימייל או נפתחת ידנית במסך הקריאות, ומשויכת לעובד לפי תחום, זמינות ועומס.",
      "העובד לא נוגע בקריאה עד שמפעיל מפעיל אותו עליה — ידנית או דרך טריגר מוגדר.",
      "השיוך נשאר החלטה של מפעיל; המפה מציגה מה משויך לעובד ומה ממתין.",
    ],
    links: [{ to: "/tickets", label: "למסך הקריאות" }],
    sources: ["GET /api/agents/:id/workspace"],
  };

  // ── stage 2: handling — employee or human ──
  const attentionCount = attention.length;
  const handlingStage = {
    id: "handling",
    icon: "🧭",
    label: "טיפול — עובד או אדם",
    chip: attentionCount > 0
      ? flowChip("waiting_human", oneMany(attentionCount, "פריט אחד ממתין לאדם", `${attentionCount} פריטים ממתינים לאדם`))
      : currentWork
        ? flowChip("active", "העובד מטפל כעת")
        : flowChip("idle", "פנוי — אין עבודה פעילה"),
    rows: [
      {
        label: "מצב העובד",
        value: summary ? `${summary.label}${summary.detail ? ` — ${summary.detail}` : ""}` : "לא ידוע",
      },
      { label: "ממתין להחלטת אדם", value: attentionCount === 0 ? "אין" : String(attentionCount) },
      {
        label: "מי מטפל כעת",
        value: currentWork ? "העובד הדיגיטלי" : attentionCount > 0 ? "ממתין לאדם — טכנאי או מנהל" : "אין עבודה פעילה",
      },
    ],
    rules: [
      "העובד הדיגיטלי מטפל במשימות באופן עצמאי — בגבולות ההרשאה שהוענקה לו, עם כלים מורשים בלבד.",
      "כל נקודת החלטה עוצרת את העבודה ומעבירה אותה לאדם: סקירת טכנאי, אישור פעולה, תקלה או שאלה ללקוח.",
      "הרשימה שממתינה לאדם היא תור הניהול; עבודה לא ממשיכה בלעדיו, והעובד אינו מאשר פעולות לעצמו.",
    ],
    note: entitlementNote,
    noteTestId: "entitlement-note-flow-stage",
    links: [{ to: "/attention-inbox", label: "לתיבת תשומת הלב" }],
    sources: ["GET /api/agents/:id/workspace"],
  };

  // ── stage 3: conversation & context ──
  const conversationStage = {
    id: "conversation",
    icon: "💬",
    label: "שיחה והקשר",
    chip: awaitingApprovalConversations > 0
      ? flowChip("approval_required", "שיחה ממתינה לאישור")
      : awaitingHumanConversations > 0
        ? flowChip("waiting_human", "שיחה ממתינה לטכנאי")
        : openConversations > 0
          ? flowChip("active", oneMany(openConversations, "שיחה פתוחה אחת", `${openConversations} שיחות פתוחות`))
          : flowChip("idle", "אין שיחות פתוחות"),
    rows: [
      { label: "שיחות אחרונות", value: conversations.length === 0 ? "אין" : String(conversations.length) },
      { label: "פתוחות", value: String(openConversations) },
      { label: "ממתינות לטכנאי", value: String(awaitingHumanConversations) },
      { label: "ממתינות לאישור", value: String(awaitingApprovalConversations) },
    ],
    rules: [
      "השיחה היא ערוץ התיאום בין הלקוח, הטכנאי והעובד — כל חילופי המידע מתועדים בה.",
      "ההקשר של העובד (קריאה, שיחה ולקוח) הוא מה שהוא רואה — אין הקשר נסתר ואין הרשאה רחבה ממנו.",
      "תוצאות הביצוע והראיות מתועדות לתוך השיחה, כך שאפשר לשחזר מה קרה ומתי.",
    ],
    links: [{ to: "/conversations", label: "למסך השיחות" }],
    sources: ["GET /api/agents/:id/workspace"],
  };

  // ── stage 4: skills & knowledge ──
  const skillsNormalized = normalizeSkillAssignmentState(skillState);
  const skillsList = skillsNormalized && Array.isArray(skillsNormalized.skills) ? skillsNormalized.skills : null;
  const availableSkills = skillsList ? skillsList.filter((s) => s.statusKey === "active").length : null;
  const approvedKnowledgeCount = knowledge?.approved?.supplied ? knowledge.approved.items.length : null;
  const candidateCount = knowledge?.candidates?.supplied ? knowledge.candidates.items.length : null;
  const declaredSpecialties = Array.isArray(agent.specialties) ? agent.specialties : null;
  const skillsStage = {
    id: "skills",
    icon: "🎯",
    label: "כישורים וידע",
    chip: availableSkills === null
      ? flowChip("no_evidence", "מצב הכישורים טרם דווח")
      : availableSkills > 0
        ? flowChip("active", oneMany(availableSkills, "כישור אחד זמין", `${availableSkills} כישורים זמינים`))
        : flowChip("idle", "אין כישורים זמינים"),
    rows: [
      {
        label: "תחומים מוצהרים",
        value: declaredSpecialties === null
          ? "לא ידוע"
          : declaredSpecialties.length === 0
            ? "טרם הוצהרו"
            : declaredSpecialties.slice(0, 4).map((s) => boundedText(s, 40)).filter(Boolean).join(" · "),
      },
      { label: "כישורים משויכים", value: skillsList === null ? "טרם דווח" : String(skillsList.length) },
      { label: "זמינים לעבודה", value: availableSkills === null ? "טרם דווח" : String(availableSkills) },
      { label: "ידע מאושר בבסיס הידע", value: approvedKnowledgeCount === null ? "טרם דווח" : String(approvedKnowledgeCount) },
      { label: "מועמדים ללמידה שטרם אושרו", value: candidateCount === null ? "טרם דווח" : String(candidateCount) },
    ],
    rules: [
      "מה שהעובד יודע: תחומים מוצהרים, כישורים משויכים ורמות נמדדות — הצהרה היא לא מדידה.",
      "מותר לעובד להסתמך על בסיס הידע המאושר בלבד; מועמדים שטרם אושרו אינם בשימוש.",
      "כישור אינו הרשאה: השיוך אינו מעניק כלים, חיבורים או אישור ואינו מרחיב את רמת ההרשאה.",
    ],
    links: [{ to: "/knowledge", label: "לבסיס הידע" }],
    sources: ["GET /api/agents/:id/workspace"],
  };

  // ── stage 5: connections ──
  const readinessSummaryRaw = readiness && typeof readiness === "object" && readiness.summary && typeof readiness.summary === "object"
    ? readiness.summary
    : null;
  // Only trust the projection when it carries a usable total. Coercing an absent or null total
  // with Number() yields 0 and would fabricate "no systems configured", so this tests for an
  // actual number; an unusable summary falls back to the connection records the page already
  // holds, exactly like a failed projection read.
  const readinessTotal = typeof readinessSummaryRaw?.total === "number" ? readinessSummaryRaw.total : NaN;
  const readinessSummary = Number.isFinite(readinessTotal) && readinessTotal >= 0
    ? readinessSummaryRaw
    : null;
  const systemCount = readinessSummary ? readinessTotal : connections.length;
  const healthySystems = readinessSummary
    ? Number(readinessSummary.healthy) || 0
    : connections.filter((c) => connectionStatus(c) === CONNECTION_STATUS.ok).length;
  // Anything the projection does not count as healthy is not ready, including the `unknown`
  // bucket the server uses for a connection whose health was never verified. Deriving the gap
  // from total - healthy rather than summing only the unavailable/needs_attention buckets keeps
  // this chip consistent with the "healthy of total" row below it, so a never-tested connection
  // can never render as a green "everything is connected".
  const problematicSystems = Math.max(systemCount - healthySystems, 0);
  const connectionsStage = {
    id: "connections",
    icon: "🔌",
    label: "חיבורים למערכות",
    chip: systemCount === 0
      ? flowChip("idle", "אין מערכות מוגדרות")
      : problematicSystems > 0
        ? flowChip("requires_configuration", oneMany(problematicSystems, "מערכת אחת דורשת טיפול", `${problematicSystems} מערכות דורשות טיפול`))
        : flowChip("active", "כל המערכות המוגדרות מחוברות"),
    rows: [
      { label: "מערכות מוכנות", value: `${healthySystems} מתוך ${systemCount}` },
      {
        label: "מערכות שזוהו",
        value: connections.length === 0
          ? "אין מערכות מחוברות"
          : connections
              .slice(0, 5)
              .map((c) => `${systemTypeLabel(c.system_type)} — ${connectionStatus(c).label}`)
              .join(" · "),
      },
      {
        label: "מקור מצב המוכנות",
        value: readinessSummary
          ? "דוח מוכנות החיבורים — תוצאת הבדיקה האחרונה שנשמרה"
          : "רשומת החיבורים בעמוד העובד",
      },
    ],
    rules: [
      "חיבור מוגדר הוא תנאי לביצוע — אבל לא הרשאה ולא ביצוע: כל שימוש במערכת חיצונית עובר הרשאות ואישור.",
      "מצב המוכנות משקף את תוצאת בדיקת החיבור האחרונה שנשמרה — בלי בדיקה חיה ובלי ניחוש.",
      "מערכת שלא מחוברת חוסמת פעולות שמסתמכות עליה עד לתיקון החיבור.",
    ],
    links: [{ to: "/connections", label: "למסך החיבורים" }],
    sources: ["GET /api/agents/:id/connection-readiness", "GET /api/agents/:id/workspace"],
  };

  // ── stage 6: approval ──
  const pendingApprovalItems = attention.filter((item) => item.severity === "approval").length;
  const pendingApprovalTotal = pendingApprovalItems + awaitingApprovalConversations;
  const approvalStage = {
    id: "approval",
    icon: "✋",
    label: "אישור פעולות",
    chip: pendingApprovalTotal > 0
      ? flowChip("approval_required", oneMany(pendingApprovalTotal, "פריט אחד ממתין לאישור", `${pendingApprovalTotal} פריטים ממתינים לאישור`))
      : flowChip("idle", "אין המתנה לאישור"),
    rows: [
      { label: "פעולות שממתינות להחלטת אדם", value: String(pendingApprovalItems) },
      { label: "שיחות שממתינות לאישור", value: String(awaitingApprovalConversations) },
      { label: "מי מחליט", value: "טכנאי מורשה, במסך הריצה — העובד אינו מאשר לעצמו" },
    ],
    rules: [
      "פעולה חיצונית או שינוי מצב אינם מתבצעים על ידי העובד לבדו: הם מוצגים לאישור אדם עם רמת הסיכון שלהם.",
      "האישור נקשר לפעולה, ליעד ולקלט המדויקים; שינוי באחד מהם מחייב אישור מחדש.",
      "ההחלטה מתקבלת במסך הריצה עם ההקשר המלא — במפה אין כפתור אישור או דחייה.",
    ],
    note: approvalBindingNotice,
    noteTestId: "approval-binding-note-flow-stage",
    links: [{ to: "/agent-runs", label: "למסך הריצות" }],
    sources: ["GET /api/agents/:id/workspace"],
  };

  // ── stage 7: execution — API / browser / remote ──
  const browserBoundary = connectionsHealth && typeof connectionsHealth === "object"
    && connectionsHealth.browser_worker && typeof connectionsHealth.browser_worker === "object"
    ? connectionsHealth.browser_worker
    : null;
  const remoteState = flowRemoteState(workspace, agentDetail);
  const transports = observedTransports(connections);
  const remoteKnown = remoteState !== "unknown";
  // Each path contributes a boolean only once its own source reported, and null while it has not.
  // The tools path is known only when the workspace payload actually carries a capabilities.tools
  // list: an absent list is not evidence of zero authorized tools. Coercing it to a plain boolean
  // made `knownPaths` never empty, which left the "state not reported" chip below unreachable and
  // let the chip assert a definite "no path available" while the connections-health read was still
  // in flight or had failed — the same fabricated certainty the readiness chip disclaims, in red.
  // The browser path is known only when the boundary actually reports its authority boolean
  // (execution_available, connections.browser_worker_capability()); a boundary object without it
  // is a contract the map does not understand, not a reported block.
  const pathKnowledge = [
    toolsReported ? tools.length > 0 : null,
    typeof browserBoundary?.execution_available === "boolean" ? browserBoundary.execution_available : null,
    remoteKnown ? remoteState === "ready" : null,
  ];
  const knownPaths = pathKnowledge.filter((value) => value !== null);
  const availablePaths = knownPaths.filter(Boolean).length;
  const executionStage = {
    id: "execution",
    icon: "⚙️",
    label: "ביצוע — API, דפדפן ומרחוק",
    chip: availablePaths > 0
      ? flowChip("active", `${availablePaths} מתוך 3 מסלולים זמינים`)
      : knownPaths.length < pathKnowledge.length
        ? flowChip("no_evidence", "מצב המסלולים טרם דווח")
        : flowChip("blocked", "אין מסלול ביצוע זמין כרגע"),
    rows: [
      {
        label: "כלים ו-API",
        value: !toolsReported
          ? "טרם דווח"
          : tools.length > 0 ? `${tools.length} כלים מורשים` : "אין כלים מורשים",
      },
      {
        label: "דפדפן (Browser Worker)",
        value: browserBoundary
          ? typeof browserBoundary.execution_available === "boolean"
            ? browserBoundary.execution_available === true
              ? `קריאה חיה (קריאה בלבד) זמינה${browserBoundary.approval_required ? " · דורש אישור אנושי לפני ביצוע" : ""} · כתיבה מול יעד אמיתי חסומה בכל תצורה`
              : `קריאה חיה לא זמינה${boundedText(browserBoundary.blocked_reason, 120) ? ` — ${browserCapabilityBlockedLabel(browserBoundary.blocked_reason)}` : ""} · כתיבה מול יעד אמיתי חסומה בכל תצורה`
            : "מצב הדפדפן לא דווח — לא מוצג ניחוש"
          : connectionsHealthFailed
            ? "מצב הדפדפן לא נטען — לא מוצג ניחוש"
            : "טוען מצב דפדפן…",
      },
      {
        label: "מרחוק (Remote)",
        value: remoteState === "ready" ? "מוכן לפעולות מרחוק" : remoteState === "not_ready" ? "לא מוכן כרגע" : "טרם דווח",
      },
      {
        label: "אמצעי חיבור שזוהו",
        value: transports.length === 0
          ? "לא זוהו חיבורי TeamViewer או OpenClaw מוגדרים"
          : transports.slice(0, 3).map((t) => `${t.name} — ${TRANSPORT_STATUS_META[t.status].label}`).join(" · "),
      },
    ],
    rules: [
      "שלושת המסלולים — כלים ו-API, דפדפן ומרחוק — עוברים בנתיב הפעולה הקנוני: תכנון, מדיניות וסיכון, אישור כשנדרש, הרשאה מוגבלת בזמן, כלי מבוקר ובקרת תוצאה.",
      "עבודת דפדפן מתבצעת רק בבקשה מפורשת שסומנה כדפדפן — אין נפילה שקטה לנתיב ה-API ולהפך.",
      "מסלול הדפדפן החי הוא קריאה בלבד: כתיבה מול יעד אמיתי אינה זמינה בכל תצורה, ואין שחזור אוטומטי — היפוך שינוי, אם יידרש, הוא ידני בלבד.",
      "סשן מרחוק נפתח ביוזמת מפעיל בלבד, דרך חיבור TeamViewer מאושר; אין חיבור מרחוק אוטומטי.",
      "מצב חירום חוסם פעולות כתיבה ברמת המערכת, והמצב המוצג כאן משקף זאת.",
    ],
    note: entitlementNote,
    noteTestId: "entitlement-note-flow-stage",
    links: [
      { to: "/connections", label: "למצב החיבורים" },
      { to: "/agent-runs", label: "למסך הריצות" },
    ],
    sources: ["GET /api/connections/health", "GET /api/agents/:id/workspace"],
  };

  // ── stage 8: verification & evidence ──
  const outcomes = (Array.isArray(runs) ? runs : []).map((run) => outcomeOf(run)).filter(Boolean);
  const verifiedOutcomes = outcomes.filter((outcome) => outcome === "verified").length;
  const unverifiedOutcomes = outcomes.filter((outcome) => outcome === "unverified").length;
  const failedOutcomes = outcomes.filter((outcome) => outcome === "failed").length;
  const cancelledOutcomes = outcomes.filter((outcome) => outcome === "cancelled").length;
  const verificationStage = {
    id: "verification",
    icon: "✅",
    label: "אימות וראיות",
    chip: runsLoading
      ? flowChip("loading", "טוען תוצאות…")
      : runsError
        ? flowChip("unknown", "מקור הריצות לא נטען")
        : verifiedOutcomes > 0
          ? flowChip("active", oneMany(verifiedOutcomes, "עבודה מאומתת אחת", `${verifiedOutcomes} עבודות מאומתות`))
          : outcomes.length > 0
            ? flowChip("no_evidence", "טרם אומתו עבודות")
            : flowChip("idle", "אין עבודות שהסתיימו"),
    rows: [
      { label: "עבודות מאומתות (אושרו על ידי טכנאי)", value: String(verifiedOutcomes) },
      { label: "הסתיימו בלי אימות טכנאי", value: String(unverifiedOutcomes) },
      { label: "נכשלו", value: String(failedOutcomes) },
      { label: "נדחו", value: String(cancelledOutcomes) },
    ],
    rules: [
      "לא כל סיום נחשב הצלחה: רק עבודה שטכנאי אישר נחשבת מאומתת.",
      "עבודה שהסתיימה בלי אימות מוצגת כטרם אומתה — לעולם לא כהצלחה.",
      "הראיות — תוצאת הריצה ואישור הטכנאי — נשמרות ברשומת הריצה; הפירוט המלא מופיע ב״ניסיון ותוצאות״ בהמשך העמוד.",
    ],
    links: [{ to: "/agent-runs", label: "למסך הריצות" }],
    sources: ["GET /api/agent-runs/?agent_id=…"],
  };

  // ── stage 9: governed learning & profile updates ──
  const history = normalizeLearningHistory(learningHistory);
  const changesCount = history && Array.isArray(history.changes) ? history.changes.length : null;
  const learningStage = {
    id: "learning",
    icon: "📈",
    label: "למידה ופרופיל",
    chip: changesCount === null
      ? flowChip("no_evidence", history ? "שינויי למידה טרם דווחו" : "מצב הלמידה טרם דווח")
      : changesCount > 0
        ? flowChip("active", oneMany(changesCount, "שינוי למידה אחד מתועד", `${changesCount} שינויי למידה מתועדים`))
        : flowChip("idle", "אין שינויים מתועדים"),
    rows: [
      { label: "שינויים מתועדים בהיסטוריית הלמידה", value: changesCount === null ? "טרם דווח" : String(changesCount) },
      { label: "מועמדים ללמידה שטרם אושרו", value: candidateCount === null ? "טרם דווח" : String(candidateCount) },
      { label: "ידע מאושר בבסיס הידע", value: approvedKnowledgeCount === null ? "טרם דווח" : String(approvedKnowledgeCount) },
    ],
    rules: [
      "משובי טכנאים, תיקונים ופערי ידע הופכים למועמדים ללמידה — ורק ידע שאושר בידי אדם נכנס לשימוש.",
      "שינויי כישורים ופרופיל נשמרים כגרסאות מבוססות ראיות; הצהרה עצמית אינה נחשבת למיומנות.",
      "הלמידה משנה מה שהעובד יודע ומסוגל לעשות — לא מה שהוא רשאי לעשות: הרשאות נשארות במקומן.",
    ],
    links: [{ to: "/knowledge", label: "לבסיס הידע" }],
    sources: ["GET /api/agents/:id/workspace"],
  };

  return [
    intakeStage,
    handlingStage,
    conversationStage,
    skillsStage,
    connectionsStage,
    approvalStage,
    executionStage,
    verificationStage,
    learningStage,
  ];
}

export function EmployeeFlowMapSection({
  agentId,
  workspace,
  agentDetail = null,
  runs = [],
  runsLoading = false,
  runsError = false,
  skillState = null,
  knowledge = null,
  learningHistory = null,
}) {
  const [activeStageId, setActiveStageId] = useState("intake");

  // Same query keys the sibling sections already mount — shared cache, no extra request. The
  // browser boundary is the authoritative connections-health contract; the readiness projection
  // is the same one the systems section below renders.
  const healthQuery = useQuery({
    queryKey: ["connectionsHealth"],
    queryFn: () => {
      if (typeof connectionsApi?.health !== "function") throw new Error("connections health API unavailable");
      return connectionsApi.health();
    },
    staleTime: 60_000,
    retry: false,
  });
  const readinessQuery = useQuery({
    queryKey: connectionReadinessQueryKey(agentId),
    queryFn: () => api.get(`/api/agents/${agentId}/connection-readiness`).then((r) => r.data),
    enabled: !!agentId,
  });

  const stages = deriveEmployeeFlowMap({
    workspace,
    agentDetail,
    runs,
    runsLoading,
    runsError,
    connectionsHealth: healthQuery.data || null,
    connectionsHealthFailed: healthQuery.isError,
    readiness: readinessQuery.data || null,
    skillState,
    knowledge,
    learningHistory,
  });
  const activeStage = stages.find((stage) => stage.id === activeStageId) || stages[0];

  return (
    <section
      id="employee-flow"
      data-testid="employee-flow-map"
      aria-label="מפת זרימה — איך העבודה עוברת"
      className="bg-white rounded-xl border shadow-sm p-4 space-y-3 scroll-mt-24"
    >
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-700">מפת זרימה — איך העבודה עוברת</h3>
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-50 text-gray-500 border border-gray-200">
          הסבר בלבד · קריאה בלבד
        </span>
        <span className="text-[11px] text-gray-400 mr-auto">מצבים חיים מהמקורות הקיימים במסך · מקור חסר מוצג כ״לא ידוע״</span>
      </div>
      <p className="text-[11px] text-gray-400">
        מהפנייה שנכנסת ועד ללמידה: כל שלב מוסבר בקצרה, ומה שמתרחש בו בפועל מוצג לפי אותם נתונים שהעמוד כבר מציג.
        המפה לא מפעילה עבודה, לא מאשרת פעולות ולא משנה הרשאות.
      </p>

      <div role="group" aria-label="שלבי הזרימה" className="flex flex-wrap items-stretch gap-1.5">
        {stages.map((stage, index) => {
          const isActive = stage.id === activeStage.id;
          return (
            <span key={stage.id} className="flex items-stretch gap-1.5">
              {index > 0 && <span aria-hidden="true" className="self-center text-gray-300">←</span>}
              <button
                type="button"
                aria-expanded={isActive}
                aria-controls={isActive ? `flow-stage-panel-${stage.id}` : undefined}
                onClick={() => setActiveStageId(stage.id)}
                className={`flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs transition-colors ${
                  isActive ? "border-blue-300 bg-blue-50" : "border-gray-200 bg-white hover:bg-gray-50"
                }`}
              >
                <span aria-hidden="true">{stage.icon}</span>
                <span className="font-medium text-gray-800">{stage.label}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full border whitespace-nowrap ${stage.chip.cls}`}>
                  {stage.chip.label}
                </span>
              </button>
            </span>
          );
        })}
      </div>

      <div
        id={`flow-stage-panel-${activeStage.id}`}
        role="region"
        aria-label={`שלב במפה: ${activeStage.label}`}
        data-testid={`flow-stage-${activeStage.id}`}
        className="rounded-lg border border-gray-200 bg-gray-50/60 p-3 space-y-2.5"
      >
        <ul className="space-y-1 text-sm text-gray-700">
          {activeStage.rules.map((rule, index) => (
            <li key={index} className="flex gap-1.5">
              <span aria-hidden="true" className="text-gray-400">•</span>
              <span>{rule}</span>
            </li>
          ))}
        </ul>
        {/* Two different disclosures can occupy this one slot on different stages, so the note
            carries its own testid: a pin on one of them must not be satisfiable by the other. */}
        {activeStage.note && (
          <p data-testid={activeStage.noteTestId} className="text-[11px] text-amber-700">
            {activeStage.note}
          </p>
        )}
        <div className="space-y-0.5">
          {activeStage.rows.map((row) => (
            <div
              key={row.label}
              className="flex flex-wrap items-start justify-between gap-x-4 gap-y-0.5 border-b border-gray-100 last:border-0 py-1"
            >
              <span className="text-xs text-gray-500">{row.label}</span>
              <span className="text-xs text-gray-800">{row.value}</span>
            </div>
          ))}
        </div>
        {activeStage.links.length > 0 && (
          <p className="flex flex-wrap gap-3 text-xs">
            {activeStage.links.map((link) => (
              <Link key={link.to} to={link.to} className="text-blue-600 hover:underline">
                {link.label}
              </Link>
            ))}
          </p>
        )}
        <p className="text-[11px] text-gray-400">
          מקורות מצב חיים:{" "}
          {activeStage.sources.map((source, index) => (
            <span key={source}>
              {index > 0 && " · "}
              <span dir="ltr" className="font-mono">{source}</span>
            </span>
          ))}
        </p>
      </div>
    </section>
  );
}

export default function EmployeeWorkspace({ agentId, agentDetail, customerId = null, runs = [], runsLoading = false, runsError = false }) {
  const { can: canAgent } = useAgentPermissions();
  const canView = canAgent("agents_view");
  const canRun = canAgent("agents_run");

  // A conversation picked from the recent list, shown in the transcript in place of the composer.
  const [openConversationId, setOpenConversationId] = useState(null);
  // A conversation ConversationPanel just created in the composer. It is re-keyed only on explicit
  // picks/hides — re-mounting the panel the moment it creates would race the first posted message
  // against the transcript reload. Until the next 15s workspace refetch this object is also the
  // source for the state chip / ticket context.
  const [createdConversation, setCreatedConversation] = useState(null);
  // Incremented when the operator hides a conversation, so the composer starts a genuinely NEW one.
  const [sessionSeq, setSessionSeq] = useState(0);
  const conversationRef = useRef(null);

  const { data: workspace, isLoading, isError, refetch } = useQuery({
    queryKey: ["agent-workspace", agentId],
    queryFn: () => agentsApi.workspace(agentId),
    refetchInterval: 15000,
  });

  // The page is reused across /agents/:a → /agents/:b navigations: do not carry one employee's
  // open conversation into another employee's workspace.
  useEffect(() => {
    setOpenConversationId(null);
    setCreatedConversation(null);
  }, [agentId]);

  useEffect(() => {
    if (!openConversationId) return;
    conversationRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [openConversationId]);

  if (isLoading) return <div className="text-sm text-gray-400">טוען את סביבת העבודה...</div>;
  if (isError || !workspace) {
    return (
      <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
        לא ניתן לטעון את סביבת העבודה
        <button type="button" onClick={() => refetch()} className="mr-2 underline">נסה שוב</button>
      </div>
    );
  }

  const { agent, current_work, assigned_tickets, recent_conversations, activity, capabilities } = workspace;
  const status = STATUS_META[agent.effective_status];
  const summary = deriveOperatorSummary(workspace);
  const summaryStyle = SUMMARY_STYLE[summary.state];
  // Growth Profile is an additive contract: whichever agents response carries it (workspace
  // top-level, the workspace agent projection, or the page's agent detail) is picked up here.
  const growthProfile = workspace.growth_profile ?? workspace.agent?.growth_profile ?? agentDetail?.growth_profile ?? null;
  // Same additive-chain rule for the knowledge payload: the governed read-only candidate history
  // and approved knowledge may arrive on the workspace response or on the agent-detail payload;
  // when the server supplies neither, the surface renders an explicit unknown.
  const knowledge = normalizeKnowledgeCandidates(
    workspace.knowledge_candidates ?? workspace.agent?.knowledge_candidates ?? agentDetail?.knowledge_candidates ?? null,
    workspace.approved_knowledge ?? workspace.agent?.approved_knowledge ?? agentDetail?.approved_knowledge ?? null,
  );
  // Same additive-contract chain for the Skill assignment/version history projection.
  const skillHistory = pickSkillHistoryPayload(workspace) ?? pickSkillHistoryPayload(workspace.agent) ?? pickSkillHistoryPayload(agentDetail) ?? null;
  // Same additive-contract chain for the Skill assignment-state projection.
  const skillState = pickSkillStatePayload(workspace) ?? pickSkillStatePayload(workspace.agent) ?? pickSkillStatePayload(agentDetail) ?? null;
  // Same additive-contract chain for the learning & proficiency projection.
  const learningHistory = workspace.learning_history ?? workspace.agent?.learning_history ?? agentDetail?.learning_history ?? null;
  // The Guardian's own workspace page carries the platform/security/backup evidence surface;
  // every other employee's workspace stays unaffected.
  const isGuardian = (agent.name || agentDetail?.name) === GUARDIAN_AGENT_NAME;

  // The conversation summary driving the state chip / ticket context: prefer the workspace payload
  // (refreshed every 15s) and fall back to the object returned when ConversationPanel created it.
  const summaryId = openConversationId || createdConversation?.id || null;
  const activeSummary = summaryId
    ? recent_conversations.find((c) => c.id === summaryId)
      || (createdConversation?.id === summaryId ? createdConversation : null)
    : null;
  const activeStatus = activeSummary?.status || null;
  const boundTicketId = activeSummary?.ticket_id || null;
  const boundTicket = boundTicketId
    ? [current_work, ...assigned_tickets].filter(Boolean).find((t) => t.ticket_id === boundTicketId) || null
    : null;
  const activeState = activeStatus
    ? CONVERSATION_SECTION_STATE[activeStatus] || { label: conversationState(activeStatus).label, tone: "gray" }
    : null;
  // A run the employee is executing FOR THIS conversation — the concrete "working on it now" line.
  const workingOnThis = summaryId && current_work?.conversation_id === summaryId ? current_work : null;

  const openConversation = (conversationId) => {
    setCreatedConversation(null);
    setOpenConversationId(conversationId);
  };
  const hideConversation = () => {
    setCreatedConversation(null);
    setOpenConversationId(null);
    setSessionSeq((n) => n + 1);
  };

  return (
    <div className="space-y-4" dir="rtl">
      <WorkspaceNav sections={WORKSPACE_NAV_SECTIONS} />

      <section id="employee-growth" className="scroll-mt-24 space-y-2">
        <EmployeeIdentityCard agent={agent} growthProfile={growthProfile} agentId={agentId} />
        <div className="flex justify-end">
          <Link
            to={`/agents/${agentId}/cv`}
            className="text-xs text-brand-600 hover:underline"
          >
            לפרופיל המקצועי המלא — ניסיון מתועד, תוצאות מאומתות ומיומנויות ←
          </Link>
        </div>
      </section>

      {isGuardian && <GuardianWorkspace />}

      <section id="employee-work" aria-label="תקציר תפעולי" className="bg-white rounded-xl border shadow-sm p-4 scroll-mt-24">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[agent.effective_status] || STATUS_BADGE.available}`}>
            {status ? status.label : agent.effective_status}
          </span>
          <span className={`inline-flex items-center gap-1.5 text-sm font-medium px-2.5 py-0.5 rounded-full border ${summaryStyle.chip}`}>
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${summaryStyle.dot}`} />
            {summary.label}
          </span>
          {summary.detail && <span className="text-xs text-gray-500 truncate max-w-full">{summary.detail}</span>}
          {summary.links.length > 0 && (
            <span className="flex items-center gap-2 text-xs">
              {summary.links.map((l) => (
                <Link key={l.to} to={l.to} className="text-blue-600 hover:underline whitespace-nowrap">{l.label}</Link>
              ))}
            </span>
          )}
          <span className="flex-1" />
          <Link to={`/agents/${agentId}/learning-center`} className="text-xs px-3 py-1.5 rounded border hover:bg-gray-50">
            🎓 מרכז הלמידה
          </Link>
          <Link to="/agent-runs" className="text-xs px-3 py-1.5 rounded border hover:bg-gray-50">ריצות</Link>
          <Link to="/conversations" className="text-xs px-3 py-1.5 rounded border hover:bg-gray-50">כל השיחות</Link>
        </div>
      </section>

      {/* DIGEMP-SYSTEM-MAP-FINAL-POLISH-123: read-only flow map — every status it shows comes from
          a contract this page already loads (workspace payload, connection-readiness, connections
          health, run records); it adds no fetch of its own and no control that could start or
          approve work. */}
      <EmployeeFlowMapSection
        agentId={agentId}
        workspace={workspace}
        agentDetail={agentDetail}
        runs={runs}
        runsLoading={runsLoading}
        runsError={runsError}
        skillState={skillState}
        knowledge={knowledge}
        learningHistory={learningHistory}
      />

      {/* DIGEMP-BROWSER-WORK-SESSION-FRONTEND-049: the browser-work session cockpit reads its
          session evidence from this already-loaded workspace payload (tolerant key family) and the
          execution boundary from connections health; it self-gates its only control (stop) on
          agents_approve, mirroring the run page, so the workspace mount needs no new props. */}
      <BrowserWorkSessionPanel agentId={agentId} workspace={workspace} />

      <PilotAcceptancePanel
        workspace={workspace}
        runs={runs}
        agentDetail={agentDetail}
        runsLoading={runsLoading}
        runsError={runsError}
      />

      <section id="employee-conversations" className="scroll-mt-24">
        <section
          id="employee-conversation"
          ref={conversationRef}
          className="bg-white rounded-xl border shadow-sm p-4 space-y-3"
          aria-label={`השיחה עם ${agent.name}`}
        >
          <div className="flex flex-wrap items-center gap-3">
            <EmployeeAvatar
              name={agent.name}
              avatarKey={agent.avatar_key}
              avatarColor={agent.avatar_color}
              status={agent.effective_status ?? agent.availability_status}
              isEnabled={agent.is_enabled}
              size="lg"
              showStatus
            />
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-base font-semibold text-gray-800">{agent.name}</h3>
                {activeState && (
                  <span className={`text-xs px-2.5 py-0.5 rounded-full border ${SECTION_STATE_TONE[activeState.tone] || SECTION_STATE_TONE.gray}`}>
                    <span className="font-medium">{activeState.label}</span>
                    {activeState.hint && <span className="text-[11px] opacity-80"> · {activeState.hint}</span>}
                  </span>
                )}
              </div>
              {agent.role_title && <div className="text-sm text-violet-700">{agent.role_title}</div>}
              {!activeSummary && canRun && agent.is_enabled && (
                <p className="text-xs text-gray-500">כתוב הודעה כאן — {agent.name} ישיב כמו עמית לעבודה</p>
              )}
              {!activeSummary && canRun && !agent.is_enabled && (
                <p className="text-xs text-gray-500">העובד מושבת — לא ניתן לפתוח שיחה חדשה</p>
              )}
              {!activeSummary && !canRun && (
                <p className="text-xs text-gray-500">אין הרשאת הפעלה — ניתן לצפות בשיחות בלבד</p>
              )}
              {workingOnThis && (
                <p className="text-xs text-amber-700">
                  כרגע: {workingOnThis.run_status_label}
                  {workingOnThis.ticket_number ? ` · קריאה #${workingOnThis.ticket_number}` : ""}
                </p>
              )}
              {activeSummary && boundTicketId && (
                <p className="text-xs text-gray-600">
                  <Link to={`/tickets/${boundTicketId}?tab=ai-diagnosis`} className="text-blue-700 hover:underline">
                    {boundTicket?.ticket_number ? `קריאה #${boundTicket.ticket_number}` : "קריאה מקושרת"}
                    {boundTicket?.ticket_title ? ` · ${boundTicket.ticket_title}` : ""}
                  </Link>
                </p>
              )}
            </div>
            <span className="flex-1" />
            {activeSummary && (
              <div className="flex items-center gap-2 text-xs">
                <Link to={`/conversations/${summaryId}`} className="px-3 py-1.5 rounded border hover:bg-gray-50">
                  פתח בעמוד השיחות
                </Link>
                <button type="button" onClick={hideConversation} className="px-3 py-1.5 rounded border hover:bg-gray-50">
                  הסתר שיחה
                </button>
              </div>
            )}
          </div>

          {canView && (openConversationId || createdConversation || (canRun && agent.is_enabled)) && (
            <ConversationPanel
              key={`${agentId}:${openConversationId || "new"}:${sessionSeq}`}
              conversationId={openConversationId}
              agentId={agentId}
              customerId={customerId || undefined}
              onConversationCreated={setCreatedConversation}
            />
          )}
        </section>
      </section>

      <OperatorAttention workspace={workspace} />

      <QueueStrip workspace={workspace} />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card title="עכשיו בעבודה">
          {current_work ? (
            <div className="text-sm space-y-1">
              <div className="font-medium text-gray-800">
                {current_work.ticket_number ? `#${current_work.ticket_number} · ` : ""}{current_work.ticket_title || "משימה מתוך שיחה"}
              </div>
              <div className="text-xs text-amber-700">{current_work.run_status_label}</div>
              <div className="text-xs text-gray-400">מאז {fmt(current_work.started_at)}</div>
              <div className="flex gap-2 text-xs">
                <Link to={`/agent-runs/${current_work.run_id}`} className="text-blue-600 hover:underline">צפה בריצה</Link>
                {current_work.conversation_id && (
                  <Link to={`/conversations/${current_work.conversation_id}`} className="text-blue-600 hover:underline">לשיחה</Link>
                )}
              </div>
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic">פנוי — אין משימה פעילה</p>
          )}
        </Card>

        <Card title="קריאות בטיפול" count={assigned_tickets.length}>
          {assigned_tickets.length === 0 && (
            <div className="text-sm text-gray-400 italic">
              <p>אין כרגע קריאות שהעובד מטפל בהן</p>
              <Link to="/tickets" className="text-xs text-blue-600 not-italic hover:underline">לכל הקריאות ←</Link>
            </div>
          )}
          <ul className="space-y-1.5 text-sm">
            {assigned_tickets.slice(0, 5).map((t) => (
              <li key={t.run_id}>
                <div className="flex items-center gap-2">
                  <Link to={`/tickets/${t.ticket_id}?tab=ai-diagnosis`} className="text-blue-600 hover:underline truncate">
                    {t.ticket_number ? `#${t.ticket_number} ` : ""}{t.ticket_title || "קריאה"}
                  </Link>
                  <span className="text-[11px] text-gray-400 whitespace-nowrap mr-auto">{t.run_status_label}</span>
                </div>
                {t.ticket_id && <BrowserWorkRequestPanel agentId={agentId} ticketId={t.ticket_id} />}
              </li>
            ))}
          </ul>
        </Card>

        <Card title="שיחות אחרונות" count={recent_conversations.length}>
          {recent_conversations.length === 0 && (
            <div className="text-sm text-gray-400 italic">
              <p>אין עדיין שיחות עם העובד</p>
            </div>
          )}
          <ul className="space-y-1.5 text-sm">
            {recent_conversations.slice(0, 5).map((c) => {
              const state = conversationState(c.status);
              return (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => openConversation(c.id)}
                    aria-label={`פתח את השיחה עם ${agent.name}${c.last_message_preview ? `: ${c.last_message_preview}` : ""}`}
                    className={`block w-full text-right hover:bg-gray-50 rounded px-1 -mx-1 ${
                      summaryId === c.id ? "bg-blue-50 ring-1 ring-blue-200" : ""
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        data-status={c.status}
                        title={state.title || undefined}
                        className={`text-[11px] px-1.5 rounded-full border inline-flex items-center gap-1 ${state.badge}`}
                      >
                        <span aria-hidden="true">{state.icon}</span>
                        {state.label}
                      </span>
                      <span className="text-[11px] text-gray-400 mr-auto">{fmt(c.updated_at)}</span>
                    </div>
                    {c.last_message_preview && (
                      <div className="text-xs text-gray-600 truncate">
                        {c.last_message_from === "employee" ? `${agent.name}: ` : "טכנאי: "}{c.last_message_preview}
                      </div>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>
      </div>

      {canRun && agent.is_enabled && (
        <BrowserWorkRequestSection agentId={agentId} agentName={agent.name} tickets={assigned_tickets} />
      )}

      <div id="employee-capabilities" className="bg-white rounded-xl border shadow-sm p-4 space-y-2 scroll-mt-24">
        <h3 className="text-sm font-semibold text-gray-700">יכולות</h3>
        <dl className="space-y-2">
          <CapabilityRow label="מערכות מחוברות">
            {capabilities.connections.length === 0 && (
              <span className="text-gray-400 italic">אין מערכות מחוברות</span>
            )}
            {capabilities.connections.map((c) => {
              const st = connectionStatus(c);
              return (
                <span
                  key={c.id}
                  title={`${c.display_name} · ${st.title}`}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${st.chip}`}
                >
                  <span>{systemTypeLabel(c.system_type)}</span>
                  <span className="opacity-80">· {st.label}</span>
                </span>
              );
            })}
          </CapabilityRow>
          <CapabilityRow label="כלים מורשים">
            {capabilities.tools.length === 0 && (
              <span className="text-gray-400 italic">אין כלים מורשים</span>
            )}
            {capabilities.tools.length > 0 && (
              <>
                <span className="px-2 py-0.5 rounded-full border border-gray-200 bg-gray-50 text-gray-600">
                  {capabilities.tools.length} כלים
                </span>
                {toolSensitivityCounts(capabilities.tools).map((s) => (
                  <span key={s.key} title={s.title} className={`px-2 py-0.5 rounded-full border ${s.chip}`}>
                    {s.label}: {s.count}
                  </span>
                ))}
              </>
            )}
            <EntitlementBoundaryNote
              capabilities={capabilities}
              testId="entitlement-note-capabilities"
              className="w-full text-[11px] text-amber-700"
            />
          </CapabilityRow>
        </dl>
        <TechnicalDrawer workspace={workspace} />
      </div>

      <ModelProviderAssignment workspace={workspace} agentDetail={agentDetail} agent={agent} runs={runs} />

      <section id="employee-systems" className="space-y-4 scroll-mt-24">
        <EmployeeConnectionHub agentDetail={agentDetail} workspace={workspace} />

        <ConnectionReadinessSection agentId={agentId} />

        <SpecialistReadinessMatrix workspace={workspace} agentDetail={agentDetail} />

        <SpecialistPacksSection workspace={workspace} agentDetail={agentDetail} />

        <RemoteOperationsReadiness workspace={workspace} agentDetail={agentDetail} />

        <EsetSpecialistSection workspace={workspace} agentDetail={agentDetail} />

        <M365EntraSpecialistSection workspace={workspace} agentDetail={agentDetail} />

        <AcronisSpecialistSection workspace={workspace} agentDetail={agentDetail} />

        <ExpertPacksSection workspace={workspace} agentDetail={agentDetail} />
      </section>

      <section id="employee-skills" className="scroll-mt-24">
        <SkillAssignmentStateSection skillState={skillState} />
      </section>

      <ScheduledOperationsSection agentId={agentId} />

      <section id="employee-evidence" className="scroll-mt-24">
        <ExperienceTimeline runs={runs} isLoading={runsLoading} isError={runsError} />
      </section>

      <section id="employee-learning" className="scroll-mt-24">
        <LearningKnowledgeSection knowledge={knowledge} />
      </section>

      <ApprovedSkillsSection skillHistory={skillHistory} />

      <LearningProficiencyHistory learningHistory={learningHistory} />

      <div className="bg-white rounded-xl border shadow-sm p-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">יומן פעילות</h3>
        {activity.length === 0 && <p className="text-sm text-gray-400 italic">אין עדיין פעילות — ריצות ושיחות של העובד יופיעו כאן.</p>}
        <ol className="space-y-1.5 max-h-72 overflow-y-auto">
          {activity.map((e, i) => (
            <li key={`${e.at}-${i}`} className="flex items-start gap-2 text-sm">
              <span className="w-5 text-center text-gray-400">{KIND_ICON[e.kind] || "•"}</span>
              <span className="flex-1 text-gray-700 truncate" title={e.label}>{e.label}</span>
              <span className="text-[11px] text-gray-400 whitespace-nowrap">{fmt(e.at)}</span>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
